import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, Stethoscope, Search, X, Plus, AlertTriangle,
  Check, Save, Users, Menu, ChevronRight, ChevronDown, FileText, MapPin, ClipboardList,
  Undo2, Redo2, History, CheckCircle2, Lock, RotateCcw, Wrench, Flag, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { suggestNextWire } from "@/components/ortho/record/useOrthoVisitForm";
import { buildOrthoVisitIdempotencyKey, orthoStageForVisitType } from "@/components/ortho/record/orthoVisitPayload";
import OrthoWireGaugeSelect from "@/components/ortho/record/OrthoWireGaugeSelect";
import {
  formatWireSelection,
  wireSelectionFromOption,
  wireSelectionFromVisit,
  wireSelectionPayload,
} from "@/components/ortho/record/wireRecording";
import { uploadPatientFile } from "@/lib/patientFilesApi";
import OrthoVisitPhotos, { OrthoVisitPhotoPreview } from "@/components/ortho/record/OrthoVisitPhotos";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useFeatures } from "@/context/FeaturesContext";
import {
  resolveOngoingTreatmentEnabled as resolveOngoingTreatmentRollout,
  useOngoingTreatmentRollout,
} from "@/context/OngoingTreatmentRolloutContext";
import ChartingTab from "@/components/charting/ChartingTab";
import {
  DOCTOR_MULTI_TOOTH_PENDING_CODE,
  buildDoctorOverlayState,
  buildDoctorProcedureLegendItems,
  buildDoctorStackSummaryByTooth,
  extractMissingFdiSet,
  normalizeStackSummaryByTooth,
} from "@/components/charting/doctorChartingLayers";
import TxPlanCategoryLegend from "@/components/treatment/TxPlanCategoryLegend";
import MultiVisitPaintDialog from "@/components/doctor/MultiVisitPaintDialog";
import OngoingBundleCard from "@/components/charting/OngoingBundleCard";
import DentureTodayDialog from "@/components/doctor/DentureTodayDialog";
import MvMistakeCorrectionDialog from "@/components/doctor/MvMistakeCorrectionDialog";
import MvCaseImportDialog from "@/components/treatment/MvCaseImportDialog";
import ProceduresPalette from "@/components/doctor/ProceduresPalette";
import VisitNoteCard from "@/components/doctor/VisitNoteCard";
import NotTodayReasonDialog from "@/components/doctor/NotTodayReasonDialog";
import TreatmentTab from "@/components/treatment/TreatmentTab";
import PlanTabsRow from "@/components/treatment/PlanTabsRow";
import { procedureColor, buildProcedureColorMap, classifyMultiTooth } from "@/components/treatment/procedureColors";
import {
  findScheduledMultiVisitNeedForPaint,
  isWholeToothProcedure,
  isMultiVisitProcedure,
  isBareToothBooking,
} from "@/lib/procedureFlags";
import { reconcileChartToReminders } from "@/lib/scheduleReconcile";
import { buildDentureChartGroups, isDentureProcedure } from "@/lib/dentures";
import SeniorPwdTagControl from "@/components/patients/SeniorPwdTagControl";
import LineEditControls, { buildLinePresets } from "@/components/doctor/LineEditControls";
import { SESSION_FOLLOW_UP_OPTIONS, describeFollowUpDays } from "@/lib/followUpWindow";
import BuildMultiToothDialog from "@/components/treatment/BuildMultiToothDialog";
import { teethAreAdjacent } from "@/lib/fdi";
import { addClinicDays, clinicDayRange, clinicTodayDateString } from "@/lib/clinicTime";
import { doctorQueueForwardDays } from "@/lib/doctorQueueWindow";
import { formatDoctorName } from "@/lib/doctorName";
import { computeMedicalHistoryAlerts } from "@/lib/medicalHistoryAlerts";
import {
  StartOrthoCaseDialog,
  SetupBillingDialog,
  canCompleteOrthoBilling,
} from "@/components/ortho/OrthoPanel";
import { getOrthoPlanCreationBlock } from "@/lib/orthoContractGuards";
import {
  nextOrthoAdjustmentNumber,
  orthoAdjustmentNumber,
  orthoEventLabel,
} from "@/lib/orthoVisitSemantics";
import {
  DYNAMIC_CHARTING_FUTURE_NEED_DEMO_PROCEDURES,
  DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT,
  DYNAMIC_CHARTING_TODAY_DEMO,
  DYNAMIC_CHARTING_TODAY_DEMO_PROCEDURES,
  DYNAMIC_CHARTING_TX_PLAN_DEMO,
  DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES,
  DYNAMIC_CHARTING_TUTORIAL_DEMO,
  DYNAMIC_CHARTING_TUTORIAL_PATIENT,
  ONGOING_TREATMENT_TUTORIAL_PROCEDURES,
} from "@/tutorials/dynamicChartingDemo";
import { checkoutWorkKey, sameCheckoutWork } from "@/lib/postOpSuggestions";
import { mergeCheckoutDraftItems } from "@/lib/checkoutDraftItems";
import {
  isCheckoutDraftRevisionConflict,
  normalizeCheckoutDraftRevision,
  saveCheckoutDraftWithRevision,
} from "@/lib/checkoutDraftRevision";
import { createDraftLineId, draftLineId, ensureDraftLineId, sameDraftLine } from "@/lib/todayDraftLine";
import { confirmCheckoutWithoutMvNote } from "@/lib/mvVisitNoteGuard";
import { showOperationalMvInChartMode, treatmentPlanAuthoringPlans } from "@/lib/mvImport";
import { buildChairsideHandoffRequirements } from "@/lib/chairsideHandoffRequirements";
import {
  hasMedicalNotTodayDisposition,
  normalizeNotTodayReasonCode,
  notTodayReasonLabel,
} from "@/lib/noPaymentVisit";

// Bundling helpers — surface strings like "MO" / "MOD" represent
// multi-surface bundles (e.g. a Composite Class II MOD), not a literal
// single surface label. Helpers normalize / split / merge per the
// oral convention M / O / D / B / L / I.
const SURFACE_ORDER = ["M", "O", "D", "B", "L", "I"];
function splitSurfaces(s) {
  if (!s) return [];
  const set = new Set(String(s).toUpperCase().split("").filter((c) => SURFACE_ORDER.includes(c)));
  return SURFACE_ORDER.filter((c) => set.has(c));
}
function normalizeSurfaceStr(s) {
  const arr = splitSurfaces(s);
  return arr.length > 0 ? arr.join("") : null;
}
function mergeSurfaces(a, b) {
  return normalizeSurfaceStr((a || "") + (b || ""));
}
function stripSurface(s, ch) {
  if (!s || !ch) return normalizeSurfaceStr(s);
  return normalizeSurfaceStr(splitSurfaces(s).filter((c) => c !== String(ch).toUpperCase()).join(""));
}
// Surface pricing — single, settings-driven source. Mirrors the SQL
// helper dos_v2.bundled_fee_centavos exactly:
//   base + (count - 1) * base * (pct / 100), rounded.
// The clinic's procedures.additional_surface_pct is the policy. The
// default is 100 — every extra surface bills at full catalog — so a
// 2-surface line = 2 × base unless the clinic sets a lower bundle %.
function bundledFeeCentavos(baseCentavos, additionalSurfacePct, surfaceCount) {
  const base = Number(baseCentavos || 0);
  const count = Math.max(1, Number(surfaceCount || 1));
  if (base <= 0) return 0;
  if (count <= 1) return base;
  const pct = Number(additionalSurfacePct ?? 100);
  return base + Math.round((count - 1) * base * (pct / 100));
}

function orthoVisitChargeDueCentavos(row, currentAppointmentVisit) {
  const currentFinancialCharge = Number(currentAppointmentVisit?.charge_event?.amount_centavos || 0);
  if (currentAppointmentVisit?.ortho_visit?.id && currentAppointmentVisit?.charge_event?.id) return currentFinancialCharge;

  const currentProductionValue = Number(currentAppointmentVisit?.procedure_instance?.committed_fee_centavos || 0);
  if (currentAppointmentVisit?.ortho_visit?.id) return currentProductionValue;

  if (row?.next_visit_charge_centavos !== undefined && row?.next_visit_charge_centavos !== null) {
    return Number(row.next_visit_charge_centavos || 0);
  }
  return 0;
}

function activeOrthoVisitRows(row) {
  return (Array.isArray(row?.visits) ? row.visits : []).filter((visitRow) => (
    !visitRow.ortho_visit?.voided_at
    && visitRow.procedure_instance?.status !== "voided"
    && visitRow.procedure_visit?.outcome !== "voided"
  ));
}

function currentOrthoVisitRow(row, visitType, appointmentId) {
  return activeOrthoVisitRows(row).find((visitRow) => {
    const ov = visitRow.ortho_visit || {};
    const pv = visitRow.procedure_visit || {};
    const isAdjustmentEvent = visitType === "adjustment"
      && ["adjustment", "emergency", "no_adjustment"].includes(String(ov.visit_type || "").toLowerCase());
    return (ov.visit_type === visitType || isAdjustmentEvent)
      && (ov.appointment_id === appointmentId || pv.appointment_id === appointmentId);
  }) || null;
}

// =====================================================================
// Doctor — Phase 1 chairside workspace
// =====================================================================
// Single screen the dentist (or DA charting on dictation) opens during
// a patient visit. Pulls the existing building blocks (charting,
// treatment plan, medical-history banner) into one chart-centric
// layout, plus a "Today's work" side panel that writes the same
// checkout_drafts.draft_data shape the Checkout page reads — so the
// front desk's checkout queue lights up the moment the doctor marks
// something done.
//
// Phase 1 deliberately omits: AI note generation, multi-tooth bridge
// tool, ortho status banner, decline/route-reason modals, custom-
// procedure modal. Those layer in later once the chairside flow is
// validated in real use.
// =====================================================================

export default function Doctor() {
  const { activeClinic, clinicRole, user } = useAuth();
  const { activeBranchId } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const [queue, setQueue]               = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError]     = useState(null);
  const [activeApptId, setActiveApptId] = useState(null);
  const [queueOpen, setQueueOpen]       = useState(false); // mobile drawer
  const [finishingApptId, setFinishingApptId] = useState(null);
  const [reopeningApptId, setReopeningApptId] = useState(null);
  const tutorialDemoMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return ["dental-chart", "doctor-ortho"].includes(params.get("tutorial")) && params.get("demo") === "1";
  }, [location.search]);
  const tutorialDemoId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tutorial") || "dental-chart";
  }, [location.search]);
  const tutorialDemoModule = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("module") || "initial-charting";
  }, [location.search]);
  const tutorialDemoVisit = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const visit = Number(params.get("visit") || 1);
    return Number.isFinite(visit) ? Math.min(3, Math.max(1, Math.trunc(visit))) : 1;
  }, [location.search]);

  // Reload the queue every 30s so a Just-Checked-In patient appears
  // chairside without the doctor needing to refresh. Cheap query
  // (one indexed range scan); Supabase realtime is overkill at v1.
  //
  // Window note: list_appointments filters by scheduled_start, but
  // the doctor cares about who's CHECKED IN *right now* — which is
  // status, not date. Walk-ins and early-arrivals can be checked in
  // for a slot that isn't today, so a today-only window misses them.
  // Use a 7-back / production-forward window so any reasonably-scheduled
  // checked-in appointment is in the result set; the client-side
  // status filter does the real work. Localhost gets a wider 180-day
  // window so long-running ortho scenarios can be tested without
  // constantly rescheduling fixture appointments.
  const loadQueue = useCallback(async () => {
    setQueueError(null);
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const forwardDays = doctorQueueForwardDays(host);
    const today = clinicTodayDateString();
    const fromRange = clinicDayRange(addClinicDays(today, -7));
    const toRange = clinicDayRange(addClinicDays(today, forwardDays));
    const { data, error } = await supabase.rpc("list_appointments", {
      p_from: fromRange.from.toISOString(),
      p_to:   toRange.to.toISOString(),
      p_doctor_id: null,
      p_chair_id: null,
      p_branch_id: activeBranchId || null,
    });
    setQueueLoading(false);
    if (error) { setQueueError(error.message ?? "Could not load queue."); return; }
    const rows = (data ?? []).filter((a) => a.status === "Checked In");
    setQueue(rows);
    // If the active appointment is no longer checked in, close the
    // workspace. doctor_finished_at is only an operational marker;
    // checkout still owns the actual Completed transition, and the
    // doctor can reopen a marked-done patient while they remain checked in.
    if (activeApptId && !rows.some((r) => r.id === activeApptId)) {
      setActiveApptId(null);
    }
  }, [activeApptId, activeBranchId]);

  const handleMarkDone = useCallback(async (appointmentId) => {
    if (!appointmentId) return;
    setFinishingApptId(appointmentId);
    const { data, error } = await supabase.rpc("mark_doctor_finished_with_patient", {
      p_appointment_id: appointmentId,
    });
    setFinishingApptId(null);
    if (error) {
      toast.error(error.message ?? "Could not mark patient done.");
      return;
    }
    const doneAt = data?.doctor_finished_at || new Date().toISOString();
    setQueue((rows) => rows.map((row) => (
      row.id === appointmentId ? { ...row, doctor_finished_at: doneAt } : row
    )));
    toast.success("Marked done for Doctor View.");
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 1279px)").matches) {
      setQueueOpen(true);
    }
    loadQueue();
  }, [loadQueue]);

  const handleReopenDone = useCallback(async (appointmentId) => {
    if (!appointmentId) return;
    setReopeningApptId(appointmentId);
    const { error } = await supabase.rpc("reopen_doctor_finished_patient", {
      p_appointment_id: appointmentId,
    });
    setReopeningApptId(null);
    if (error) {
      toast.error(error.message ?? "Could not reopen chairside workspace.");
      return;
    }
    setQueue((rows) => rows.map((row) => (
      row.id === appointmentId ? { ...row, doctor_finished_at: null } : row
    )));
    toast.success("Chairside workspace reopened.");
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (tutorialDemoMode) {
      setQueueLoading(false);
      return undefined;
    }
    loadQueue();
    const t = setInterval(loadQueue, 30_000);
    return () => clearInterval(t);
  }, [loadQueue, tutorialDemoMode]);

  const activeAppt = useMemo(
    () => queue.find((q) => q.id === activeApptId) ?? null,
    [queue, activeApptId],
  );

  // Layout strategy: protect the chart's horizontal real estate.
  // Two render modes:
  //   * Browse  (no patient picked) — queue rail visible at xl+, empty
  //                                   workspace on the right.
  //   * Treat   (patient picked) — queue rail collapses entirely; the
  //                                workspace takes the full row so the
  //                                chart gets the whole viewport width.
  //                                The queue stays one tap away via a
  //                                "Switch patient" pill that reopens
  //                                it as a slide-over drawer.
  // The drawer is the universal fallback for tablet / mobile and for
  // re-picking a patient mid-treatment.
  const inTreatMode = !!activeAppt;

  if (tutorialDemoMode) {
    if (tutorialDemoId === "doctor-ortho") {
      return (
        <DoctorOrthoTutorialDemo
          currency={activeClinic?.currency || "PHP"}
          clinicRole={clinicRole}
        />
      );
    }
    return (
      <DoctorChartingTutorialDemo
        currency={activeClinic?.currency || "PHP"}
        clinicRole={clinicRole}
        module={tutorialDemoModule}
        ongoingTreatmentVisit={tutorialDemoVisit}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 min-h-[640px]" data-testid="doctor-page" data-tutorial-id="doctor-page">
      <div className={`flex gap-3 ${inTreatMode ? "" : "xl:flex-row flex-col"}`}>
        {/* Queue rail — only visible in browse mode at xl+. In treat
            mode it's gone entirely; access via the pill above. */}
        {!inTreatMode ? (
          <aside className="hidden xl:block w-[280px] shrink-0">
            <QueueContent
              queue={queue}
              loading={queueLoading}
              error={queueError}
              activeApptId={activeApptId}
              onSelect={(id) => { setActiveApptId(id); setQueueOpen(false); }}
              showCloseButton={false}
              onClose={() => {}}
            />
          </aside>
        ) : null}

        {/* Workspace — full-width in treat mode; flex-1 alongside rail
            in browse mode. */}
        <main className="flex-1 min-w-0 min-h-0">
          {!activeAppt ? (
            <EmptyWorkspace
              queueLoading={queueLoading}
              queueCount={queue.length}
              onPick={() => setQueueOpen(true)}
            />
          ) : (
            <PatientWorkspace
              key={activeAppt.id}
              appt={activeAppt}
              clinic={activeClinic}
              clinicRole={clinicRole}
              currency={activeClinic?.currency || "PHP"}
              onOpenPatient={() => navigate(`/patients/${activeAppt.patient_id}`)}
              onOpenMedicalHistory={() => navigate(`/patients/${activeAppt.patient_id}?tab=medical-history`)}
              onSwitchPatient={() => setQueueOpen(true)}
              onMarkDone={() => handleMarkDone(activeAppt.id)}
              onReopenDone={() => handleReopenDone(activeAppt.id)}
              markingDone={finishingApptId === activeAppt.id}
              reopeningDone={reopeningApptId === activeAppt.id}
              queueCount={queue.length}
            />
          )}
        </main>
      </div>

      {/* Queue drawer — universal fallback. Opens via the pill at any
          screen size, also auto-opens when no patient is picked on
          tablet/mobile. */}
      <QueueDrawer
        open={queueOpen}
        queue={queue}
        loading={queueLoading}
        error={queueError}
        activeApptId={activeApptId}
        onSelect={(id) => { setActiveApptId(id); setQueueOpen(false); }}
        onClose={() => setQueueOpen(false)}
      />
    </div>
  );
}

function DoctorChartingTutorialDemo({ currency = "PHP", clinicRole, module = "initial-charting", ongoingTreatmentVisit = 1 }) {
  const [chartMode, setChartMode] = useState(module === "today" ? "today" : module === "future-need" ? "future_need" : "chart");
  const patient = DYNAMIC_CHARTING_TUTORIAL_PATIENT;

  if (module === "today") {
    return (
      <TodayChartingTutorialDemo
        currency={currency}
        clinicRole={clinicRole}
      />
    );
  }

  if (module === "ongoing-treatment") {
    return (
      <TodayChartingTutorialDemo
        key={`ongoing-treatment-visit-${ongoingTreatmentVisit}`}
        currency={currency}
        clinicRole={clinicRole}
        ongoingTreatmentDemo
        ongoingTreatmentVisit={ongoingTreatmentVisit}
      />
    );
  }

  if (module === "future-need") {
    return (
      <FutureNeedChartingTutorialDemo
        currency={currency}
        clinicRole={clinicRole}
      />
    );
  }

  if (module === "tx-planning" || module === "rct-zirconia-multi-visit") {
    return (
      <TxPlanningChartingTutorialDemo
        currency={currency}
        clinicRole={clinicRole}
      />
    );
  }

  const modePanel = (
    <DynamicChartingTutorialModePanel
      mode={chartMode}
    />
  );

  return (
    <div className="flex flex-col gap-3" data-testid="doctor-page" data-tutorial-id="doctor-page">
      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        data-testid="doctor-workspace-body"
        data-tutorial-id="doctor-primary-surface"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Demo patient</p>
            <h1 className="text-lg font-bold text-slate-950">{patient.full_name}</h1>
            <p className="text-xs text-slate-500">
              {patient.patient_number} · Age: {formatPatientAge(patient.date_of_birth)} · Dr. Levi Jordan · Chair 1
            </p>
          </div>
          <button
            type="button"
            disabled
            data-testid="tutorial-demo-done-charting"
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white opacity-80"
          >
            <Check className="h-3.5 w-3.5" />
            Done charting
          </button>
        </div>
        <div className="p-4">
          <ChartingTab
            patientId={patient.id}
            patient={patient}
            chairside
            chartMode={chartMode}
            onChartModeChange={setChartMode}
            tutorialDemo={DYNAMIC_CHARTING_TUTORIAL_DEMO}
            belowChartCanvas={modePanel}
          />
        </div>
      </section>
      <div className="sr-only" aria-hidden="true">
        Role: {clinicRole || "training"}
      </div>
    </div>
  );
}

function DoctorOrthoTutorialDemo({ currency = "PHP", clinicRole }) {
  const { user } = useAuth();
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const visitType = ["bonding", "adjustment", "debonding"].includes(params.get("visit"))
    ? params.get("visit")
    : "bonding";
  const patient = useMemo(() => ({
    id: "tutorial-doctor-ortho-patient",
    full_name: "Bianca Cruz",
    patient_number: "ORTHO-CHAIR-001",
    date_of_birth: "1998-03-14",
    preferred_doctor_name: "Dr. Mara Santos",
    has_medical_history: true,
    senior_pwd_status: "none",
  }), []);
  const appt = useMemo(() => ({
    id: `tutorial-doctor-ortho-${visitType}`,
    patient_id: patient.id,
    patient_name: patient.full_name,
    doctor_name: "Dr. Mara Santos",
    chair_name: "Chair 2",
    status: "Checked In",
    scheduled_start: "2026-05-27T09:00:00.000Z",
    ortho_case_id: "tutorial-doctor-ortho-case",
    ortho_visit_type: visitType,
  }), [patient, visitType]);
  const doctors = useMemo(() => [{
    id: "tutorial-ortho-doctor",
    profile_id: user?.id || "tutorial-user",
    full_name: "Dr. Mara Santos",
    display_name: "Dr. Mara Santos",
  }, {
    id: "tutorial-ortho-doctor-2",
    profile_id: "tutorial-user-2",
    full_name: "Dr. Rina Cruz",
    display_name: "Dr. Rina Cruz",
  }], [user?.id]);
  const context = useMemo(() => ({
    loading: false,
    isOrthoVisit: true,
    visitType,
    appointmentId: appt.id,
    activeCase: buildDoctorOrthoTutorialCase(visitType),
    doctors,
    wireOptions: [
      { id: "tutorial-wire-014-niti", display_name: "0.014 NiTi" },
      { id: "tutorial-wire-016-niti", display_name: "0.016 NiTi" },
      { id: "tutorial-wire-018-ss", display_name: "0.018 SS" },
    ],
    settings: {
      retainer_charge_mode: "split",
      removable_retainer_cost_centavos: 400000,
      fixed_retainer_cost_centavos: 500000,
    },
    reload: () => {},
  }), [appt.id, doctors, visitType]);

  const orthoConsultation = useMemo(() => ({
    id: "tutorial-ortho-consultation",
    code: "ORTHO-CONSULT",
    short_code: "ORTHO",
    name: "Orthodontic Consultation",
    category: "Orthodontic",
    default_fee_centavos: 100000,
    additional_surface_pct: 100,
    tooth_specific: false,
    surface_specific: false,
    archived: false,
  }), []);
  const consultationWorkKey = `tn:tutorial-ortho-consultation-${visitType}`;
  const scheduled = useMemo(() => [{
    work_key: consultationWorkKey,
    tn_id: `tutorial-ortho-consultation-${visitType}`,
    procedure_id: orthoConsultation.id,
    procedure_code: orthoConsultation.code,
    procedure_name: orthoConsultation.name,
    display_label: orthoConsultation.name,
    default_fee_centavos: orthoConsultation.default_fee_centavos,
    tooth_number: null,
    surface: null,
  }], [consultationWorkKey, orthoConsultation, visitType]);
  const items = useMemo(() => [{
    id: `tutorial-ortho-consultation-item-${visitType}`,
    work_key: consultationWorkKey,
    tutorial_id: "doctor-item-demo-ortho-consultation",
    procedure_id: orthoConsultation.id,
    procedure_code: orthoConsultation.code,
    procedure_name: orthoConsultation.name,
    procedure_base_centavos: orthoConsultation.default_fee_centavos,
    default_fee_centavos: orthoConsultation.default_fee_centavos,
    performed_price_centavos: orthoConsultation.default_fee_centavos,
    item_discount_centavos: 0,
    qty: 1,
    tooth_number: "",
    surface: null,
    outcome: "done",
  }], [consultationWorkKey, orthoConsultation, visitType]);

  return (
    <TodayChartingTutorialDemo
      key={`doctor-ortho-today-${visitType}`}
      currency={currency}
      clinicRole={clinicRole}
      orthoDemo={{
        patient,
        appointment: appt,
        context,
        user: user || { id: "tutorial-user" },
        procedures: [orthoConsultation],
        scheduled,
        items,
      }}
    />
  );
}

function buildDoctorOrthoTutorialCase(visitType) {
  const baseCase = {
    id: "tutorial-doctor-ortho-case",
    case_type: "fixed",
    system: "Metal Conventional",
    system_name: "Metal Conventional",
    status: visitType === "bonding" ? "active_pending_bonding" : "active",
    primary_doctor_id: "tutorial-ortho-doctor",
    estimated_total_visits: 24,
    chief_complaint: "Active braces case for chairside ortho visit training.",
    ready_for_debonding: visitType === "debonding",
    debonding_timing: visitType === "debonding" ? "today" : null,
  };
  const billing = {
    id: "tutorial-doctor-ortho-billing",
    billing_model: "per_visit",
    package_price_centavos: 6500000,
    down_payment_centavos: 2000000,
    down_payment_status: "paid",
    per_visit_charge_centavos: 250000,
    estimated_total_visits: 24,
    number_of_payments: 24,
  };
  const bondingVisit = {
    ortho_visit: {
      id: "tutorial-bonding-visit",
      visit_type: "bonding",
      visit_number: 1,
      arch_bonded_upper: true,
      arch_bonded_lower: true,
      upper_wire_option_id: "tutorial-wire-014-niti",
      lower_wire_option_id: "tutorial-wire-014-niti",
      upper_wire_label_snapshot: "0.014 NiTi",
      lower_wire_label_snapshot: "0.014 NiTi",
      elastics_status: "none_this_visit",
      elastics_prescribed: "None this visit",
      oh_status: "good",
      next_visit_interval_weeks: 4,
      next_visit_goal: "First adjustment and wire check.",
      notes: "Initial bonding completed.",
      created_at: "2026-04-01T09:00:00.000Z",
    },
    procedure_visit: {
      completed_at: "2026-04-01T09:40:00.000Z",
      performed_by: "tutorial-ortho-doctor-2",
    },
  };
  const adjustmentVisit = {
    ortho_visit: {
      id: "tutorial-adjustment-visit",
      visit_type: "adjustment",
      visit_number: 2,
      adjustment_number: 6,
      arch_bonded_upper: true,
      arch_bonded_lower: true,
      procedures_this_visit: ["archwire_change", "tighten_existing"],
      upper_wire_option_id: "tutorial-wire-016-niti",
      lower_wire_option_id: "tutorial-wire-016-niti",
      upper_wire_label_snapshot: "0.016 NiTi",
      lower_wire_label_snapshot: "0.016 NiTi",
      elastics_status: "new_prescription",
      elastics_detail: "Class II elastics, night wear",
      elastics_prescribed: "Class II elastics, night wear",
      oh_status: "fair",
      next_visit_interval_weeks: 4,
      next_visit_goal: "Check canine rotation and oral hygiene.",
      notes: "Adjusted wires and reinforced brushing.",
      created_at: "2026-05-01T09:00:00.000Z",
    },
    procedure_visit: {
      completed_at: "2026-05-01T09:35:00.000Z",
      performed_by: "tutorial-ortho-doctor",
    },
  };
  const visits = visitType === "bonding"
    ? []
    : visitType === "adjustment"
      ? [bondingVisit, adjustmentVisit]
      : [bondingVisit, adjustmentVisit];

  return {
    case: baseCase,
    billing,
    balance: {
      total_collected_centavos: 2000000,
      contract_remaining_centavos: 4500000,
      balance_remaining_centavos: 4500000,
    },
    visits,
  };
}

const ONGOING_TREATMENT_TUTORIAL_ITEM_ID = "demo-ongoing-rct-item";

function ongoingTreatmentTutorialAppointmentId(visitNumber) {
  return visitNumber > 1
    ? `${DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT.id}-visit-${visitNumber}`
    : DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT.id;
}

function buildOngoingTreatmentTutorialItem(procedure, visitNumber = 1) {
  return {
    id: ONGOING_TREATMENT_TUTORIAL_ITEM_ID,
    tutorial_id: "doctor-item-demo-ongoing-rct",
    procedure_id: procedure.id,
    procedure_code: procedure.code,
    procedure_name: procedure.name,
    procedure_base_centavos: 0,
    default_fee_centavos: 0,
    performed_price_centavos: 0,
    item_discount_centavos: 0,
    qty: 1,
    tooth_number: "46",
    surface: null,
    outcome: "done",
    treatment_plan_id: "tutorial-ongoing-treatment",
    treatment_plan_item_id: ONGOING_TREATMENT_TUTORIAL_ITEM_ID,
    line_note: visitNumber > 1 ? `Ongoing treatment visit ${visitNumber}` : null,
  };
}

function buildOngoingTreatmentTutorialBundle(procedure, visitNumber = 1) {
  const afterFirstCheckout = visitNumber > 1;
  return {
    plan_id: "tutorial-ongoing-treatment",
    mv_code: "MV-TUTORIAL",
    status: "Accepted",
    agreed_centavos: Number(procedure.default_fee_centavos || 0),
    paid_centavos: afterFirstCheckout ? 425000 : 0,
    balance_centavos: afterFirstCheckout
      ? Math.max(0, Number(procedure.default_fee_centavos || 0) - 425000)
      : Number(procedure.default_fee_centavos || 0),
    finish_requested: false,
    price_editable: false,
    procedures: [{
      item_id: ONGOING_TREATMENT_TUTORIAL_ITEM_ID,
      procedure_id: procedure.id,
      procedure_name: procedure.name,
      tooth_number: "46",
      surface: null,
      fee_centavos: Number(procedure.default_fee_centavos || 0),
      status: "Ongoing",
      visits_done: visitNumber,
      recorded_appointment_ids: Array.from(
        { length: visitNumber },
        (_, index) => ongoingTreatmentTutorialAppointmentId(index + 1),
      ),
      is_multi_visit: true,
      multi_tooth_kind: null,
      tutorial_complete_id: "bundle-complete-demo-ongoing-rct",
    }],
  };
}

function TodayChartingTutorialDemo({ currency = "PHP", clinicRole, ongoingTreatmentDemo = false, ongoingTreatmentVisit = 1, orthoDemo = null }) {
  const patient = orthoDemo?.patient || DYNAMIC_CHARTING_TUTORIAL_PATIENT;
  const procedures = orthoDemo?.procedures || (ongoingTreatmentDemo
    ? ONGOING_TREATMENT_TUTORIAL_PROCEDURES
    : DYNAMIC_CHARTING_TODAY_DEMO_PROCEDURES);
  const ongoingProcedure = ongoingTreatmentDemo ? procedures[0] : null;
  const isOngoingReturnVisit = ongoingTreatmentDemo && ongoingTreatmentVisit > 1;
  const [chartMode, setChartMode] = useState("today");
  const [armedProcedureId, setArmedProcedureId] = useState(null);
  const [items, setItems] = useState(() => {
    if (orthoDemo?.items) return orthoDemo.items;
    return isOngoingReturnVisit ? [buildOngoingTreatmentTutorialItem(ongoingProcedure, ongoingTreatmentVisit)] : [];
  });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [doctorFinishedAt, setDoctorFinishedAt] = useState(null);
  const [pendingMultiVisit, setPendingMultiVisit] = useState(null);
  const [ongoingBundle, setOngoingBundle] = useState(() => (
    isOngoingReturnVisit ? buildOngoingTreatmentTutorialBundle(ongoingProcedure, ongoingTreatmentVisit) : null
  ));
  const scheduled = useMemo(() => orthoDemo?.scheduled || [], [orthoDemo]);
  const appt = useMemo(() => ({
    ...(orthoDemo?.appointment || DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT),
    id: orthoDemo?.appointment?.id || (ongoingTreatmentDemo
      ? ongoingTreatmentTutorialAppointmentId(ongoingTreatmentVisit)
      : DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT.id),
    doctor_finished_at: doctorFinishedAt,
  }), [doctorFinishedAt, ongoingTreatmentDemo, ongoingTreatmentVisit, orthoDemo]);
  const procedureColorMap = useMemo(
    () => buildProcedureColorMap(procedures),
    [procedures],
  );
  const doneWorkKeysSet = useMemo(() => {
    const keys = new Set();
    for (const item of items) {
      const key = scheduledWorkKey(item);
      if (key) keys.add(key);
    }
    return keys;
  }, [items]);
  const todayTotals = useMemo(() => {
    const done = items.filter((item) => (item.outcome || "done") === "done");
    const gross = done.reduce(
      (sum, item) => sum + Number(item.qty || 1) * Number(item.performed_price_centavos || 0),
      0,
    );
    const discount = done.reduce((sum, item) => sum + Number(item.item_discount_centavos || 0), 0);
    return {
      gross,
      discount,
      net: Math.max(0, gross - discount),
      count: done.length,
    };
  }, [items]);
  const overlayFindings = useMemo(() => procedures.map((procedure) => ({
    code: `__doc_${procedure.id}`,
    color_hex: procedureColor(procedure, procedureColorMap),
    name: procedure.name,
  })), [procedureColorMap, procedures]);
  const overlayState = useMemo(() => buildDoctorOverlayState({
    chartMode,
    scheduled,
    dispositions: {},
    draftItems: items,
    activePlanItems: [],
    futureNeeds: [],
    multiToothBuffer: [],
  }), [chartMode, items, scheduled]);
  const stackSummaryByTooth = useMemo(() => buildDoctorStackSummaryByTooth({
    chartMode,
    scheduled,
    dispositions: {},
    draftItems: items,
    futureNeeds: [],
    procedureColorById: Object.fromEntries(
      procedures.map((procedure) => [procedure.id, procedureColor(procedure, procedureColorMap)]),
    ),
  }), [chartMode, items, procedureColorMap, procedures, scheduled]);
  const doctorProcedureLegendItems = useMemo(() => buildDoctorProcedureLegendItems({
    chartMode,
    scheduled,
    dispositions: {},
    draftItems: items,
    activePlanItems: [],
    futureNeeds: [],
  }), [chartMode, items, scheduled]);

  const mutateItems = useCallback((updater) => {
    setItems((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      setUndoStack((stack) => [...stack, current]);
      setRedoStack([]);
      return next;
    });
  }, []);

  const addDemoItem = useCallback((proc, ctx = {}) => {
    if (!proc) return;
    const tooth = ctx.tooth_number ? String(ctx.tooth_number) : "";
    const surface = ctx.surface ? normalizeSurfaceStr(ctx.surface) : null;
    const defaultFee = Number(proc.default_fee_centavos || 0);
    const demoId = getTodayTutorialDemoItemId(proc, tooth, surface);
    const tutorialId = getTodayTutorialDemoItemAnchor(proc, tooth);

    mutateItems((current) => {
      if (current.some((item) => item.id === demoId)) return current;
      return [
        ...current,
        {
          id: demoId,
          tutorial_id: tutorialId,
          procedure_id: proc.id,
          procedure_code: proc.code,
          procedure_name: proc.name,
          procedure_base_centavos: defaultFee,
          additional_surface_pct: Number(proc.additional_surface_pct ?? 100),
          default_fee_centavos: defaultFee,
          performed_price_centavos: defaultFee,
          item_discount_centavos: 0,
          qty: 1,
          tooth_number: tooth,
          surface,
          outcome: "done",
        },
      ];
    });
  }, [mutateItems]);

  const removeDemoItem = useCallback((id) => {
    mutateItems((current) => current.filter((item) => item.id !== id));
  }, [mutateItems]);

  const setDemoOutcome = useCallback((id, outcome) => {
    mutateItems((current) => current.map((item) => (
      item.id === id ? { ...item, outcome } : item
    )));
  }, [mutateItems]);

  const setDemoPrice = useCallback((id, centavos) => {
    mutateItems((current) => current.map((item) => (
      item.id === id ? { ...item, performed_price_centavos: Number(centavos || 0) } : item
    )));
  }, [mutateItems]);

  const setDemoDiscount = useCallback((id, { pct, amount, reason }) => {
    mutateItems((current) => current.map((item) => {
      if (item.id !== id) return item;
      const gross = Number(item.qty || 1) * Number(item.performed_price_centavos || 0);
      const discount = pct != null && Number(pct) > 0
        ? Math.round(gross * (Number(pct) / 100))
        : Number(amount || 0);
      return {
        ...item,
        item_discount_centavos: discount,
        item_discount_pct: pct != null && Number(pct) > 0 ? Number(pct) : null,
        item_discount_reason: reason || null,
      };
    }));
  }, [mutateItems]);

  const setDemoNote = useCallback((id, noteText) => {
    mutateItems((current) => current.map((item) => (
      item.id === id ? { ...item, line_note: noteText || null } : item
    )));
  }, [mutateItems]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const previous = stack[stack.length - 1];
      setItems((current) => {
        setRedoStack((redo) => [...redo, current]);
        return previous;
      });
      return stack.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((stack) => {
      if (stack.length === 0) return stack;
      const next = stack[stack.length - 1];
      setItems((current) => {
        setUndoStack((undoStackCurrent) => [...undoStackCurrent, current]);
        return next;
      });
      return stack.slice(0, -1);
    });
  }, []);

  const handleToothClick = useCallback((toothNumber, surface) => {
    if (chartMode !== "today") return;
    const proc = procedures.find((procedure) => procedure.id === armedProcedureId);
    if (!proc) {
      toast.info("Pick a procedure from the palette first.");
      return;
    }
    if (ongoingTreatmentDemo && isMultiVisitProcedure(proc)) {
      setPendingMultiVisit({
        proc,
        fdi: toothNumber ? String(toothNumber) : null,
        surface: isWholeToothProcedure(proc) ? null : surface,
      });
      return;
    }
    addDemoItem(proc, {
      tooth_number: toothNumber,
      surface: isWholeToothProcedure(proc) ? null : surface,
    });
  }, [addDemoItem, armedProcedureId, chartMode, ongoingTreatmentDemo, procedures]);

  const startDemoOngoingTreatment = useCallback(() => {
    if (!pendingMultiVisit?.proc) return;
    const { proc, fdi, surface } = pendingMultiVisit;
    mutateItems(() => [{
      ...buildOngoingTreatmentTutorialItem(proc, 1),
      tooth_number: fdi,
      surface,
    }]);
    setOngoingBundle({
      ...buildOngoingTreatmentTutorialBundle(proc, 1),
      procedures: buildOngoingTreatmentTutorialBundle(proc, 1).procedures.map((procedure) => ({
        ...procedure,
        tooth_number: fdi,
        surface,
      })),
    });
    setPendingMultiVisit(null);
  }, [mutateItems, pendingMultiVisit]);

  const pickWholeMouthProcedure = useCallback((proc) => {
    if (ongoingTreatmentDemo && isMultiVisitProcedure(proc)) {
      setPendingMultiVisit({ proc, fdi: null, surface: null });
      return;
    }
    addDemoItem(proc, { tooth_number: null, surface: null });
  }, [addDemoItem, ongoingTreatmentDemo]);

  const palette = chartMode === "today" ? (
    <ProceduresPalette
      procedures={procedures}
      colorMap={procedureColorMap}
      armedProcedureId={armedProcedureId}
      onArm={setArmedProcedureId}
      onPickWholeMouth={pickWholeMouthProcedure}
      mode="today"
      currency={currency}
      fillHeight={!!orthoDemo}
      eraserOn={false}
      onPickEraser={null}
    />
  ) : null;

  const scheduledSlot = orthoDemo ? (
    <ToDoTodayCard
      embedded
      scheduled={scheduled}
      currency={currency}
      doneWorkKeys={doneWorkKeysSet}
      dispositions={{}}
      orthoContext={{ ...orthoDemo.context, done: false }}
      onDone={(row) => addDemoItem(procedures.find((procedure) => procedure.id === row.procedure_id), row)}
      onUndoDone={(row) => removeDemoItem(items.find((item) => scheduledWorkKey(item) === scheduledWorkKey(row))?.id)}
      onNotToday={() => true}
      onDecline={() => true}
      onClearDisposition={() => {}}
    />
  ) : null;

  const todayWorkCard = chartMode === "today" ? (
    <TodayWorkCard
      items={items}
      saving={false}
      savedAt={null}
      currency={currency}
      patient={patient}
      procedures={procedures}
      colorMap={procedureColorMap}
      onAdd={addDemoItem}
      onRemove={removeDemoItem}
      onSetOutcome={setDemoOutcome}
      onSetPrice={setDemoPrice}
      onSetDiscount={setDemoDiscount}
      onSetNote={setDemoNote}
      onUndo={undo}
      onRedo={redo}
      canUndo={undoStack.length > 0}
      canRedo={redoStack.length > 0}
      scheduledSlot={scheduledSlot}
    />
  ) : null;

  const orthoCurrentVisitModule = orthoDemo ? (
    <div
      className="min-w-0"
      data-testid="doctor-ortho-current-visit-rail"
      data-tutorial-id="doctor-ortho-chairside-workflow"
    >
      <OrthoChairsidePanel
        context={orthoDemo.context}
        appointmentId={appt.id}
        currency={currency}
        clinicRole="clinic_admin"
        user={orthoDemo.user}
        onSaved={() => {}}
        open
        embedded
        tutorialMode
        onUndoConversion={() => {}}
      />
    </div>
  ) : null;
  const hasAlignedRightRail = ongoingTreatmentDemo || !!orthoCurrentVisitModule;

  return (
    <div className="flex flex-col gap-3" data-testid="doctor-page" data-tutorial-id="doctor-page">
      <WorkspaceHeader
        appt={appt}
        patient={patient}
        clinicRole={clinicRole}
        onOpenPatient={() => {}}
        onOpenOrtho={() => {}}
        onSwitchPatient={null}
        onMarkDone={() => setDoctorFinishedAt(new Date("2026-05-18T08:00:00.000Z").toISOString())}
        markingDone={false}
        queueCount={0}
        currency={currency}
        todayTotals={todayTotals}
        lastVisit={null}
        lastVisitLoaded
        patientContext={{ medicalHistory: { data: {} }, loaded: true }}
      />
      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
        data-testid="doctor-workspace-body"
        data-tutorial-id="doctor-primary-surface"
      >
        <ChartingTab
          patientId={patient.id}
          patient={patient}
          chairside
          chartMode={chartMode}
          onChartModeChange={setChartMode}
          tutorialDemo={DYNAMIC_CHARTING_TODAY_DEMO}
          paletteOverride={palette}
          belowChartCanvas={hasAlignedRightRail ? null : todayWorkCard}
          belowChartLeft={hasAlignedRightRail ? todayWorkCard : null}
          belowChartRight={orthoCurrentVisitModule || (ongoingTreatmentDemo && ongoingBundle ? (
            <OngoingBundleCard
              bundle={ongoingBundle}
              stagedDoneItemIds={new Set(["demo-ongoing-rct-item"])}
              onCompleteItem={(itemId) => setOngoingBundle((current) => ({
                ...current,
                finish_requested: true,
                procedures: current.procedures.map((procedure) => (
                  procedure.item_id === itemId
                    ? { ...procedure, status: "Completed" }
                    : procedure
                )),
              }))}
              onRequestFinish={() => setOngoingBundle((current) => ({
                ...current,
                finish_requested: true,
              }))}
              currentAppointmentId={DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT.id}
            />
          ) : null)}
          belowChartEqualColumns={!!orthoCurrentVisitModule || (ongoingTreatmentDemo && !!ongoingBundle)}
          matchPaletteHeight={!!orthoCurrentVisitModule}
          chartFooter={
            chartMode === "today" ? (
              <ChairsideChartLegend>
                <WholeMouthRibbon
                  scheduled={scheduled}
                  items={items}
                  doneWorkKeys={doneWorkKeysSet}
                  dispositions={{}}
                  colorMap={procedureColorMap}
                  procedures={procedures}
                  bare
                />
                <TxPlanCategoryLegend
                  items={doctorProcedureLegendItems}
                  procedures={procedures}
                  showProcedures
                  bare
                />
              </ChairsideChartLegend>
            ) : null
          }
          onToothInterceptClick={chartMode === "today" ? handleToothClick : null}
          interceptEraseArmed={false}
          onToothInterceptErase={null}
          armedProcedureName={
            armedProcedureId
              ? procedures.find((procedure) => procedure.id === armedProcedureId)?.name || null
              : null
          }
          armedProcedureColor={
            armedProcedureId
              ? procedureColor(procedures.find((procedure) => procedure.id === armedProcedureId), procedureColorMap)
              : null
          }
          onClearArmedProcedure={armedProcedureId ? () => setArmedProcedureId(null) : null}
          interceptOnUndo={undo}
          interceptOnRedo={redo}
          interceptCanUndo={undoStack.length > 0}
          interceptCanRedo={redoStack.length > 0}
          dimFindings={chartMode === "today"}
          overlayState={chartMode === "today" ? overlayState : null}
          overlayFindings={chartMode === "today" ? overlayFindings : null}
          stackSummaryByTooth={chartMode === "today" ? stackSummaryByTooth : null}
        />
      </section>
      <div className="sr-only" aria-hidden="true">
        Role: {clinicRole || "training"}
      </div>
      {pendingMultiVisit ? (
        <MultiVisitPaintDialog
          procName={pendingMultiVisit.proc.name}
          fdi={pendingMultiVisit.fdi}
          surface={pendingMultiVisit.surface}
          onStartBundle={startDemoOngoingTreatment}
          onSetupPlan={() => setPendingMultiVisit(null)}
          onDoAllToday={() => {
            addDemoItem(pendingMultiVisit.proc, {
              tooth_number: pendingMultiVisit.fdi,
              surface: pendingMultiVisit.surface,
            });
            setPendingMultiVisit(null);
          }}
          onCancel={() => setPendingMultiVisit(null)}
        />
      ) : null}
    </div>
  );
}

function FutureNeedChartingTutorialDemo({ currency = "PHP", clinicRole }) {
  const [chartMode, setChartMode] = useState("future_need");
  const [armedProcedureId, setArmedProcedureId] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [sessionFollowUpDays, setSessionFollowUpDays] = useState(14);
  const patient = DYNAMIC_CHARTING_TUTORIAL_PATIENT;
  const procedures = DYNAMIC_CHARTING_FUTURE_NEED_DEMO_PROCEDURES;
  const procedureColorMap = useMemo(
    () => buildProcedureColorMap(procedures),
    [procedures],
  );
  const procedureColorById = useMemo(
    () => Object.fromEntries(
      procedures.map((procedure) => [procedure.id, procedureColor(procedure, procedureColorMap)]),
    ),
    [procedureColorMap, procedures],
  );
  const overlayFindings = useMemo(() => procedures.map((procedure) => ({
    code: `__doc_${procedure.id}`,
    color_hex: procedureColor(procedure, procedureColorMap),
    name: procedure.name,
  })), [procedureColorMap, procedures]);
  const overlayState = useMemo(() => buildDoctorOverlayState({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    activePlanItems: [],
    futureNeeds: needs,
    multiToothBuffer: [],
  }), [chartMode, needs]);
  const stackSummaryByTooth = useMemo(() => buildDoctorStackSummaryByTooth({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    futureNeeds: needs,
    procedureColorById,
  }), [chartMode, needs, procedureColorById]);
  const doctorProcedureLegendItems = useMemo(() => buildDoctorProcedureLegendItems({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    activePlanItems: [],
    futureNeeds: needs,
  }), [chartMode, needs]);

  const mutateNeeds = useCallback((updater) => {
    setNeeds((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      setUndoStack((stack) => [...stack, current]);
      setRedoStack([]);
      return next;
    });
  }, []);

  const addDemoNeed = useCallback((proc, ctx = {}) => {
    if (!proc) return;
    const tooth = ctx.tooth_number ? String(ctx.tooth_number) : "";
    const surface = ctx.surface ? normalizeSurfaceStr(ctx.surface) : null;
    const defaultFee = Number(proc.default_fee_centavos || 0);
    const demoId = proc.id === "demo-rct-46" && tooth === "46"
      ? "demo-future-rct-46"
      : `demo-future-${safeTestId(proc.id || "procedure")}-${safeTestId(tooth || "general")}`;
    const tutorialId = proc.id === "demo-rct-46" && tooth === "46"
      ? "doctor-item-demo-future-rct-46"
      : undefined;

    mutateNeeds((current) => {
      if (current.some((need) => need.id === demoId)) return current;
      return [
        ...current,
        {
          id: demoId,
          tutorial_id: tutorialId,
          procedure_id: proc.id,
          procedure_name: proc.name,
          procedure_category: proc.category,
          color_hex: procedureColor(proc, procedureColorMap),
          default_fee_centavos: defaultFee,
          effective_fee_centavos: defaultFee,
          additional_surface_pct: Number(proc.additional_surface_pct ?? 100),
          tooth_number: tooth,
          surface,
          surface_count: surface ? splitSurfaces(surface).length : 1,
          status: "Open",
          follow_up_after_days: sessionFollowUpDays,
          notes: "Diagnosed RCT need from Initial Charting. Not performed today.",
        },
      ];
    });
  }, [mutateNeeds, procedureColorMap, sessionFollowUpDays]);

  const removeDemoNeed = useCallback(async (id) => {
    mutateNeeds((current) => current.filter((need) => need.id !== id));
  }, [mutateNeeds]);

  const updateDemoNeed = useCallback(async (id, patch) => {
    mutateNeeds((current) => current.map((need) => {
      if (need.id !== id) return need;
      return {
        ...need,
        ...patch,
        effective_fee_centavos: patch.fee_centavos_override ?? need.effective_fee_centavos,
      };
    }));
  }, [mutateNeeds]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const previous = stack[stack.length - 1];
      setNeeds((current) => {
        setRedoStack((redo) => [...redo, current]);
        return previous;
      });
      return stack.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((stack) => {
      if (stack.length === 0) return stack;
      const next = stack[stack.length - 1];
      setNeeds((current) => {
        setUndoStack((undoStackCurrent) => [...undoStackCurrent, current]);
        return next;
      });
      return stack.slice(0, -1);
    });
  }, []);

  const handleToothClick = useCallback((toothNumber, surface) => {
    if (chartMode !== "future_need") return;
    const proc = procedures.find((procedure) => procedure.id === armedProcedureId);
    if (!proc) {
      toast.info("Pick a future procedure from the palette first.");
      return;
    }
    addDemoNeed(proc, {
      tooth_number: toothNumber,
      surface: isWholeToothProcedure(proc) ? null : surface,
    });
  }, [addDemoNeed, armedProcedureId, chartMode, procedures]);

  const palette = chartMode === "future_need" ? (
    <ProceduresPalette
      procedures={procedures}
      colorMap={procedureColorMap}
      armedProcedureId={armedProcedureId}
      onArm={setArmedProcedureId}
      onPickWholeMouth={(proc) => addDemoNeed(proc, { tooth_number: null, surface: null })}
      mode="future_need"
      currency={currency}
      eraserOn={false}
      onPickEraser={null}
    />
  ) : null;

  const futureNeedWork = chartMode === "future_need" ? (
    <FutureNeedsCard
      needs={needs}
      loaded
      currency={currency}
      patient={patient}
      onRemove={removeDemoNeed}
      onUpdate={updateDemoNeed}
      onUndo={undo}
      onRedo={redo}
      canUndo={undoStack.length > 0}
      canRedo={redoStack.length > 0}
      sessionFollowUpDays={sessionFollowUpDays}
      setSessionFollowUpDays={setSessionFollowUpDays}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-3" data-testid="doctor-page" data-tutorial-id="doctor-page">
      <WorkspaceHeader
        appt={DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT}
        patient={patient}
        clinicRole={clinicRole}
        onOpenPatient={() => {}}
        onOpenOrtho={() => {}}
        onSwitchPatient={null}
        onMarkDone={() => {}}
        markingDone={false}
        queueCount={0}
        currency={currency}
        todayTotals={{ gross: 0, discount: 0, net: 0, count: 0 }}
        lastVisit={null}
        lastVisitLoaded
        patientContext={{ medicalHistory: { data: {} }, loaded: true }}
      />
      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
        data-testid="doctor-workspace-body"
        data-tutorial-id="doctor-primary-surface"
      >
        <ChartingTab
          patientId={patient.id}
          patient={patient}
          chairside
          chartMode={chartMode}
          onChartModeChange={setChartMode}
          tutorialDemo={DYNAMIC_CHARTING_TODAY_DEMO}
          paletteOverride={palette}
          belowChartCanvas={futureNeedWork}
          chartFooter={
            chartMode === "future_need" ? (
              <ChairsideChartLegend>
                <FutureWholeMouthRibbon
                  needs={needs}
                  colorMap={procedureColorMap}
                  procedures={procedures}
                  bare
                />
                <TxPlanCategoryLegend
                  items={doctorProcedureLegendItems}
                  procedures={procedures}
                  showProcedures
                  bare
                />
              </ChairsideChartLegend>
            ) : null
          }
          onToothInterceptClick={chartMode === "future_need" ? handleToothClick : null}
          interceptEraseArmed={false}
          onToothInterceptErase={null}
          armedProcedureName={
            armedProcedureId
              ? procedures.find((procedure) => procedure.id === armedProcedureId)?.name || null
              : null
          }
          armedProcedureColor={
            armedProcedureId
              ? procedureColor(procedures.find((procedure) => procedure.id === armedProcedureId), procedureColorMap)
              : null
          }
          onClearArmedProcedure={armedProcedureId ? () => setArmedProcedureId(null) : null}
          interceptOnUndo={undo}
          interceptOnRedo={redo}
          interceptCanUndo={undoStack.length > 0}
          interceptCanRedo={redoStack.length > 0}
          dimFindings={chartMode === "future_need"}
          overlayState={chartMode === "future_need" ? overlayState : null}
          overlayFindings={chartMode === "future_need" ? overlayFindings : null}
          stackSummaryByTooth={chartMode === "future_need" ? stackSummaryByTooth : null}
        />
      </section>
      <div className="sr-only" aria-hidden="true">
        Role: {clinicRole || "training"}
      </div>
    </div>
  );
}

function TxPlanningChartingTutorialDemo({ currency = "PHP", clinicRole }) {
  const [chartMode, setChartMode] = useState("tx_plan");
  const [armedProcedureId, setArmedProcedureId] = useState(null);
  const [items, setItems] = useState([]);
  const [phaseNames, setPhaseNames] = useState({});
  const patient = DYNAMIC_CHARTING_TUTORIAL_PATIENT;
  const procedures = DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES;
  const procedureColorMap = useMemo(
    () => buildProcedureColorMap(procedures),
    [procedures],
  );
  const procedureColorById = useMemo(
    () => Object.fromEntries(
      procedures.map((procedure) => [procedure.id, procedureColor(procedure, procedureColorMap)]),
    ),
    [procedureColorMap, procedures],
  );
  const overlayFindings = useMemo(() => procedures.map((procedure) => ({
    code: `__doc_${procedure.id}`,
    color_hex: procedureColor(procedure, procedureColorMap),
    name: procedure.name,
  })), [procedureColorMap, procedures]);
  const overlayState = useMemo(() => buildDoctorOverlayState({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    activePlanItems: items,
    futureNeeds: [],
    multiToothBuffer: [],
  }), [chartMode, items]);
  const stackSummaryByTooth = useMemo(() => buildDoctorStackSummaryByTooth({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    activePlanItems: items,
    futureNeeds: [],
    procedureColorById,
  }), [chartMode, items, procedureColorById]);
  const doctorProcedureLegendItems = useMemo(() => buildDoctorProcedureLegendItems({
    chartMode,
    scheduled: [],
    dispositions: {},
    draftItems: [],
    activePlanItems: items,
    futureNeeds: [],
  }), [chartMode, items]);
  const txPlanningPhaseNames = useMemo(() => {
    const used = new Set();
    for (const item of items) {
      const itemPhase = Number(item?.phase_number);
      if (Number.isFinite(itemPhase) && itemPhase > 0) used.add(itemPhase);
      for (const visit of item?.procedure_visits ?? []) {
        const visitPhase = Number(visit?.phase_number);
        if (Number.isFinite(visitPhase) && visitPhase > 0) used.add(visitPhase);
      }
    }
    for (const phaseKey of Object.keys(phaseNames || {})) {
      const phase = Number(phaseKey);
      if (Number.isFinite(phase) && phase > 0) used.add(phase);
    }
    return Object.fromEntries(
      [...used]
        .sort((a, b) => a - b)
        .map((phase) => [phase, phaseNames?.[String(phase)] || `Appointment ${phase}`]),
    );
  }, [items, phaseNames]);

  const ensureTxPlanningPhaseName = useCallback((phaseNumber) => {
    const phase = Number(phaseNumber);
    if (!Number.isFinite(phase) || phase <= 0) return;
    setPhaseNames((current) => (
      current[String(phase)] ? current : { ...current, [String(phase)]: `Appointment ${phase}` }
    ));
  }, []);

  const handleTutorialAddPhase = useCallback((phaseNumber) => {
    ensureTxPlanningPhaseName(phaseNumber);
  }, [ensureTxPlanningPhaseName]);

  const handleTutorialSetVisitPhase = useCallback((itemId, visitId, phaseNumber) => {
    setItems((current) => current.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        procedure_visits: (item.procedure_visits || []).map((visit) => (
          visit.id === visitId ? { ...visit, phase_number: phaseNumber == null ? null : Number(phaseNumber) } : visit
        )),
      };
    }));
    ensureTxPlanningPhaseName(phaseNumber);
    if (itemId === "demo-tx-rct-46" && String(visitId).endsWith("-visit-2") && Number(phaseNumber) === 2) {
      window.dispatchEvent(new CustomEvent("tutorial:tx-planning-rct-v2-appointment-2"));
    }
  }, [ensureTxPlanningPhaseName]);

  const addPlanItem = useCallback((proc, ctx = {}) => {
    if (!proc) return;
    const tooth = ctx.tooth_number ? String(ctx.tooth_number) : "";
    const surface = proc.surface_specific && ctx.surface ? String(ctx.surface).toUpperCase() : null;
    const defaultFee = Number(proc.default_fee_centavos || 0);
    const demoId = getTxPlanningDemoItemId(proc, tooth);
    const tutorialId = getTxPlanningDemoItemAnchor(proc, tooth);

    setItems((current) => {
      if (current.some((item) => item.id === demoId)) return current;
      return [
        ...current,
        {
          id: demoId,
          tutorial_id: tutorialId,
          procedure_id: proc.id,
          procedure_code: proc.code,
          procedure_name: proc.name,
          display_label: proc.name,
          procedure_category: proc.category,
          category: proc.category,
          color: procedureColor(proc, procedureColorMap),
          color_hex: procedureColor(proc, procedureColorMap),
          fee_centavos: defaultFee,
          committed_fee_centavos: defaultFee,
          discount_centavos: 0,
          discount_pct: null,
          discount_amount_centavos: null,
          unit_count: 1,
          qty: 1,
          tooth_number: tooth,
          surface,
          status: "Candidate",
          phase_number: getTxPlanningDemoPhaseNumber(proc, tooth),
          procedure_visits: buildTxPlanningDemoVisits(proc, demoId),
        },
      ];
    });
  }, [procedureColorMap]);

  const handleToothClick = useCallback((toothNumber, surface) => {
    if (chartMode !== "tx_plan") return;
    const proc = procedures.find((procedure) => procedure.id === armedProcedureId);
    if (!proc) {
      toast.info("Pick a treatment-plan procedure first.");
      return;
    }
    addPlanItem(proc, { tooth_number: toothNumber, surface });
  }, [addPlanItem, armedProcedureId, chartMode, procedures]);

  const palette = chartMode === "tx_plan" ? (
    <ProceduresPalette
      procedures={procedures}
      colorMap={procedureColorMap}
      armedProcedureId={armedProcedureId}
      onArm={setArmedProcedureId}
      onPickWholeMouth={(proc) => addPlanItem(proc, { tooth_number: null, surface: null })}
      mode="tx_plan"
      currency={currency}
      eraserOn={false}
      onPickEraser={null}
      onPickMultiTooth={null}
    />
  ) : null;

  const activePlan = useMemo(() => ({
    id: "tutorial-tx-plan-1",
    plan_code: "TP-TUTORIAL",
    status: "Draft",
    notes: "Demo treatment plan built from the Initial Charting findings.",
    items,
    phase_names: txPlanningPhaseNames,
    phase_meta: {},
    tooth_notes: {},
    doctor_name: "Dr. Levi Jordan",
    doctor_prc_license: "PRC-0000000",
    created_at: "2026-05-18T00:00:00.000Z",
  }), [items, txPlanningPhaseNames]);

  const tutorialDemoData = useMemo(() => ({
    plans: [{
      id: activePlan.id,
      plan_code: activePlan.plan_code,
      status: activePlan.status,
      created_at: activePlan.created_at,
      item_count: items.length,
    }],
    activePlanId: activePlan.id,
    activePlan,
    procedures,
    findings: DYNAMIC_CHARTING_TX_PLAN_DEMO.findings ?? [],
    paletteCategories: DYNAMIC_CHARTING_TX_PLAN_DEMO.paletteCategories ?? [],
    snapshot: { state: DYNAMIC_CHARTING_TX_PLAN_DEMO.state },
    chairSetupMinutes: 15,
    medicalHistory: { data: {} },
    planBalance: null,
    handlers: {
      onAddPhase: handleTutorialAddPhase,
      onSetVisitPhase: handleTutorialSetVisitPhase,
    },
  }), [activePlan, handleTutorialAddPhase, handleTutorialSetVisitPhase, items.length, procedures]);

  const planWork = chartMode === "tx_plan" ? (
    <div data-tutorial-id="charting-treatment-plan-work">
      <TreatmentTab
        patientId={patient.id}
        patient={patient}
        chartless
        hideMultiTooth
        tutorialDemoData={tutorialDemoData}
      />
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-3" data-testid="doctor-page" data-tutorial-id="doctor-page">
      <WorkspaceHeader
        appt={DYNAMIC_CHARTING_TODAY_DEMO_APPOINTMENT}
        patient={patient}
        clinicRole={clinicRole}
        onOpenPatient={() => {}}
        onOpenOrtho={() => {}}
        onSwitchPatient={null}
        onMarkDone={() => {}}
        markingDone={false}
        queueCount={0}
        currency={currency}
        todayTotals={{ gross: 0, discount: 0, net: 0, count: 0 }}
        lastVisit={null}
        lastVisitLoaded
        patientContext={{ medicalHistory: { data: {} }, loaded: true }}
      />
      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
        data-testid="doctor-workspace-body"
        data-tutorial-id="doctor-primary-surface"
      >
        <ChartingTab
          patientId={patient.id}
          patient={patient}
          chairside
          chartMode={chartMode}
          onChartModeChange={setChartMode}
          tutorialDemo={DYNAMIC_CHARTING_TX_PLAN_DEMO}
          paletteOverride={palette}
          belowChartCanvas={planWork}
          chartFooter={
            chartMode === "tx_plan" ? (
              <TxPlanCategoryLegend
                items={doctorProcedureLegendItems}
                procedures={procedures}
                showProcedures
              />
            ) : null
          }
          onToothInterceptClick={chartMode === "tx_plan" ? handleToothClick : null}
          interceptEraseArmed={false}
          onToothInterceptErase={null}
          armedProcedureName={
            armedProcedureId
              ? procedures.find((procedure) => procedure.id === armedProcedureId)?.name || null
              : null
          }
          armedProcedureColor={
            armedProcedureId
              ? procedureColor(procedures.find((procedure) => procedure.id === armedProcedureId), procedureColorMap)
              : null
          }
          onClearArmedProcedure={armedProcedureId ? () => setArmedProcedureId(null) : null}
          interceptOnUndo={null}
          interceptOnRedo={null}
          interceptCanUndo={false}
          interceptCanRedo={false}
          dimFindings={chartMode === "tx_plan"}
          overlayState={chartMode === "tx_plan" ? overlayState : null}
          overlayFindings={chartMode === "tx_plan" ? overlayFindings : null}
          stackSummaryByTooth={chartMode === "tx_plan" ? stackSummaryByTooth : null}
        />
      </section>
      <div className="sr-only" aria-hidden="true">
        Role: {clinicRole || "training"}
      </div>
    </div>
  );
}

function getTxPlanningDemoItemId(proc, tooth) {
  if (proc?.id === "demo-tx-rct-46" && tooth === "46") return "demo-tx-rct-46";
  if (proc?.id === "demo-tx-zirconia-46" && tooth === "46") return "demo-tx-zirconia-46";
  return `demo-tx-${safeTestId(proc?.id || "procedure")}-${safeTestId(tooth || "general")}`;
}

function getTxPlanningDemoPhaseNumber(proc, tooth = null) {
  if (proc?.id === "demo-tx-zirconia-46") return 3;
  return 1;
}

function buildTxPlanningDemoVisits(proc, itemId) {
  if (proc?.id === "demo-tx-zirconia-46") {
    return [
      {
        id: `${itemId}-visit-1`,
        tutorial_id: "doctor-visit-demo-tx-zirconia-46-prep",
        treatment_plan_item_id: itemId,
        sequence: 1,
        template_label: "Zirconia prep, scan, shade, temp crown",
        chair_min: 90,
        lab_gap_days: 7,
        phase_number: 3,
        status: "planned",
      },
      {
        id: `${itemId}-visit-2`,
        tutorial_id: "doctor-visit-demo-tx-zirconia-46-try-in",
        treatment_plan_item_id: itemId,
        sequence: 2,
        template_label: "Zirconia try-in, fit, contacts, occlusion, shade",
        chair_min: 30,
        lab_gap_days: 3,
        phase_number: 4,
        status: "planned",
      },
      {
        id: `${itemId}-visit-3`,
        tutorial_id: "doctor-visit-demo-tx-zirconia-46-cement",
        treatment_plan_item_id: itemId,
        sequence: 3,
        template_label: "Zirconia crown cementation",
        chair_min: 45,
        lab_gap_days: null,
        phase_number: 5,
        status: "planned",
      },
    ];
  }
  if (proc?.id === "demo-tx-rct-46") {
    return [
      {
        id: `${itemId}-visit-1`,
        tutorial_id: "doctor-visit-demo-tx-rct-46-1",
        treatment_plan_item_id: itemId,
        sequence: 1,
        template_label: "Pulp removal, clean and shape, temp filling",
        chair_min: 90,
        lab_gap_days: 7,
        phase_number: 1,
        status: "planned",
      },
      {
        id: `${itemId}-visit-2`,
        tutorial_id: "doctor-visit-demo-tx-rct-46-2",
        treatment_plan_item_id: itemId,
        sequence: 2,
        template_label: "Confirm clear, root fill, seal",
        chair_min: 75,
        lab_gap_days: 7,
        phase_number: 1,
        status: "planned",
      },
    ];
  }
  return [
    {
      id: `${itemId}-visit-1`,
      tutorial_id: getTxPlanningDemoVisitAnchor(proc, itemId),
      treatment_plan_item_id: itemId,
      sequence: 1,
      template_label: proc?.name || "Planned procedure",
      chair_min: 45,
      lab_gap_days: null,
      phase_number: getTxPlanningDemoPhaseNumber(proc),
      status: "planned",
    },
  ];
}

function getTxPlanningDemoItemAnchor(proc, tooth) {
  if (proc?.id === "demo-tx-rct-46" && tooth === "46") return "doctor-item-demo-tx-rct-46";
  if (proc?.id === "demo-tx-zirconia-46" && tooth === "46") return "doctor-item-demo-tx-zirconia-46";
  return undefined;
}

function getTxPlanningDemoVisitAnchor(proc, itemId) {
  return undefined;
}

function getTodayTutorialDemoItemId(proc, tooth, surface) {
  if (proc?.id === "demo-composite-15" && tooth) {
    return `demo-today-composite-${safeTestId(tooth)}`;
  }
  if (proc?.id === "demo-extraction-48" && tooth === "48") {
    return "demo-today-extraction-48";
  }
  if (proc?.id === "demo-prophy" && !tooth) {
    return "demo-today-prophy";
  }
  return `demo-today-${safeTestId(proc?.id || "procedure")}-${safeTestId(tooth || "general")}-${safeTestId(surface || "whole")}`;
}

function getTodayTutorialDemoItemAnchor(proc, tooth) {
  if (proc?.id === "demo-composite-15" && tooth) {
    return `doctor-item-demo-today-composite-${safeTestId(tooth)}`;
  }
  if (proc?.id === "demo-extraction-48" && tooth === "48") {
    return "doctor-item-demo-today-extraction-48";
  }
  if (proc?.id === "demo-prophy" && !tooth) {
    return "doctor-item-demo-today-prophy";
  }
  return undefined;
}

function DynamicChartingTutorialModePanel({ mode }) {
  if (mode === "today") {
    return (
      <section
        className="rounded-xl border border-amber-200 bg-amber-50/60 p-4"
        data-testid="tutorial-today-work"
        data-tutorial-id="charting-today-work"
      >
        <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Today</p>
        <h2 className="mt-1 text-sm font-semibold text-slate-950">Today's work list</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="rounded-lg border border-amber-100 bg-white px-3 py-2">Tooth 15 · O composite restoration · staged for this visit</li>
          <li className="rounded-lg border border-amber-100 bg-white px-3 py-2">Tooth 48 · extraction · staged for this visit</li>
        </ul>
      </section>
    );
  }

  if (mode === "future_need") {
    return (
      <section
        className="rounded-xl border border-blue-200 bg-blue-50/60 p-4"
        data-testid="tutorial-future-need-work"
        data-tutorial-id="charting-future-need-work"
      >
        <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Future Need</p>
        <h2 className="mt-1 text-sm font-semibold text-slate-950">Open needs for follow-up</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="rounded-lg border border-blue-100 bg-white px-3 py-2">Tooth 13 · crown evaluation · discuss next visit</li>
          <li className="rounded-lg border border-blue-100 bg-white px-3 py-2">Tooth 36 · monitor watch area · recall follow-up</li>
        </ul>
      </section>
    );
  }

  if (mode === "tx_plan") {
    return null;
  }

  return null;
}

// ---------------------------------------------------------------------
// Queue — split into pure content card + drawer wrapper so the rail
// (browse mode) and the slide-over (treat mode) reuse identical UI.
// ---------------------------------------------------------------------
// Queue card caps at viewport height so a long checked-in list
// scrolls inside its own card instead of pushing the whole page
// taller. Self-contained scroller — does not stack with any other
// on the page.
function QueueContent({ queue, loading, error, activeApptId, onSelect, showCloseButton, onClose }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm flex flex-col max-h-[calc(100vh-7rem)]" data-testid="doctor-queue">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Checked in</h2>
          <span className="text-[11px] text-slate-500">({queue.length})</span>
        </div>
        {showCloseButton ? (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close"
            data-testid="doctor-queue-close"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {loading ? (
          <div className="text-xs text-slate-400 px-2 py-3 inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 rounded-md px-2 py-2 text-xs text-rose-700 m-1">
            {error}
          </div>
        ) : queue.length === 0 ? (
          <div className="text-center px-3 py-6">
            <p className="text-xs text-slate-500">No one in the chair right now.</p>
            <p className="text-[11px] text-slate-400 mt-1">Patients appear here when the front desk checks them in.</p>
          </div>
        ) : queue.map((a) => {
          const active = a.id === activeApptId;
          const done = !!a.doctor_finished_at;
          return (
            <button
              key={a.id}
              onClick={() => onSelect(a.id)}
              data-testid={`doctor-queue-item-${a.id}`}
              className={[
                "w-full text-left rounded-lg px-3 py-2 border transition-colors",
                done
                  ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-50"
                  : active
                  ? "bg-teal-50 border-teal-300"
                  : "bg-white border-transparent hover:bg-slate-50 border-slate-100",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-slate-900 truncate">{a.patient_name || "Unnamed"}</p>
                {done ? (
                  <span
                    className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                    data-testid={`doctor-queue-done-${a.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Done
                  </span>
                ) : null}
              </div>
              <p className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1 mt-0.5">
                <Stethoscope className="w-3 h-3 text-slate-400" />
                {formatDoctorName(a.doctor_name, "Unassigned")}
                <span className="text-slate-300">·</span>
                <MapPin className="w-3 h-3 text-slate-400" />
                {a.chair_name || "No chair"}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {done
                  ? `Done ${formatTime(a.doctor_finished_at)}`
                  : `In since ${a.checked_in_at ? formatTime(a.checked_in_at) : "—"}`}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QueueDrawer({ open, queue, loading, error, activeApptId, onSelect, onClose }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex"
      onClick={onClose}
      data-testid="doctor-queue-drawer-overlay"
    >
      <div
        className="w-80 max-w-[85vw] h-full bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <QueueContent
          queue={queue}
          loading={loading}
          error={error}
          activeApptId={activeApptId}
          onSelect={onSelect}
          showCloseButton
          onClose={onClose}
        />
      </div>
    </div>
  );
}

function EmptyWorkspace({ queueLoading, queueCount, onPick }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm min-h-[400px] flex items-center justify-center p-12 text-center" data-testid="doctor-empty" data-tutorial-id="doctor-primary-surface">
      <div className="max-w-sm">
        <div className="w-14 h-14 rounded-full bg-teal-50 mx-auto flex items-center justify-center">
          <Stethoscope className="w-6 h-6 text-teal-600" />
        </div>
        <h2 className="mt-4 text-base font-semibold text-slate-900">
          {queueLoading ? "Loading queue…" : queueCount > 0 ? "Pick a patient to start" : "No patients in the chair"}
        </h2>
        <p className="mt-1.5 text-sm text-slate-500">
          {queueCount > 0
            ? "Choose someone from the checked-in list to bring up their chart, treatment plan, and today's work."
            : "Once the front desk checks a patient in, they'll appear in the queue and you can start charting."}
        </p>
        <button
          onClick={onPick}
          className="xl:hidden mt-4 inline-flex items-center gap-1.5 text-sm font-semibold bg-teal-600 text-white rounded-md px-3 py-1.5 hover:bg-teal-700"
        >
          <Menu className="w-4 h-4" /> Open queue
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// useTxPlan — owns the patient's treatment plans + active plan items.
// Lifted out of TxPlanCard so chart-click handlers in Tx Plan mode can
// add items directly via addItem(), and the chart overlay can read
// the active plan's items for paint.
// ---------------------------------------------------------------------
function useTxPlan(patientId) {
  const [plans, setPlans]               = useState([]);
  const [checked, setChecked]           = useState(false);
  const [activePlanId, setActivePlanId] = useState(null);
  const [activePlan, setActivePlan]     = useState(null);
  const [creating, setCreating]         = useState(false);
  const [adding, setAdding]             = useState(false);

  const visiblePlans = useMemo(
    () => plans.filter((p) => !["Superseded", "Rejected"].includes(p.status)),
    [plans],
  );

  const loadPlans = useCallback(async () => {
    if (!patientId) { setChecked(true); return; }
    const { data } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
    const rows = treatmentPlanAuthoringPlans(data);
    setPlans(rows);
    setChecked(true);
    const visible = rows.filter((p) => !["Superseded", "Rejected"].includes(p.status));
    if (visible.length > 0) {
      setActivePlanId((prev) => (prev && visible.some((p) => p.id === prev) ? prev : visible[0].id));
    } else {
      setActivePlanId(null);
    }
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;
    loadPlans().catch(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, [loadPlans]);

  // `planIdOverride` lets a caller refresh against a freshly-created
  // plan id whose setActivePlanId() hasn't propagated yet — without
  // it, the closure here captures the old activePlanId and the next
  // re-render loses the just-loaded items. Hit on first paint of a
  // brand-new Draft plan: addItem inserted the row but the chart
  // overlay stayed empty until the second click triggered the
  // useEffect-driven reload.
  const reloadActive = useCallback(async (planIdOverride = null) => {
    const id = planIdOverride ?? activePlanId;
    if (!id) { setActivePlan(null); return; }
    const { data } = await supabase.rpc("get_treatment_plan", { p_plan_id: id });
    setActivePlan(data ?? null);
  }, [activePlanId]);

  useEffect(() => { reloadActive(); }, [reloadActive]);

  const createPlan = useCallback(async () => {
    setCreating(true);
    const { data, error } = await supabase.rpc("create_treatment_plan", { p_patient_id: patientId });
    setCreating(false);
    if (error) { toast.error(error.message ?? "Could not create plan."); return null; }
    const newId = data?.id ?? data;
    if (newId) setActivePlanId(newId);
    await loadPlans();
    return newId;
  }, [patientId, loadPlans]);

  // Add a plan item. ctx = { tooth_number, surface }. Returns the new
  // item id on success, null if the target plan isn't Draft or the
  // RPC fails. Caller can pass `planOverride` to add against a plan
  // that isn't yet reflected in `activePlan` state — used by the
  // auto-revise flow where the freshly-created Draft hasn't
  // propagated through the activePlanId useEffect yet.
  const addItem = useCallback(async (proc, ctx = {}, planOverride = null) => {
    const target = planOverride || activePlan;
    if (!target || target.status !== "Draft") return null;
    setAdding(true);
    const { data, error } = await supabase.rpc("add_plan_item", {
      p_plan_id: target.id,
      p_input: {
        procedure_id: proc.id,
        tooth_number: ctx.tooth_number ? String(ctx.tooth_number) : null,
        surface:      ctx.surface ?? null,
      },
    });
    setAdding(false);
    if (error) { toast.error(error.message ?? "Could not add item."); return null; }
    // Hand reloadActive the plan id we just inserted into — guards
    // against the stale-closure case where the caller created a new
    // Draft a moment ago and activePlanId hasn't re-rendered yet.
    await reloadActive(target.id);
    return data?.id ?? null;
  }, [activePlan, reloadActive]);

  // Revise the active plan in place — calls revise_treatment_plan
  // (which spawns a Draft from the Proposed/Accepted/etc. parent and
  // marks the parent Superseded), refreshes plans, switches the
  // active plan to the new Draft, and returns the new plan object.
  // Returns null on error / when there's nothing to revise.
  const revisePlan = useCallback(async () => {
    if (!activePlan) return null;
    const { data, error } = await supabase.rpc("revise_treatment_plan", {
      p_plan_id: activePlan.id,
    });
    if (error) { toast.error(error.message ?? "Could not revise plan."); return null; }
    const newId = data?.new_plan_id;
    if (!newId) return null;
    // Hydrate the new Draft right away so callers can act on it
    // without waiting for the activePlanId useEffect to refire.
    const { data: newPlan } = await supabase.rpc("get_treatment_plan", { p_plan_id: newId });
    await loadPlans();
    setActivePlanId(newId);
    setActivePlan(newPlan ?? null);
    return newPlan ?? null;
  }, [activePlan, loadPlans]);

  // Per-line discount on a plan item — works on any plan status.
  // Pass {pct, amount, reason}; null/null clears.
  const setItemDiscount = useCallback(async (itemId, { pct, amount, reason }) => {
    const { error } = await supabase.rpc("set_plan_item_discount", {
      p_item_id: itemId,
      p_pct:     pct ?? null,
      p_amount:  amount ?? null,
      p_reason:  reason ?? null,
    });
    if (error) { toast.error(error.message ?? "Could not update discount."); return false; }
    await reloadActive();
    return true;
  }, [reloadActive]);

  // Price + note on a plan item — Draft only (server enforces).
  const setItemPrice = useCallback(async (itemId, centavos) => {
    const { error } = await supabase.rpc("update_plan_item", {
      p_item_id: itemId,
      p_changes: { fee_centavos: Number(centavos || 0) },
    });
    if (error) { toast.error(error.message ?? "Could not update price."); return false; }
    await reloadActive();
    return true;
  }, [reloadActive]);

  const setItemNote = useCallback(async (itemId, noteText) => {
    const { error } = await supabase.rpc("update_plan_item", {
      p_item_id: itemId,
      p_changes: { notes: noteText ?? "" },
    });
    if (error) { toast.error(error.message ?? "Could not update note."); return false; }
    await reloadActive();
    return true;
  }, [reloadActive]);

  const syncActivePlan = useCallback((plan) => {
    setActivePlan(plan ?? null);
    if (plan?.id) setActivePlanId(plan.id);
  }, []);

  return {
    plans, visiblePlans, activePlanId, setActivePlanId, activePlan,
    checked, creating, adding,
    createPlan, addItem, revisePlan, reloadActive, loadPlans,
    setItemDiscount, setItemPrice, setItemNote, syncActivePlan,
  };
}

// ---------------------------------------------------------------------
// useFutureNeeds — owns the patient's standalone Open future needs.
// Future Need mode paints these into the chart and lists them in the
// FutureNeedsCard. Open needs are erasable (whiteboard layer); past
// Open they go through schedule/decline RPCs, not this hook.
// ---------------------------------------------------------------------
function useFutureNeeds(patientId) {
  const [needs, setNeeds]   = useState([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!patientId) { setLoaded(true); return; }
    const { data, error } = await supabase.rpc("list_open_treatment_needs", { p_patient_id: patientId });
    if (error) {
      // eslint-disable-next-line no-console
      console.error("[future-need] list_open_treatment_needs failed:", error);
      toast.error(`Could not load future needs: ${error.message ?? "unknown error"}`);
      setLoaded(true);
      return;
    }
    setNeeds(Array.isArray(data) ? data : []);
    setLoaded(true);
  }, [patientId]);

  useEffect(() => { reload(); }, [reload]);

  const addNeed = useCallback(async (proc, ctx = {}) => {
    if (!patientId) {
      toast.error("Patient not loaded yet.");
      return null;
    }
    // Session follow-up window: when the doctor has set a "come back when"
    // pick on the Future Needs card, every new paint inherits it. NULL
    // means "Doctor's choice" — booking dialog falls back to category map.
    // Build the payload conditionally so we don't send the key when the
    // doctor hasn't picked anything (keeps the RPC's bundle-merge logic
    // from clobbering an existing value with a NULL on a second surface
    // paint).
    const p_input = {
      patient_id:   patientId,
      procedure_id: proc.id,
      tooth_number: ctx.tooth_number ? String(ctx.tooth_number) : null,
      surface:      ctx.surface ?? null,
    };
    if (ctx.follow_up_after_days != null) {
      p_input.follow_up_after_days = ctx.follow_up_after_days;
    }
    const { data, error } = await supabase.rpc("add_treatment_need", { p_input });
    if (error) {
      // Surface the raw error so PostgREST schema-mismatch (the
      // most common silent-failure cause) is visible. Also dumps
      // to console for easy DevTools triage.
      // eslint-disable-next-line no-console
      console.error("[future-need] add_treatment_need failed:", error);
      toast.error(`Future need failed: ${error.message ?? "unknown error"}`);
      return null;
    }
    await reload();
    // RETURNS public.treatment_needs comes back as a single row
    // OR an array depending on the PostgREST inference. Read both
    // shapes so we don't silently drop the new id and break the
    // undo stack.
    const row = Array.isArray(data) ? data[0] : data;
    return row?.id ?? null;
  }, [patientId, reload]);

  const removeNeed = useCallback(async (needId) => {
    const { error } = await supabase.rpc("delete_open_treatment_need", { p_need_id: needId });
    if (error) { toast.error(error.message ?? "Could not erase future need."); return false; }
    await reload();
    return true;
  }, [reload]);

  // Partial update — fee override, discount, notes, surface, etc.
  // Caller passes any subset of update_treatment_need's accepted keys.
  const updateNeed = useCallback(async (needId, changes) => {
    const { error } = await supabase.rpc("update_treatment_need", {
      p_id: needId,
      p_changes: changes,
    });
    if (error) {
      toast.error(error.message ?? "Could not update future need.");
      return false;
    }
    await reload();
    return true;
  }, [reload]);

  return { needs, loaded, reload, addNeed, removeNeed, updateNeed };
}

// ---------------------------------------------------------------------
// useChartOps — undo/redo stack for DB-persisted chart paints (Tx Plan
// items + Future Needs). Each recordOp registers an inverse pair:
// undo() runs delete_*, redo() runs add_* and updates the id holder.
// Today mode keeps its own local-state stack inside useVisitDraft
// (no DB roundtrip needed).
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// useLastVisit — pulls the most recent FINALIZED visit for a patient
// so the chairside header can show "Last seen DATE · N procedures
// · Dr. NAME" the moment the doctor opens the chart. Saves 2 minutes
// of context-rebuilding per patient (no need to bounce out to the
// patient record). list_patient_visits already returns everything
// we need (finalized_at, doctor_name, procedure_count) — sorted
// DESC — so we just take the first finalized row.
// ---------------------------------------------------------------------
function useLastVisit(patientId) {
  const [last, setLast]     = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!patientId) { setLoaded(true); return () => {}; }
    setLoaded(false);
    supabase
      .rpc("list_patient_visits", { p_patient_id: patientId })
      .then(({ data, error }) => {
        if (cancelled) return;
        setLoaded(true);
        if (error) { setLast(null); return; }
        const rows = Array.isArray(data) ? data : [];
        const finalized = rows.find((r) => r.status === "Finalized" && r.finalized_at);
        setLast(finalized || null);
      });
    return () => { cancelled = true; };
  }, [patientId]);

  return { last, loaded };
}

function useChairsidePatientContext(patientId) {
  const [medicalHistory, setMedicalHistory] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!patientId) {
      setMedicalHistory(null);
      setLoaded(true);
      return () => {};
    }
    setLoaded(false);
    setMedicalHistory(null);
    supabase
      .rpc("get_medical_history", { p_patient_id: patientId })
      .then(({ data, error }) => {
        if (cancelled) return;
        setLoaded(true);
        if (error) {
          setMedicalHistory(null);
          return;
        }
        setMedicalHistory(data ?? null);
      });
    return () => { cancelled = true; };
  }, [patientId]);

  return { medicalHistory, loaded };
}

function useChartOps() {
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  const [busy,      setBusy]      = useState(false);

  const recordOp = useCallback((op) => {
    undoRef.current = [...undoRef.current, op];
    redoRef.current = [];
    setUndoCount(undoRef.current.length);
    setRedoCount(0);
  }, []);

  const undo = useCallback(async () => {
    if (busy || undoRef.current.length === 0) return;
    const op = undoRef.current[undoRef.current.length - 1];
    setBusy(true);
    try {
      await op.undo();
      undoRef.current = undoRef.current.slice(0, -1);
      redoRef.current = [...redoRef.current, op];
      setUndoCount(undoRef.current.length);
      setRedoCount(redoRef.current.length);
    } catch (e) {
      toast.error(e?.message ?? "Could not undo.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const redo = useCallback(async () => {
    if (busy || redoRef.current.length === 0) return;
    const op = redoRef.current[redoRef.current.length - 1];
    setBusy(true);
    try {
      await op.redo();
      redoRef.current = redoRef.current.slice(0, -1);
      undoRef.current = [...undoRef.current, op];
      setUndoCount(undoRef.current.length);
      setRedoCount(redoRef.current.length);
    } catch (e) {
      toast.error(e?.message ?? "Could not redo.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const reset = useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    setUndoCount(0);
    setRedoCount(0);
  }, []);

  return {
    recordOp, undo, redo, reset,
    canUndo: undoCount > 0 && !busy,
    canRedo: redoCount > 0 && !busy,
  };
}

// ---------------------------------------------------------------------
// PatientWorkspace
// ---------------------------------------------------------------------
function PatientWorkspace({ appt, clinic, clinicRole, currency, onOpenPatient, onOpenMedicalHistory, onSwitchPatient, onMarkDone, onReopenDone, markingDone, reopeningDone, queueCount }) {
  const { user } = useAuth();
  const { activeBranchId } = useBranch();
  const { hasFeature } = useFeatures();
  const {
    enabled: ongoingTreatmentEnabled,
    refresh: refreshOngoingTreatmentRollout,
  } = useOngoingTreatmentRollout();
  const canUseTxPlanCharting = hasFeature("tx_plan_charting");
  const disabledChairsideModes = useMemo(
    () => (canUseTxPlanCharting ? [] : ["tx_plan"]),
    [canUseTxPlanCharting],
  );
  const [patient, setPatient] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [patientError, setPatientError]     = useState(null);
  const [startOrthoOpen, setStartOrthoOpen] = useState(false);
  const [setupOrthoCase, setSetupOrthoCase] = useState(null);
  const [pendingChairsideBondingCase, setPendingChairsideBondingCase] = useState(null);
  const [contractSigningCase, setContractSigningCase] = useState(null);
  const [contractSigningSubmitting, setContractSigningSubmitting] = useState(false);
  const [bondingDecisionCase, setBondingDecisionCase] = useState(null);
  const [bondingDecisionSubmitting, setBondingDecisionSubmitting] = useState(false);
  const [chairsideAppointment, setChairsideAppointment] = useState(appt);
  const [orthoVisitModuleOpen, setOrthoVisitModuleOpen] = useState(!!appt?.ortho_case_id && !!appt?.ortho_visit_type);
  const [convertingOrthoVisit, setConvertingOrthoVisit] = useState(null);
  const [undoingOrthoVisit, setUndoingOrthoVisit] = useState(false);
  const activeAppointmentIdRef = useRef(appt?.id || null);

  useEffect(() => {
    const appointmentChanged = activeAppointmentIdRef.current !== (appt?.id || null);
    const shouldDefaultOpenOrthoModule = !!appt?.ortho_case_id && !!appt?.ortho_visit_type && !appt?.doctor_finished_at;
    setChairsideAppointment(appt);
    if (appointmentChanged) {
      activeAppointmentIdRef.current = appt?.id || null;
      setOrthoVisitModuleOpen(shouldDefaultOpenOrthoModule);
      setConvertingOrthoVisit(null);
      setUndoingOrthoVisit(false);
    } else if (appt?.doctor_finished_at) {
      setOrthoVisitModuleOpen(false);
    }
  }, [appt]);

  // Hydrate the patient row so charting + headers can render. The
  // appointment has the patient_id but not full demographics.
  const reloadPatient = useCallback(async () => {
    setPatientError(null);
    const { data, error } = await supabase.rpc("get_patient", { p_id: appt.patient_id });
    if (error) { setPatientError(error.message ?? "Could not load patient."); return; }
    const row = Array.isArray(data) ? data[0] : data;
    setPatient(row ?? null);
  }, [appt.patient_id]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPatient(true);
    setPatientError(null);
    supabase.rpc("get_patient", { p_id: appt.patient_id }).then(({ data, error }) => {
      if (cancelled) return;
      setLoadingPatient(false);
      if (error) { setPatientError(error.message ?? "Could not load patient."); return; }
      const row = Array.isArray(data) ? data[0] : data;
      setPatient(row ?? null);
    });
    return () => { cancelled = true; };
  }, [appt.patient_id]);

  // Hook MUST be called unconditionally — runs every render so the
  // draft loads in parallel with the patient hydration. Loading /
  // error early-returns happen below, after all hook calls. (React
  // Rules of Hooks; ESLint rejects hooks after an early return.)
  // Layout per Cameron's wireframe:
  //   1. Patient Information (header)
  //   2. Dynamic Charting   (chart canvas, full width)
  //   3. Today's Work       (Performed Today list)
  //   4. Tx Plan Builder    (conditional — embedded TreatmentTab)
  //   5. Visit Note         (autosaved to its clinical note row)
  // The hook coordinates both surfaces, but their database sources stay
  // separate: checkout_drafts owns Today's items while clinical_visit_notes
  // owns the visit narrative and its locked history.
  const draft = useVisitDraft(appt.id);

  // Active chart mode — drives both the chart's paint behavior
  // (PR D) and which context panel renders below the chart.
  // Default 'chart' = paint findings.
  const [chartMode, setChartMode] = useState("chart");

  useEffect(() => {
    if (!canUseTxPlanCharting && chartMode === "tx_plan") {
      setChartMode("chart");
    }
  }, [canUseTxPlanCharting, chartMode]);

  // Procedures palette state — armed procedure ID for non-Chart modes.
  // PR D wires this through the chart's onToothInterceptClick so a
  // tap on a tooth attaches the armed procedure with that tooth#.
  const [armedProcedureId, setArmedProcedureId] = useState(null);
  const [procedures, setProcedures] = useState([]);
  // procedure_id -> { enabled, after_days, followup_procedure_name }
  // Derived from the procedures catalog; drives the chairside post-op
  // chip on Done rows in ToDoTodayCard. The chief-dentist clinical
  // rule: follow-up defaults ON for procedures with
  // post_op_followup_enabled, doctor can wave off at the chair with
  // a one-tap reason. The waiver lives in followupWaivers local state
  // and applies during the checkout commit.
  const [followupWaivers, setFollowupWaivers] = useState({});

  // Load the catalog once per workspace mount. list_clinic_procedures
  // is already used by the New Appointment panel + the procedure
  // picker dialog — same shape, RLS-scoped to active clinic.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data }, { data: removalFlags }] = await Promise.all([
        supabase.rpc("list_clinic_procedures"),
        supabase.from("procedures").select("id, is_extraction, completion_finding_code"),
      ]);
      if (cancelled) return;
      const flagsById = Object.fromEntries((removalFlags || []).map((row) => [row.id, row]));
      setProcedures((Array.isArray(data) ? data : []).map((row) => ({
        ...row,
        ...(flagsById[row.id] || {}),
      })));
    })();
    return () => { cancelled = true; };
  }, []);

  // post_op_followup map. list_clinic_procedures doesn't expose the
  // post-op fields (its return signature predates that feature) — so
  // we fetch them directly from the procedures table via PostgREST.
  // Tolerant of permissions issues: an empty map just means the chip
  // silently doesn't render anywhere, which is the right safe default.
  const [postOpByProcedure, setPostOpByProcedure] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase
        .from("procedures")
        .select("id, name, post_op_followup_enabled, post_op_followup_after_days, post_op_followup_procedure_id");
      if (cancelled) return;
      if (e || !Array.isArray(data)) { setPostOpByProcedure({}); return; }
      const byId = Object.fromEntries(data.map((p) => [p.id, p]));
      const map = {};
      for (const p of data) {
        if (!p?.post_op_followup_enabled) continue;
        if (!p?.post_op_followup_after_days || !p?.post_op_followup_procedure_id) continue;
        map[p.id] = {
          enabled:                 true,
          after_days:              Number(p.post_op_followup_after_days) || 7,
          followup_procedure_id:   p.post_op_followup_procedure_id,
          followup_procedure_name: byId[p.post_op_followup_procedure_id]?.name || "Follow-up",
        };
      }
      setPostOpByProcedure(map);
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset armed procedure when mode changes — Tx Plan vs Today vs
  // Future Need are different intents; keeping the same arm across
  // a mode flip is more confusing than helpful.
  useEffect(() => {
    setArmedProcedureId(null);
  }, [chartMode]);

  // Tx Plan + Future Need state — lifted up so the chart click
  // handler and the cards below the chart share the same source.
  const tx     = useTxPlan(patient?.id);
  const future = useFutureNeeds(patient?.id);
  const lastVisit = useLastVisit(patient?.id);
  const patientContext = useChairsidePatientContext(patient?.id);
  const chairsideOrtho = useChairsideOrtho(chairsideAppointment, patient?.id);

  // On-the-go multi-visit bundle for this patient (Phase 1). The card reads
  // get_patient_bundle; every action re-reads it so the screen matches the DB.
  const [bundle, setBundle] = useState(null);
  // Mirror of the embedded TreatmentTab's plan-tab state, so Tx Plan mode
  // can render the plan selector tucked under the odontogram (gapSlot)
  // while the builder itself stays full-width below. TreatmentTab remains
  // the source of truth; this is a controlled view fed by onPlansChange.
  const [txTabsData, setTxTabsData] = useState({ plans: [], activePlanId: null, busyCreate: false, canCreate: false });
  const [markingBundleItemId, setMarkingBundleItemId] = useState(null);
  const [completingBundleItemId, setCompletingBundleItemId] = useState(null);
  const [undoingBundleItemId, setUndoingBundleItemId] = useState(null);
  const [finishRequesting, setFinishRequesting] = useState(false);
  const [mvImportOpen, setMvImportOpen] = useState(false);
  const [dentureDialogProcedure, setDentureDialogProcedure] = useState(null);
  const [dentureDialogItemId, setDentureDialogItemId] = useState(null);
  const [mistakeCorrectionTarget, setMistakeCorrectionTarget] = useState(null);
  const handoffRequirements = useMemo(
    () => ongoingTreatmentEnabled
      ? buildChairsideHandoffRequirements(bundle)
      : [],
    [bundle, ongoingTreatmentEnabled],
  );
  const primaryHandoffRequirement = handoffRequirements[0] || null;

  const loadBundle = useCallback(async () => {
    if (!ongoingTreatmentEnabled || !patient?.id) { setBundle(null); return; }
    const { data, error } = await supabase.rpc("get_patient_bundle", { p_patient_id: patient.id });
    if (error) { toast.error(error.message ?? "Could not load the ongoing bundle."); return; }
    setBundle(data ?? null);
  }, [ongoingTreatmentEnabled, patient?.id]);

  useEffect(() => { loadBundle(); }, [loadBundle]);

  useEffect(() => {
    if (ongoingTreatmentEnabled) return;
    setBundle(null);
    setBundleNotes(null);
    setMvImportOpen(false);
    setDentureDialogProcedure(null);
    setDentureDialogItemId(null);
    setMistakeCorrectionTarget(null);
  }, [ongoingTreatmentEnabled]);

  const openHandoffRequirement = useCallback((requirement = primaryHandoffRequirement) => {
    if (!requirement) return;
    const proc = procedures.find((row) => row.id === requirement.procedureId);
    if (!proc) {
      toast.error("This denture procedure is missing from the clinic catalog.");
      return;
    }
    const existingItem = (bundle?.procedures || []).find((row) => row.item_id === requirement.itemId);
    setDentureDialogItemId(requirement.itemId);
    setDentureDialogProcedure({
      ...proc,
      default_fee_centavos: existingItem?.fee_centavos ?? proc.default_fee_centavos,
    });
  }, [bundle?.procedures, primaryHandoffRequirement, procedures]);

  // Dialog action: "Start multi-visit today". If the patient has NO open
  // bundle, create one and take it live immediately (no separate "Start"
  // button, no visible Draft). If they already HAVE an open bundle (Draft or
  // live), add this procedure to it — same-visit assembly.
  const startOrAddToBundle = useCallback(async (proc, fdi, surface) => {
    if (!ongoingTreatmentEnabled) return;
    const appointmentId = appt?.id;
    if (!appointmentId) { toast.error("No active visit to attach this to."); return; }
    const { data: existing } = await supabase.rpc("get_patient_bundle", { p_patient_id: patient.id });
    const hasOpenBundle = !!existing?.plan_id;
    const calendarItem = (existing?.procedures ?? []).find((item) => (
      item.procedure_id === proc.id
      && !item.tooth_number
      && !item.arch
      && Number(item.visits_done || 0) === 0
    ));

    // Calendar can open an MV before reception knows the clinical tooth. The
    // first chairside paint claims that exact unscoped item; it must not add a
    // second procedure line to the same case.
    if (hasOpenBundle && calendarItem && fdi != null) {
      const { error: scopeError } = await supabase.rpc("scope_calendar_mv_item", {
        p_item_id: calendarItem.item_id,
        p_appointment_id: appointmentId,
        p_tooth_number: String(fdi),
        p_surface: surface ?? null,
      });
      if (scopeError) {
        toast.error(scopeError.message ?? "Could not assign this treatment to the selected tooth.");
        return;
      }
      await loadBundle();
      draft.reload();
      toast.success(`${proc.name} assigned to #${fdi} — today's MV is ready.`);
      return;
    }

    const rpc = hasOpenBundle ? "add_procedure_to_bundle" : "start_informal_bundle";
    const { data: res, error } = await supabase.rpc(rpc, {
      p_appointment_id: appointmentId,
      p_procedure_id: proc.id,
      p_tooth_number: fdi != null ? String(fdi) : null,
      p_surface: surface ?? null,
    });
    if (error) { toast.error(error.message ?? "Could not add this to the bundle."); return; }
    // A brand-new MV starts live immediately — take it from Draft to In
    // Progress right away so there's no "Draft" step and no "Start" button.
    if (!hasOpenBundle && res?.plan_id) {
      const { error: startErr } = await supabase.rpc("start_bundle", {
        p_plan_id: res.plan_id,
        p_appointment_id: appointmentId,
      });
      if (startErr) { toast.error(startErr.message ?? "Could not start the ongoing treatment."); return; }
    }
    await loadBundle();
    // Going live links Visit-1s to today's appointment, so they become part of
    // Today's plan — re-hydrate it so they appear immediately.
    draft.reload();
    toast.success(`${proc.name} added — tracking as we go.`);
  }, [appt?.id, patient?.id, loadBundle, draft, ongoingTreatmentEnabled]);

  // A booked Recall item is not a fresh Today paint: it is the same persisted
  // Future Need coming back for treatment. Graduate that exact scheduled need
  // into the MV atomically, then re-read both the ongoing card and Today's plan
  // from the database. There is no three-choice popup in this path because the
  // dentist already decided "future need" when the item was first charted.
  const startScheduledNeedAsBundle = useCallback(async (scheduledNeed, proc) => {
    const appointmentId = appt?.id;
    if (!appointmentId || !scheduledNeed?.tn_id) {
      toast.error("This recalled treatment is not linked to today's appointment.");
      return false;
    }
    const { error } = await supabase.rpc("start_scheduled_need_as_bundle", {
      p_need_id: scheduledNeed.tn_id,
      p_appointment_id: appointmentId,
    });
    if (error) {
      toast.error(error.message ?? "Could not start this ongoing treatment.");
      return false;
    }
    await loadBundle();
    draft.reload();
    toast.success(`${proc.name} started — today's visit is linked and the rest will stay tracked.`);
    return true;
  }, [appt?.id, loadBundle, draft]);

  const handleMarkBundleItemDone = useCallback(async (itemId, isMultiVisit) => {
    if (!ongoingTreatmentEnabled) return;
    setMarkingBundleItemId(itemId);
    // One completion verb per procedure type (Phase 7 Fix 2):
    //   multi-visit "Done"  → log_bundle_visit: records the visit's work, GROWS
    //     a visit if the pre-made ones are used up, and NEVER completes the
    //     procedure (it keeps rebooking; closes only via Mark complete / Finish).
    //   single-visit "Done" → complete_bundle_procedure: renders THIS visit
    //     (production credit posts) AND closes the procedure — a single-visit
    //     procedure has one completion, so "Done" = tapos na.
    const { error } = isMultiVisit
      ? await supabase.rpc("log_bundle_visit", {
          p_item_id: itemId,
          p_performed_by: appt?.doctor_id ?? null,
        })
      : await supabase.rpc("complete_bundle_procedure", {
          p_item_id: itemId,
          p_performed_by: appt?.doctor_id ?? null,
        });
    setMarkingBundleItemId(null);
    if (error) { toast.error(error.message ?? "Could not record this."); return; }
    await loadBundle();
    draft.reload();
    toast.success(isMultiVisit ? "Visit logged." : "Procedure completed.");
  }, [appt?.doctor_id, loadBundle, draft, ongoingTreatmentEnabled]);

  // Explicit "Mark complete" — a bundle procedure closes ONLY when the doctor
  // says so. It never auto-completes when its visits run out.
  const handleCompleteBundleItem = useCallback(async (itemId) => {
    if (!ongoingTreatmentEnabled) return;
    if (!appt?.id || !appt?.doctor_id) {
      toast.error("A checked-in appointment and doctor are required to complete this procedure.");
      return;
    }
    setCompletingBundleItemId(itemId);
    const { error } = await supabase.rpc("complete_bundle_item_today", {
      p_item_id: itemId,
      p_appointment_id: appt.id,
      p_performed_by: appt.doctor_id,
    });
    setCompletingBundleItemId(null);
    if (error) { toast.error(error.message ?? "Could not complete this procedure."); return; }
    await Promise.all([loadBundle(), draft.reload()]);
    toast.success("Procedure completed and Today marked Done.");
  }, [appt?.id, appt?.doctor_id, loadBundle, draft, ongoingTreatmentEnabled]);

  // Dentures remain in the same MV as RCT, but Today records only events the
  // doctor explicitly confirms. An ordinary visit keeps the item open.
  const handleRecordDentureVisit = useCallback(async (itemId, dentureItem = null) => {
    if (!appt?.id) {
      toast.error("A checked-in appointment is required to record this visit.");
      return;
    }
    if (!dentureItem?.arch) {
      const proc = procedures.find((row) => row.id === dentureItem?.procedure_id);
      if (!proc) {
        toast.error("This denture procedure is missing from the clinic catalog.");
        return;
      }
      setDentureDialogItemId(itemId);
      setDentureDialogProcedure({
        ...proc,
        default_fee_centavos: dentureItem?.fee_centavos ?? proc.default_fee_centavos,
      });
      return;
    }
    setMarkingBundleItemId(itemId);
    const { data, error } = await supabase.rpc("record_denture_visit", {
      p_item_id: itemId,
      p_appointment_id: appt.id,
    });
    setMarkingBundleItemId(null);
    if (error) { toast.error(error.message ?? "Could not record this denture visit."); return; }
    await loadBundle();
    draft.reload();
    toast.success(data?.already_recorded
      ? "This denture visit was already recorded today."
      : "Denture visit recorded for today.");
  }, [appt?.id, loadBundle, draft, procedures]);

  // Final completion is the denture's explicit terminal event. The legacy RPC
  // name describes the clinical event, while the operator-facing action is the
  // same unambiguous "Mark complete" used by every other multi-visit item. The
  // database records today's visit and completes the existing MV item in one
  // transaction.
  const handleInstallDenture = useCallback(async (itemId) => {
    if (!appt?.id) {
      toast.error("A checked-in appointment is required to mark this item complete.");
      return;
    }
    setCompletingBundleItemId(itemId);
    const { error } = await supabase.rpc("install_denture_today", {
      p_item_id: itemId,
      p_appointment_id: appt.id,
    });
    setCompletingBundleItemId(null);
    if (error) { toast.error(error.message ?? "Could not mark this item complete."); return; }
    await loadBundle();
    draft.reload();
    setDentureReloadToken((token) => token + 1);
    toast.success("Item marked complete. No more visits are needed.");
  }, [appt?.id, loadBundle, draft]);

  const handleUndoBundleItem = useCallback(async (itemId) => {
    if (!ongoingTreatmentEnabled) return;
    setUndoingBundleItemId(itemId);
    const { error } = await supabase.rpc("undo_bundle_item_completion", { p_item_id: itemId });
    setUndoingBundleItemId(null);
    if (error) { toast.error(error.message ?? "Could not reopen this procedure."); return; }
    await loadBundle();
    draft.reload();
    toast.success("Procedure reopened.");
  }, [loadBundle, draft, ongoingTreatmentEnabled]);

  // Re-price one bundle item — allowed only in the first-visit staging window
  // (guarded server-side). Returns true so the card can exit its edit state.
  const handleEditBundlePrice = useCallback(async (itemId, feeCentavos) => {
    const { error } = await supabase.rpc("update_bundle_item_price", {
      p_item_id: itemId,
      p_fee_centavos: feeCentavos,
    });
    if (error) { toast.error(error.message ?? "Could not update the price."); return false; }
    await loadBundle();
    draft.reload();
    toast.success("Price updated.");
    return true;
  }, [loadBundle, draft]);

  // "Tapos na, bill it" — the doctor flags the treatment done; front desk bills
  // + closes it at checkout. This does NOT bill or close here (a bundle's charge
  // lives at checkout), so it's just a signal.
  const handleRequestFinish = useCallback(async () => {
    if (!bundle?.plan_id || !appt?.id) return;
    setFinishRequesting(true);
    const { error } = await supabase.rpc("request_bundle_finish", {
      p_plan_id: bundle.plan_id,
      p_appointment_id: appt.id,
    });
    setFinishRequesting(false);
    if (error) { toast.error(error.message ?? "Could not mark the treatment finished."); return; }
    // request_bundle_finish persists today's matching MV rows as Done in the
    // same transaction as the finish request. Re-read both panels so the
    // doctor immediately sees the database-confirmed handoff.
    await Promise.all([loadBundle(), draft.reload()]);
    toast.success("Treatment finished. Today is marked Done and the front desk will collect the remaining balance.");
  }, [bundle?.plan_id, appt?.id, loadBundle, draft]);

  const handleCancelFinish = useCallback(async () => {
    if (!bundle?.plan_id) return;
    setFinishRequesting(true);
    const { error } = await supabase.rpc("cancel_bundle_finish", { p_plan_id: bundle.plan_id });
    setFinishRequesting(false);
    if (error) { toast.error(error.message ?? "Could not undo."); return; }
    await loadBundle();
  }, [bundle?.plan_id, loadBundle]);

  const dentureProcedureIds = useMemo(
    () => new Set(procedures.filter(isDentureProcedure).map((procedure) => procedure.id)),
    [procedures],
  );

  const handleRemoveScheduledDenture = useCallback(async (scheduledRow) => {
    if (!scheduledRow?.tn_id || !appt?.id) {
      toast.error("This scheduled denture is missing its booking record.");
      return false;
    }
    const { data, error } = await supabase.rpc("remove_scheduled_denture_from_today", {
      p_appointment_id: appt.id,
      p_need_id: scheduledRow.tn_id,
    });
    if (error) {
      toast.error(error.message ?? "Could not remove the scheduled denture.");
      return false;
    }
    await loadBundle();
    draft.reload();
    toast.success(data?.case_abandoned
      ? "Mistaken denture booking removed. The empty duplicate case was closed."
      : "Removed from today. The ongoing denture case was kept.");
    return true;
  }, [appt?.id, draft, loadBundle]);

  // Per-visit progress diary for the open MV. Rides a SEPARATE RPC
  // (get_mv_notes), never get_patient_bundle, so the card's notes and its money
  // can't break each other. Every write re-reads so the screen matches the DB.
  const [bundleNotes, setBundleNotes] = useState(null);

  const loadBundleNotes = useCallback(async () => {
    if (!ongoingTreatmentEnabled || !bundle?.plan_id) { setBundleNotes(null); return; }
    const { data, error } = await supabase.rpc("get_mv_notes", {
      p_plan_id: bundle.plan_id,
      p_appointment_id: appt?.id ?? null,
    });
    if (error) { toast.error(error.message ?? "Could not load visit notes."); return; }
    setBundleNotes(data ?? null);
  }, [bundle?.plan_id, appt?.id, ongoingTreatmentEnabled]);

  useEffect(() => { loadBundleNotes(); }, [loadBundleNotes]);

  const handleSaveBundleNote = useCallback(async ({ what_happened, next_visit_plan }) => {
    if (!ongoingTreatmentEnabled || !bundle?.plan_id || !appt?.id) return;
    const { error } = await supabase.rpc("save_mv_note", {
      p_plan_id: bundle.plan_id,
      p_appointment_id: appt.id,
      p_what_happened: what_happened ?? "",
      p_next_visit_plan: next_visit_plan ?? "",
    });
    if (error) { toast.error(error.message ?? "Could not save the note."); return; }
    await loadBundleNotes();
  }, [bundle?.plan_id, appt?.id, loadBundleNotes, ongoingTreatmentEnabled]);

  const handleAddBundleNoteAddendum = useCallback(async (noteId, text) => {
    if (!ongoingTreatmentEnabled) return;
    const { error } = await supabase.rpc("add_mv_note_addendum", {
      p_note_id: noteId,
      p_text: text,
    });
    if (error) { toast.error(error.message ?? "Could not add the note."); return; }
    await loadBundleNotes();
    toast.success("Addendum added.");
  }, [loadBundleNotes, ongoingTreatmentEnabled]);

  const handleBundleMistakeCorrected = useCallback(async (result) => {
    // The correction RPC commits first. Every affected surface then re-reads
    // database truth; the card never disappears optimistically.
    await loadBundle();
    draft.reload();
    tx.reloadActive?.();
    treatmentTabRef.current?.refresh?.();
    setDentureReloadToken((token) => token + 1);
    toast.success(result?.mode === "reverse_recorded"
      ? "Mistaken entry removed. Recorded production credit was reversed."
      : "Mistaken entry removed from the ongoing treatment.");
  }, [draft, loadBundle, tx]);

  // Soft empty-note nudge when the doctor hands the visit to checkout. If the
  // open MV has no note for today, confirm that the doctor really wants to
  // continue without one — but NEVER block: confirming records the skip
  // (audit only) and proceeds. Cancelling keeps the doctor on chairside so
  // they can write the note. Falls back to the raw handoff when there's no
  // open MV or the note already has content.
  const saveVisitNoteNow = draft.saveNoteNow;
  const handleMarkDoneWithNotePrompt = useCallback(async (appointmentId) => {
    if (primaryHandoffRequirement) {
      toast.error(`${primaryHandoffRequirement.procedureName}: ${primaryHandoffRequirement.message}`);
      openHandoffRequirement(primaryHandoffRequirement);
      return;
    }

    // Force the final keystroke into the database before doctor_finished_at
    // changes. If saving fails, keep the patient in Chairside.
    const noteSaved = await saveVisitNoteNow();
    if (!noteSaved) return;

    if (hasMedicalNotTodayDisposition(draft.dispositions) && !draft.note.trim()) {
      toast.error("Add a Visit note explaining the medical reason before handing the patient to reception.");
      return;
    }

    const bundleNoteIsBlank =
      !!bundle?.plan_id &&
      !draft.note.trim() &&
      (!bundleNotes?.current_note ||
        (!bundleNotes.current_note.what_happened && !bundleNotes.current_note.next_visit_plan));
    if (bundleNoteIsBlank) {
      const continueWithoutNote = confirmCheckoutWithoutMvNote((message) => window.confirm(message));
      if (!continueWithoutNote) return; // stay on chairside so the doctor can write the note
      // Confirmed without a note — record it (never blocks), then continue.
      const { error } = await supabase.rpc("log_mv_note_skip", {
        p_plan_id: bundle.plan_id,
        p_appointment_id: appt.id,
      });
      if (error) { toast.error(error.message ?? "Could not record the skip."); }
    }
    await onMarkDone?.(appointmentId);
  }, [bundle?.plan_id, bundleNotes?.current_note, appt?.id, onMarkDone, openHandoffRequirement, primaryHandoffRequirement, saveVisitNoteNow, draft.dispositions, draft.note]);

  const [orthoPanelDone, setOrthoPanelDone] = useState(false);
  const orthoVisitOptions = useMemo(
    () => buildChairsideOrthoVisitOptions(chairsideOrtho.rows),
    [chairsideOrtho.rows],
  );
  const showHeaderOrthoButton = !chairsideOrtho.loading && !chairsideOrtho.activeCase;
  const showOrthoVisitModule = chairsideOrtho.isOrthoVisit || !!chairsideOrtho.activeCase || orthoVisitOptions.length > 0;
  const currentOrthoVisitRecorded = useMemo(() => Boolean(currentOrthoVisitRow(
    chairsideOrtho.activeCase,
    chairsideOrtho.visitType,
    chairsideAppointment?.id,
  )?.ortho_visit?.id), [chairsideAppointment?.id, chairsideOrtho.activeCase, chairsideOrtho.visitType]);
  const orthoVisitRecorded = orthoPanelDone || currentOrthoVisitRecorded;
  const effectiveDraftItems = useMemo(() => {
    const items = Array.isArray(draft.items) ? draft.items : [];
    if (chairsideOrtho.isOrthoVisit) return items;
    return items.filter((item) => !isOrthoCheckoutDraftRow(item));
  }, [chairsideOrtho.isOrthoVisit, draft.items]);

  const handleOpenOrtho = useCallback(() => {
    const patientBlock = getOrthoPlanCreationBlock({ patient });
    if (patientBlock) {
      toast.error(patientBlock.message);
      return;
    }

    if (convertingOrthoVisit) return;
    if (chairsideOrtho.loading) {
      toast.message("Loading ortho case...");
      return;
    }

    const row = chairsideOrtho.activeCase;

    if (!row) {
      setStartOrthoOpen(true);
      return;
    }

    if (chairsideOrtho.isOrthoVisit) {
      setOrthoVisitModuleOpen(true);
      return;
    }

    setOrthoVisitModuleOpen(true);
  }, [chairsideOrtho, convertingOrthoVisit, patient]);

  const handleContinueOrthoVisit = useCallback(() => {
    setOrthoVisitModuleOpen(true);
    setChartMode("today");
    window.setTimeout(() => {
      scrollToChairsideOrthoPanel();
    }, 0);
  }, []);

  const convertCurrentVisitToOrthoVisit = useCallback(async (option) => {
    if (!chairsideAppointment?.id || !option?.caseId || !option?.visitType) return false;
    const pendingKey = `${option.caseId}:${option.visitType}`;
    setConvertingOrthoVisit(pendingKey);
    const { data, error } = await supabase.rpc("make_current_visit_ortho_visit", {
      p_appointment_id: chairsideAppointment.id,
      p_case_id: option.caseId,
      p_visit_type: option.visitType,
    });
    setConvertingOrthoVisit(null);
    if (error) {
      toast.error(error.message || `Could not make this visit an Ortho ${option.label.toLowerCase()} visit.`);
      chairsideOrtho.reload?.();
      return false;
    }
    const updatedAppointment = data?.appointment || data;
    if (updatedAppointment?.id) {
      setChairsideAppointment((current) => ({ ...current, ...updatedAppointment }));
    } else {
      setChairsideAppointment((current) => ({
        ...current,
        ortho_case_id: option.caseId,
        ortho_visit_type: option.visitType,
      }));
    }
    setOrthoVisitModuleOpen(true);
    chairsideOrtho.reload?.();
    toast.success(option.successMessage || `Today's visit is now an Ortho ${option.label.toLowerCase()} visit.`);
    window.setTimeout(() => {
      scrollToChairsideOrthoPanel();
    }, 0);
    return true;
  }, [chairsideAppointment?.id, chairsideOrtho]);

  const handleSelectOrthoVisit = useCallback(async (option) => {
    if (!chairsideAppointment?.id || !option?.caseId || !option?.visitType || convertingOrthoVisit) return;
    await convertCurrentVisitToOrthoVisit(option);
  }, [chairsideAppointment?.id, convertingOrthoVisit, convertCurrentVisitToOrthoVisit]);

  const handleStartBondingNow = useCallback(async () => {
    const row = bondingDecisionCase;
    const caseId = row?.case?.id;
    if (!caseId || bondingDecisionSubmitting || convertingOrthoVisit) return;
    setBondingDecisionSubmitting(true);
    const converted = await convertCurrentVisitToOrthoVisit({
      caseId,
      visitType: "bonding",
      label: "Bond",
      successMessage: "Current visit converted to Ortho bonding.",
    });
    setBondingDecisionSubmitting(false);
    if (converted) {
      setBondingDecisionCase(null);
    }
  }, [bondingDecisionCase, bondingDecisionSubmitting, convertingOrthoVisit, convertCurrentVisitToOrthoVisit]);

  const handleScheduleBondingLater = useCallback(() => {
    setBondingDecisionCase(null);
    chairsideOrtho.reload?.();
    toast.message("Ortho case is ready. Schedule the bonding visit from Patient record > Ortho.");
  }, [chairsideOrtho]);

  const handleMarkChairsideContractSigned = useCallback(async () => {
    const row = contractSigningCase;
    const caseId = row?.case?.id;
    if (!caseId || contractSigningSubmitting) return;

    setContractSigningSubmitting(true);
    let result;
    try {
      result = await supabase.rpc("mark_ortho_contract_signed", {
        p_case_id: caseId,
      });
    } catch (err) {
      toast.error(err?.message || "Could not mark Ortho case accepted.");
      setContractSigningSubmitting(false);
      return;
    }
    setContractSigningSubmitting(false);

    if (result.error) {
      toast.error(result.error.message || "Could not mark Ortho case accepted.");
      return;
    }

    const nextRow = {
      ...row,
      billing: {
        ...(row.billing || {}),
        ...(result.data || {}),
      },
    };
    setContractSigningCase(null);
    setBondingDecisionCase(nextRow);
    chairsideOrtho.reload?.();
    toast.success("Ortho case accepted.");
  }, [chairsideOrtho, contractSigningCase, contractSigningSubmitting]);

  const handleUndoOrthoVisit = useCallback(async () => {
    if (!chairsideAppointment?.id || undoingOrthoVisit) return;
    setUndoingOrthoVisit(true);
    const restoringAdjustment = chairsideAppointment?.ortho_visit_type === "debonding";
    const { data, error } = await supabase.rpc(
      restoringAdjustment
        ? "restore_ortho_adjustment_after_debonding_today"
        : "undo_current_visit_ortho_visit",
      {
      p_appointment_id: chairsideAppointment.id,
      },
    );
    setUndoingOrthoVisit(false);
    if (error) {
      toast.error(error.message || "Could not remove the Ortho visit from today.");
      return;
    }
    const updatedAppointment = data?.appointment || data;
    setChairsideAppointment((current) => ({
      ...current,
      ...(updatedAppointment?.id ? updatedAppointment : {}),
      ortho_case_id: updatedAppointment?.ortho_case_id ?? (restoringAdjustment ? current?.ortho_case_id : null),
      ortho_visit_type: updatedAppointment?.ortho_visit_type ?? (restoringAdjustment ? "adjustment" : null),
    }));
    setOrthoPanelDone(false);
    setOrthoVisitModuleOpen(restoringAdjustment);
    chairsideOrtho.reload?.();
    toast.success(restoringAdjustment ? "Debonding removed. This visit is an adjustment again." : "Ortho visit removed from today.");
  }, [chairsideAppointment?.id, chairsideAppointment?.ortho_visit_type, chairsideOrtho, undoingOrthoVisit]);

  const handleChairsideOrthoVisitTypeChanged = useCallback(({ caseId, appointmentId, visitType }) => {
    if (!visitType || (appointmentId && appointmentId !== chairsideAppointment?.id)) return;
    setChairsideAppointment((current) => ({
      ...current,
      ortho_case_id: caseId || current?.ortho_case_id || null,
      ortho_visit_type: visitType,
    }));
    setOrthoPanelDone(false);
    setOrthoVisitModuleOpen(true);
  }, [chairsideAppointment?.id]);

  useEffect(() => {
    setOrthoPanelDone(false);
    setPendingChairsideBondingCase(null);
    setContractSigningCase(null);
    setBondingDecisionCase(null);
  }, [appt?.id]);

  // Doctor's session-level "come back when" pick. When set, every new
  // Future Need paint in this chairside session stamps the same window.
  // NULL = Doctor's choice (booking dialog uses the procedure category
  // default). Lives at page level so the chart paint handler can read
  // it AND the FutureNeedsCard can render the picker.
  const [sessionFollowUpDays, setSessionFollowUpDays] = useState(null);

  // The embedded TreatmentTab (chartless mode) renders the full
  // patient-page builder under the chairside chart in Tx Plan mode.
  // Its ref lets the chairside paint flow trigger a refresh after
  // writing through useTxPlan + add_plan_item, so plan tabs and
  // builder stay in lockstep.
  const treatmentTabRef = useRef(null);

  // Chart canvas ref so we can scroll it into view when the user
  // arms a procedure while scrolled down in the long Tx Plan
  // builder. The dentist's intent on arming is "I want to paint
  // next" — losing the chart to viewport is friction.
  const chartCanvasRef = useRef(null);
  const [chartBaseState, setChartBaseState] = useState(null);
  const [dentureReloadToken, setDentureReloadToken] = useState(0);
  const [patientDentures, setPatientDentures] = useState([]);
  const missingFdiSet = useMemo(
    () => extractMissingFdiSet(chartBaseState),
    [chartBaseState],
  );
  useEffect(() => {
    let cancelled = false;
    if (!patient?.id) {
      setPatientDentures([]);
      return () => { cancelled = true; };
    }
    supabase.rpc("list_patient_dentures", { p_patient_id: patient.id }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        setPatientDentures([]);
        return;
      }
      setPatientDentures(Array.isArray(data) ? data : []);
    });
    return () => { cancelled = true; };
  }, [patient?.id, dentureReloadToken]);
  const dentureChartGroups = useMemo(
    () => buildDentureChartGroups(patientDentures),
    [patientDentures],
  );

  // Erase tool armed state — chairside-only. When on, tooth-surface
  // clicks remove paint instead of adding it. Reset whenever mode
  // switches (Tx Plan-mode erase doesn't carry over to Today mode).
  const [eraseArmed, setEraseArmed] = useState(false);
  useEffect(() => { setEraseArmed(false); }, [chartMode]);

  // Multi-Tooth Tool flow — chart-canvas selection that mirrors the
  // patient-tab Tx Plan version. Click Multi-Tooth → arms select mode;
  // tap adjacent teeth on the chart → buffer accumulates; click Define
  // (N) → BuildMultiToothDialog opens with the selected teeth preset.
  // Only meaningful in tx_plan mode; resets when the chart mode flips.
  const [multiToothArmed,  setMultiToothArmed]  = useState(false);
  const [multiToothBuffer, setMultiToothBuffer] = useState([]);
  const [multiToothDialogOpen, setMultiToothDialogOpen] = useState(false);
  useEffect(() => {
    setMultiToothArmed(false);
    setMultiToothBuffer([]);
    setMultiToothDialogOpen(false);
  }, [chartMode]);

  // Duplicate-paint warning state. Cameron's rule: same procedure
  // on same tooth (or same tooth+surface for surface-specific
  // procedures) requires confirmation. Once dismissed via the
  // "Don't show again" checkbox, the warning is silenced for the
  // current browser.
  const [pendingDup, setPendingDup] = useState(null); // { proc, fdi, surface, label, mode } | null
  const [pendingMultiVisit, setPendingMultiVisit] = useState(null); // { proc, fdi, surface, label, rolloutEnabled } | null
  const resolveOngoingTreatmentEnabled = useCallback(
    () => resolveOngoingTreatmentRollout({
      enabled: ongoingTreatmentEnabled,
      refresh: refreshOngoingTreatmentRollout,
    }),
    [ongoingTreatmentEnabled, refreshOngoingTreatmentRollout],
  );
  // After painting a MULTI-VISIT procedure in Future Need mode, auto-open the
  // "Propose as a plan?" gate on that fresh need — mirrors the Today-chart
  // multi-visit prompt. FutureNeedsCard watches this and opens its modal.
  const [autoProposeNeedId, setAutoProposeNeedId] = useState(null);
  // Keyboard shortcut cheatsheet — press "?" to open. Discoverability
  // path for the chairside-only shortcuts (1/2/3/4 mode switch,
  // ⌘Z / ⌘⇧Z / ⌘Y undo/redo).
  const [helpOpen, setHelpOpen] = useState(false);
  const dupDismissed = (() => {
    try { return localStorage.getItem("dos_v2_dupe_warning_dismissed") === "1"; }
    catch { return false; }
  })();

  // Undo/redo for DB-persisted chart paints. Distinct from the
  // useVisitDraft local stack (which covers Today mode).
  const ops = useChartOps();
  // Reset the ops stack when the appointment / patient changes —
  // undoing a paint from a previous patient's session would be a
  // cross-patient leak.
  useEffect(() => { ops.reset(); }, [appt.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll: when the user arms a procedure or the erase tool
  // while scrolled down in the long Tx Plan builder, the chart is
  // off-screen — they'd have to manually scroll back to paint.
  // Bring it into view automatically. Only fires when something
  // becomes armed (transitions from false → true), and only when
  // the chart is currently below the viewport.
  useEffect(() => {
    const armed = !!armedProcedureId || eraseArmed;
    if (!armed) return;
    const el = chartCanvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (fullyVisible) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [armedProcedureId, eraseArmed]);

  // Synthetic overlay catalog — every procedure in the catalog
  // becomes a finding-shaped entry with a category-derived color.
  // The chart paints overlay entries on top of the base findings,
  // so a Composite painted on tooth #14 surface M shows as that
  // procedure's color over any caries finding on the same surface.
  // Position-based color map (built once per procedures load) —
  // gives the dark→light ramp within each category that hash-based
  // assignment couldn't deliver. Procedures sorted alphabetically
  // inside their category, hue index = position % 5.
  const procedureColorMap = useMemo(
    () => buildProcedureColorMap(procedures),
    [procedures],
  );

  const overlayFindings = useMemo(() => {
    return [
      ...procedures.map((p) => ({
        code: `__doc_${p.id}`,
        color_hex: procedureColor(p, procedureColorMap),
        name: p.name,
      })),
      {
        code: DOCTOR_MULTI_TOOTH_PENDING_CODE,
        color_hex: "#a78bfa",
        name: "Multi-tooth selection",
      },
    ];
  }, [procedures, procedureColorMap]);

  // tn_ids of scheduled needs the doctor has already pulled into
  // Today's Work via "Done" — the To Do Today card uses this set to
  // hide rows that are already in the bill so the doctor isn't
  // tempted to double-tap. Hook above any early return for Rules of
  // Hooks compliance, same as todayTotals below.
  const doneWorkKeysSet = useMemo(() => {
    const s = new Set();
    for (const it of effectiveDraftItems) {
      const key = scheduledWorkKey(it);
      if (key) s.add(key);
      if (it?.tn_id) s.add(`tn:${it.tn_id}`);
    }
    return s;
  }, [effectiveDraftItems]);

  // Reconcile the chart with Today's plan: when the dentist charts a
  // procedure that matches a booked reminder, the reminder auto-checks off
  // (no separate "Done" tap). Chief-dentist guardrails:
  //   • match by procedure_id;
  //   • if the reminder names a tooth, the charted work must be on THAT tooth
  //     (a composite on #26 must not tick off a #12 reminder);
  //   • a generic (no-tooth) booking matches any same-procedure chart;
  //   • one charted item consumes at most one reminder (one-to-one).
  // Returns reminderKey -> { tooth, surface } so the row can show
  // "Done · from chart #12", never a bare silent checkmark.
  const chartReconciledReminders = useMemo(
    () => reconcileChartToReminders(draft.scheduled, effectiveDraftItems, {
      keyOf: scheduledWorkKey,
      doneKeys: doneWorkKeysSet,
      dispositions: draft.dispositions,
    }),
    [draft.scheduled, draft.dispositions, effectiveDraftItems, doneWorkKeysSet],
  );

  // Bundle items marked Done in "Today's plan" this visit (staged in the
  // draft — the visit isn't logged until checkout). Passed to the Ongoing
  // card so its matching row flips Scheduled → Ongoing immediately, no refresh.
  const stagedDoneBundleItemIds = useMemo(() => {
    const s = new Set();
    for (const it of effectiveDraftItems) {
      if ((it.outcome || "done") === "done" && it.treatment_plan_item_id) {
        s.add(it.treatment_plan_item_id);
      }
    }
    return s;
  }, [effectiveDraftItems]);

  // Running tally of Today's draft — gross / discount / net across
  // every "done" item. Pinned to the chairside header so the doctor
  // sees what'll bill at checkout regardless of which mode they're
  // in (Chart / Tx Plan / Today / Future Need). Only "done" items
  // count; Not-Today and Declined sit in the same draft list but
  // shouldn't roll into the bill. Hook MUST be defined above the
  // loading / error early returns to satisfy the Rules of Hooks.
  const todayTotals = useMemo(() => {
    const done = effectiveDraftItems.filter((it) => (it.outcome || "done") === "done");
    const gross = done.reduce(
      (s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
    const discount = done.reduce(
      (s, it) => s + Number(it.item_discount_centavos || 0), 0);
    const net = Math.max(0, gross - discount);
    return { gross, discount, net, count: done.length };
  }, [effectiveDraftItems]);

  // Synthetic overlay STATE — built from the active mode's painted
  // items. Each item with a tooth_number contributes a finding entry
  // on that tooth. Whole-tooth items (surface=null) expand to all
  // five surfaces so the tooth visually paints in full color.
  //   today        → checkout-draft items (outcome === 'done')
  //   tx_plan      → active plan items (Candidate/Accepted/etc.)
  //   future_need  → patient's Open standalone treatment_needs
  // On-the-go bundle procedures, shaped for the chart overlay (they paint on
  // their tooth in Today mode so the odontogram shows in-progress treatment).
  const bundleItems = useMemo(
    () => (bundle?.procedures || [])
      .filter((p) => p.tooth_number && p.procedure_id)
      .map((p) => ({
        tooth_number: p.tooth_number,
        procedure_id: p.procedure_id,
        surface: p.surface ?? null,
        procedure_name: p.procedure_name,
        // status drives the odontogram color: an OPEN item paints "Ongoing"
        // (in-progress) even with logged visits; a Completed item drops out
        // of the Ongoing layer so its tooth greens like any finished work.
        status: p.status,
        item_id: p.item_id,
      })),
    [bundle],
  );

  const overlayState = useMemo(() => buildDoctorOverlayState({
    chartMode,
    scheduled: draft.scheduled,
    dispositions: draft.dispositions,
    draftItems: effectiveDraftItems,
    activePlanItems: tx.activePlan?.items || [],
    futureNeeds: future.needs,
    bundleItems,
    multiToothBuffer,
  }), [chartMode, effectiveDraftItems, draft.scheduled, draft.dispositions, tx.activePlan, future.needs, bundleItems, multiToothBuffer]);

  const stackSummaryByTooth = useMemo(() => {
    if (chartMode === "tx_plan") return normalizeStackSummaryByTooth(tx.activePlan?.items || []);
    if (chartMode !== "today" && chartMode !== "future_need") return null;
    const procedureColorById = Object.fromEntries(
      procedures.map((procedure) => [procedure.id, procedureColor(procedure, procedureColorMap)]),
    );
    return buildDoctorStackSummaryByTooth({
      chartMode,
      scheduled: draft.scheduled,
      dispositions: draft.dispositions,
      draftItems: effectiveDraftItems,
      futureNeeds: future.needs,
      bundleItems,
      procedureColorById,
    });
  }, [chartMode, draft.dispositions, effectiveDraftItems, draft.scheduled, future.needs, bundleItems, procedureColorMap, procedures, tx.activePlan]);

  const doctorProcedureLegendItems = useMemo(() => buildDoctorProcedureLegendItems({
    chartMode,
    scheduled: draft.scheduled,
    dispositions: draft.dispositions,
    draftItems: effectiveDraftItems,
    activePlanItems: tx.activePlan?.items || [],
    futureNeeds: future.needs,
    bundleItems,
  }), [chartMode, effectiveDraftItems, draft.scheduled, draft.dispositions, tx.activePlan, future.needs, bundleItems]);

  const futureNeedNoteTextByTooth = useMemo(() => {
    if (chartMode !== "future_need") return {};
    const out = {};
    for (const n of (future.needs || [])) {
      if (!n.tooth_number) continue;
      const fdi = String(n.tooth_number);
      const note = String(n.notes || "").trim();
      if (!note) continue;
      const existing = out[fdi] ? out[fdi].split("\n") : [];
      if (!existing.includes(note)) out[fdi] = [...existing, note].join("\n");
    }
    return out;
  }, [chartMode, future.needs]);

  const saveFutureNeedToothNote = useCallback(async (fdi, noteText) => {
    if (chartMode !== "future_need") return false;
    const rows = (future.needs || []).filter((n) => String(n.tooth_number || "") === String(fdi));
    if (rows.length === 0) {
      toast.error("Paint a future need on this tooth before adding a Future Need note.");
      return false;
    }
    const clean = String(noteText || "").trim();
    const results = await Promise.all(rows.map((n) => future.updateNeed(n.id, { notes: clean })));
    if (results.some((ok) => ok === false)) return false;
    toast.success(clean ? "Future Need note saved." : "Future Need note cleared.");
    return true;
  }, [chartMode, future]);

  // planDotsByTooth retired — surface paint replaces it. Keep a
  // null pass-through so the prop wiring stays explicit.
  const planDotsByTooth = null;

  // Multi-tooth groups (Bridge / Splint / RPD / Denture / Crown) for
  // the chairside chart in Tx Plan mode. Mirrors TreatmentTab's
  // computation so the doctor chart paints the same BRIDGE frame +
  // A/P chips Cameron sees on the patient-tab Tx Plan chart. Server
  // returns display_kind_label on each item; classifyMultiTooth is
  // the legacy-fallback when display_kind_label is null.
  const multiToothGroups = useMemo(() => {
    const out = [...dentureChartGroups];
    if (chartMode !== "tx_plan") return out.length > 0 ? out : null;
    const items = tx.activePlan?.items || [];
    const dentureItemIds = new Set(dentureChartGroups.map((group) => group.sourceItemId));
    for (const it of items) {
      if (it.status === "Declined") continue;
      if (dentureItemIds.has(String(it.id))) continue;
      const teethArr = Array.isArray(it.teeth)
        ? it.teeth
            .map((t) => typeof t === "object"
              ? { tooth_number: t?.tooth_number, role: t?.role || null }
              : { tooth_number: t, role: null })
            .filter((t) => t.tooth_number)
        : [];
      if (teethArr.length < 2) continue;
      const fallback = classifyMultiTooth({
        multi_tooth_kind: it.multi_tooth_kind,
        procedure_name:   it.procedure_name,
      });
      const label = it.display_kind_label || fallback.label;
      const kind = it.display_kind_label
        ? (it.display_kind_label === "BRIDGE"  ? "bridge"
         : it.display_kind_label === "SPLINT"  ? "splint"
         : it.display_kind_label === "RPD"     ? "partial_denture"
         : it.display_kind_label === "DENTURE" ? "full_denture"
         : it.display_kind_label === "CROWN"   ? "crown_unit"
         : "other")
        : fallback.kind;
      const color = (kind === "bridge"          ? "#b91c1c"
                  : kind === "splint"          ? "#c2410c"
                  : kind === "partial_denture" ? "#047857"
                  : kind === "full_denture"    ? "#6d28d9"
                  : kind === "crown_unit"      ? "#0e7490"
                  : fallback.color);
      const rolesByFdi = {};
      for (const t of teethArr) {
        if (t.role) rolesByFdi[String(t.tooth_number)] = t.role;
      }
      out.push({
        id:    it.id,
        kind,
        label,
        color,
        teeth: teethArr.map((t) => String(t.tooth_number)),
        rolesByFdi,
      });
    }
    return out.length > 0 ? out : null;
  }, [chartMode, dentureChartGroups, tx.activePlan]);

  // Helper: does the active mode already have this procedure on
  // the same tooth+surface? Whole-tooth (surface=null) collides
  // with itself; surface-specific collides only on the same
  // surface. Used to gate duplicate paints and to find the target
  // for the erase tool.
  // Bundle-aware: a row's surface "MO" already covers M and O. The
  // duplicate-paint guard fires only when the SPECIFIC surface char is
  // already in the bundle, so the doctor doesn't get a "duplicate?"
  // prompt while progressively filling surfaces of the same composite.
  // Whole-tooth procedures (surface=null) still collide with themselves.
  const findExistingPaint = useCallback((procId, fdi, surface) => {
    // Whole-mouth (non-tooth-specific) rows store tooth_number as
    // null; normalize fdi so passing null matches them by their
    // empty-string canonical form. Without this, String(null) =
    // "null" never matches String(null || "") = "" and the
    // duplicate-paint warning silently no-ops on whole-mouth.
    const fdiStr = fdi != null ? String(fdi) : "";
    const surfChar = surface ? String(surface).toUpperCase() : null;
    const collide = (rowSurface) => {
      if (rowSurface == null && surfChar == null) return true;       // whole-tooth twice
      if (rowSurface == null || surfChar == null) return false;
      return splitSurfaces(rowSurface).includes(surfChar);
    };
    if (chartMode === "today") {
      // Check the draft first (already-done items); if no match,
      // also check the booked-for-today set so the doctor gets a
      // duplicate warning before re-painting an extraction that's
      // already on the chart from a prior-visit booking.
      const draftMatch = draft.items.find((it) =>
        it.procedure_id === procId &&
        String(it.tooth_number || "") === fdiStr &&
        collide(it.surface ?? null) &&
        (it.outcome || "done") === "done");
      if (draftMatch) return draftMatch;
      const schedMatch = (draft.scheduled || []).find((s) =>
        s.procedure_id === procId &&
        String(s.tooth_number || "") === fdiStr &&
        collide(s.surface ?? null));
      return schedMatch || null;
    }
    if (chartMode === "tx_plan") {
      return (tx.activePlan?.items || []).find((it) =>
        it.procedure_id === procId &&
        String(it.tooth_number || "") === fdiStr &&
        collide(it.surface ?? null));
    }
    if (chartMode === "future_need") {
      return (future.needs || []).find((n) =>
        n.procedure_id === procId &&
        String(n.tooth_number || "") === fdiStr &&
        collide(n.surface ?? null));
    }
    return null;
  }, [chartMode, draft.items, draft.scheduled, tx.activePlan, future.needs]);

  // The actual paint, factored out so "Add anyway" from the
  // duplicate-prompt dialog can re-invoke it after the user
  // confirms they really want a second one.
  const executePaint = useCallback(async (proc, toothNumber, effectiveSurface, label) => {
    if (chartMode === "today") {
      draft.addItem(proc, { tooth_number: toothNumber, surface: effectiveSurface });
      toast.success(label);
      return;
    }
    if (chartMode === "tx_plan") {
      // Guided Freedom: when there's no plan yet, painting on the
      // chart implicitly says "start one." Auto-create a Draft and
      // continue. The dentist still owns the choice — they can
      // discard the auto-created Draft from the builder if it was
      // accidental.
      let workingPlan = tx.activePlan;
      if (!workingPlan) {
        const newId = await tx.createPlan();
        if (!newId) return;
        workingPlan = { id: newId, status: "Draft" };
        treatmentTabRef.current?.setActivePlanId?.(newId);
        toast.success("New plan started.");
      }

      // Auto-revise: if the active plan was already Proposed /
      // Accepted / Partially Accepted / Rejected, painting on it is
      // schema-locked. Spawn a fresh Draft revision in place so the
      // dentist's click "just works" instead of bouncing them out to
      // the full editor. Superseded should never be active (filtered
      // out of visiblePlans), but guard anyway.
      if (workingPlan.status === "Superseded") {
        toast.info("Active plan was superseded. Pick a current plan first.");
        return;
      }
      if (workingPlan.status !== "Draft") {
        const revised = await tx.revisePlan();
        if (!revised) return;
        workingPlan = revised;
        toast.success("Started a new revision to paint on.");
      }

      const id = await tx.addItem(
        proc,
        { tooth_number: toothNumber, surface: effectiveSurface },
        workingPlan,
      );
      if (!id) return;
      toast.success(label);
      // Sync the embedded TreatmentTab so its plan tabs / builder
      // pick up the new item (and the new revision id, if auto-
      // revise spawned one). setActivePlanId first so it follows
      // into the new Draft, then refresh.
      if (workingPlan?.id) treatmentTabRef.current?.setActivePlanId?.(workingPlan.id);
      treatmentTabRef.current?.refresh?.();
      const idHolder = { current: id };
      const payload  = { tooth_number: toothNumber, surface: effectiveSurface };
      ops.recordOp({
        undo: async () => {
          const { error } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
          if (error) throw new Error(error.message ?? "Could not undo.");
          await tx.reloadActive();
          treatmentTabRef.current?.refresh?.();
        },
        redo: async () => {
          const newId = await tx.addItem(proc, payload);
          if (!newId) throw new Error("Could not redo.");
          idHolder.current = newId;
          treatmentTabRef.current?.refresh?.();
        },
      });
      return;
    }
    if (chartMode === "future_need") {
      const ctx = {
        tooth_number: toothNumber,
        surface: effectiveSurface,
        follow_up_after_days: sessionFollowUpDays,
      };
      const id = await future.addNeed(proc, ctx);
      if (!id) return;
      toast.success(label);
      // Multi-visit (RCT, Crown, denture…): pop the "Propose as a plan?" gate
      // right after flagging it — same reflex as the Today chart. Single-visit
      // needs just get noted; the per-row button is still there for them.
      if (isMultiVisitProcedure(proc)) setAutoProposeNeedId(id);
      const idHolder = { current: id };
      ops.recordOp({
        undo: async () => {
          const { error } = await supabase.rpc("delete_open_treatment_need", { p_need_id: idHolder.current });
          if (error) throw new Error(error.message ?? "Could not undo.");
          await future.reload();
        },
        redo: async () => {
          // Capture the session window AT redo-time so a doctor who
          // changed their mind after undo gets the new value applied.
          const newId = await future.addNeed(proc, { ...ctx, follow_up_after_days: sessionFollowUpDays });
          if (!newId) throw new Error("Could not redo.");
          idHolder.current = newId;
        },
      });
      return;
    }
  }, [chartMode, draft, tx, future, ops, sessionFollowUpDays]);

  // Tooth-click intercept — fires when chartMode is non-Chart and
  // the user taps a tooth/surface. Looks up the armed procedure
  // and routes to the mode-specific downstream surface.
  //   today        → write to the visit draft (debounced autosave)
  //   tx_plan      → add_plan_item to the active Draft plan
  //   future_need  → add_treatment_need (standalone, status=Open)
  // Whole-tooth procedures (RCT, Crown, Extraction, etc.) save
  // surface=null and visually paint all 5 surfaces.
  const onToothInterceptClick = useCallback(async (toothNumber, surface) => {
    // Multi-Tooth select mode — tooth taps toggle the buffer instead
    // of painting. Adjacency enforced client-side; server re-validates
    // when add_procedure_to_plan runs from the dialog.
    if (multiToothArmed) {
      const fdiStr = String(toothNumber);
      setMultiToothBuffer((prev) => {
        const isRemoving = prev.includes(fdiStr);
        const next = isRemoving
          ? prev.filter((x) => x !== fdiStr)
          : [...prev, fdiStr];
        if (!isRemoving && !teethAreAdjacent(next)) {
          toast.error("Adjacent teeth only — same arch, no gaps.");
          return prev;
        }
        return next;
      });
      return;
    }
    if (!armedProcedureId) {
      toast.info("Pick a procedure from the palette first.");
      return;
    }
    const proc = procedures.find((p) => p.id === armedProcedureId);
    if (!proc) return;
    const wholeTooth = isWholeToothProcedure(proc);

    if (!wholeTooth && surface == null) return;

    const effectiveSurface = wholeTooth ? null : surface;
    const label = wholeTooth || effectiveSurface == null
      ? `${proc.name} on #${toothNumber}`
      : `${proc.name} · #${toothNumber} ${effectiveSurface}`;

    // A matching scheduled Future Need is the SAME persisted work returning
    // through Recall, not a duplicate paint. Recognize it before both the
    // duplicate warning and the generic multi-visit choice dialog.
    const scheduledMultiVisitNeed = chartMode === "today"
      ? findScheduledMultiVisitNeedForPaint(
          draft.scheduled,
          proc,
          toothNumber,
          effectiveSurface,
        )
      : null;
    if (scheduledMultiVisitNeed) {
      const rolloutEnabled = await resolveOngoingTreatmentEnabled();
      setPendingMultiVisit({ proc, fdi: toothNumber, surface: effectiveSurface, label, rolloutEnabled });
      return;
    }

    // Duplicate guard: same procedure already on this tooth+surface?
    // Whole-tooth procedure already on this tooth? Warn before
    // adding a second copy. "Don't show again" silences the dialog
    // for the rest of the browser session via localStorage.
    const dup = findExistingPaint(armedProcedureId, toothNumber, effectiveSurface);
    if (dup && !dupDismissed) {
      setPendingDup({ proc, fdi: toothNumber, surface: effectiveSurface, label });
      return;
    }

    // Multi-visit catch: in Today mode, a procedure that normally spans
    // more than one visit shouldn't be silently filed as single-visit
    // "today" work. Ask where it goes (Guided Freedom — the dentist can
    // still finish it all today).
    if (chartMode === "today" && isMultiVisitProcedure(proc)) {
      const rolloutEnabled = await resolveOngoingTreatmentEnabled();
      // Already an open ongoing treatment? Don't re-ask the 3-choice dialog —
      // drop this procedure straight into the existing MV (Phase 7 Fix 3).
      if (rolloutEnabled && bundle) {
        await startOrAddToBundle(proc, toothNumber, effectiveSurface);
        return;
      }
      setPendingMultiVisit({ proc, fdi: toothNumber, surface: effectiveSurface, label, rolloutEnabled });
      return;
    }

    await executePaint(proc, toothNumber, effectiveSurface, label);
  }, [armedProcedureId, procedures, findExistingPaint, executePaint, dupDismissed, multiToothArmed, chartMode, bundle, startOrAddToBundle, draft.scheduled, resolveOngoingTreatmentEnabled]);

  // Whole-mouth (non-tooth-specific) procedures: doctor clicks them
  // in the palette and they're added straight to the active mode's
  // surface — no chart click. Same duplicate guard as the tooth
  // path, just keyed on (procedure_id, null tooth, null surface).
  const onPickWholeMouth = useCallback(async (proc) => {
    if (!proc) return;
    const label = `${proc.name} (whole mouth)`;
    const scheduledMultiVisitNeed = chartMode === "today"
      ? findScheduledMultiVisitNeedForPaint(draft.scheduled, proc, null, null)
      : null;
    if (scheduledMultiVisitNeed) {
      const rolloutEnabled = await resolveOngoingTreatmentEnabled();
      setPendingMultiVisit({ proc, fdi: null, surface: null, label, rolloutEnabled });
      return;
    }
    const dup = findExistingPaint(proc.id, null, null);
    if (dup && !dupDismissed) {
      setPendingDup({ proc, fdi: null, surface: null, label });
      return;
    }
    if (chartMode === "today" && isMultiVisitProcedure(proc)) {
      const rolloutEnabled = await resolveOngoingTreatmentEnabled();
      // Already an open ongoing treatment? Skip the dialog, add straight in.
      if (rolloutEnabled && bundle) {
        await startOrAddToBundle(proc, null, null);
        return;
      }
      setPendingMultiVisit({ proc, fdi: null, surface: null, label, rolloutEnabled });
      return;
    }
    await executePaint(proc, null, null, label);
  }, [findExistingPaint, executePaint, dupDismissed, chartMode, bundle, startOrAddToBundle, draft.scheduled, resolveOngoingTreatmentEnabled]);

  // "Propose as a plan" from a Future Need — runs the DB conversion engine
  // (convert_treatment_need_to_plan), which atomically spins up a Draft plan
  // with the need's procedure (+ any bundled needs) and their visits, and
  // retires the converted needs so they can't double-bill. Then it drops the
  // dentist into Tx Plan mode on the new draft so they see what they created.
  const handleProposeNeedAsPlan = useCallback(async (needId, bundleNeedIds) => {
    try {
      const { data, error } = await supabase.rpc("convert_treatment_need_to_plan", {
        p_need_id: needId,
        p_bundle_need_ids: Array.isArray(bundleNeedIds) ? bundleNeedIds : [],
      });
      if (error) {
        toast.error(error.message ?? "Could not propose the plan.");
        return null;
      }
      const planId = data?.plan_id ?? null;
      const count = Array.isArray(data?.items) ? data.items.length : 1;
      await future.reload();          // converted needs drop off the Open list
      await tx.loadPlans?.();          // surface the new draft in the plan list
      if (planId) {
        tx.setActivePlanId?.(planId);
        treatmentTabRef.current?.setActivePlanId?.(planId);
        setChartMode("tx_plan");       // show the draft they just proposed
      }
      toast.success(count > 1
        ? `Draft plan created with ${count} procedures — front desk can present it.`
        : "Draft plan created — front desk can present it.");
      return planId;
    } catch (e) {
      toast.error(e?.message ?? "Could not propose the plan.");
      return null;
    }
  }, [future, tx, setChartMode]);

  const onArmProcedure = useCallback((procedureId) => {
    if (!procedureId) {
      setArmedProcedureId(null);
      return;
    }
    const proc = procedures.find((row) => row.id === procedureId);
    if (chartMode === "today" && isDentureProcedure(proc)) {
      setArmedProcedureId(null);
      setDentureDialogItemId(null);
      setDentureDialogProcedure(proc);
      return;
    }
    setArmedProcedureId(procedureId);
  }, [chartMode, procedures]);

  const ensureDraftTxPlanForMultiTooth = useCallback(async () => {
    let workingPlan = tx.activePlan;
    if (!workingPlan) {
      const newId = await tx.createPlan();
      if (!newId) return null;
      treatmentTabRef.current?.setActivePlanId?.(newId);
      toast.success("New plan started.");
      return newId;
    }

    if (workingPlan.status === "Superseded") {
      toast.info("Active plan was superseded. Pick a current plan first.");
      return null;
    }

    if (workingPlan.status !== "Draft") {
      const revised = await tx.revisePlan();
      if (!revised?.id) return null;
      treatmentTabRef.current?.setActivePlanId?.(revised.id);
      toast.success("Started a new revision to paint on.");
      return revised.id;
    }

    return workingPlan.id;
  }, [tx]);

  // Erase tool — clicking a surface in any non-chart mode removes
  // the painted item there. Whole-tooth items (surface=null) erase
  // when any of their 5 surfaces is clicked. Surface-specific items
  // erase only on the matching surface. Routes to the mode's delete
  // path, including pushing an undo onto the chart-ops stack so
  // ⌘Z can put the paint back.
  // Bundle-aware: prefer the row whose multi-surface string contains
  // the clicked surface character, fall back to whole-tooth (no surface).
  // Erasing a single surface from a multi-surface bundle strips the
  // char; erasing the last surface deletes the row entirely. The chart
  // overlay updates because `surface` field on the row changes.
  const onToothInterceptErase = useCallback(async (toothNumber, surface) => {
    const fdiStr = String(toothNumber);
    const surfChar = surface ? String(surface).toUpperCase() : null;

    if (chartMode === "today") {
      const target = draft.items.find((it) =>
        String(it.tooth_number || "") === fdiStr
        && surfChar
        && splitSurfaces(it.surface).includes(surfChar))
        || draft.items.find((it) =>
          String(it.tooth_number || "") === fdiStr && (it.surface ?? null) == null);
      if (!target) return;
      const lineId = draftLineId(target);
      if (surfChar && target.surface) {
        draft.removeSurface(lineId, surfChar);
      } else {
        draft.removeItem(lineId);
      }
      return;
    }

    if (chartMode === "tx_plan") {
      if (!tx.activePlan || tx.activePlan.status !== "Draft") {
        toast.info("Plan must be Draft to erase items.");
        return;
      }
      const items = tx.activePlan.items || [];
      const target = items.find((it) =>
        String(it.tooth_number || "") === fdiStr
        && surfChar
        && splitSurfaces(it.surface).includes(surfChar))
        || items.find((it) =>
          String(it.tooth_number || "") === fdiStr && (it.surface ?? null) == null);
      if (!target) return;
      // Multi-surface bundle: strip one. Whole-tooth or last surface:
      // delete the row.
      if (surfChar && target.surface && splitSurfaces(target.surface).length > 1) {
        const { error } = await supabase.rpc("erase_plan_item_surface", {
          p_item_id: target.id,
          p_surface_char: surfChar,
        });
        if (error) { toast.error(error.message ?? "Could not erase surface."); return; }
        await tx.reloadActive();
        treatmentTabRef.current?.refresh?.();
        return;
      }
      const proc = procedures.find((p) => p.id === target.procedure_id);
      const { error } = await supabase.rpc("delete_plan_item", { p_item_id: target.id });
      if (error) { toast.error(error.message ?? "Could not erase."); return; }
      await tx.reloadActive();
      treatmentTabRef.current?.refresh?.();
      const restore = { tooth_number: target.tooth_number, surface: target.surface };
      const idHolder = { current: null };
      ops.recordOp({
        undo: async () => {
          if (!proc) throw new Error("Procedure no longer in catalog.");
          const newId = await tx.addItem(proc, restore);
          if (!newId) throw new Error("Could not undo erase.");
          idHolder.current = newId;
        },
        redo: async () => {
          if (!idHolder.current) return;
          const { error: e } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
          if (e) throw new Error(e.message ?? "Could not redo erase.");
          await tx.reloadActive();
        },
      });
      return;
    }

    if (chartMode === "future_need") {
      const target = future.needs.find((n) =>
        String(n.tooth_number || "") === fdiStr
        && surfChar
        && splitSurfaces(n.surface).includes(surfChar))
        || future.needs.find((n) =>
          String(n.tooth_number || "") === fdiStr && (n.surface ?? null) == null);
      if (!target) return;
      if (surfChar && target.surface && splitSurfaces(target.surface).length > 1) {
        const { error } = await supabase.rpc("erase_treatment_need_surface", {
          p_need_id: target.id,
          p_surface_char: surfChar,
        });
        if (error) { toast.error(error.message ?? "Could not erase surface."); return; }
        await future.reload();
        return;
      }
      const proc = procedures.find((p) => p.id === target.procedure_id);
      const ok = await future.removeNeed(target.id);
      if (!ok) return;
      const restore = { tooth_number: target.tooth_number, surface: target.surface };
      const idHolder = { current: null };
      ops.recordOp({
        undo: async () => {
          if (!proc) throw new Error("Procedure no longer in catalog.");
          const newId = await future.addNeed(proc, restore);
          if (!newId) throw new Error("Could not undo erase.");
          idHolder.current = newId;
        },
        redo: async () => {
          if (!idHolder.current) return;
          const { error: e } = await supabase.rpc("delete_open_treatment_need", { p_need_id: idHolder.current });
          if (e) throw new Error(e.message ?? "Could not redo erase.");
          await future.reload();
        },
      });
      return;
    }
  }, [chartMode, draft, tx, future, procedures, ops]);

  // Unified keyboard handler — ⌘Z / Ctrl+Z = undo, ⌘⇧Z / Ctrl+Y = redo,
  // and bare 1 / 2 / 3 / 4 = mode switch. Routes by mode: Today uses
  // the local draft stack, Tx Plan and Future Need use the chart-ops
  // stack (which calls delete_* RPCs). Lives at workspace level so
  // it works in any mode (the previous version was scoped inside
  // TodayWorkCard so ⌘Z in Tx Plan was a silent no-op).
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const meta = e.metaKey || e.ctrlKey;

      // Bare 1/2/3/4 — switch chart mode. Bare "?" — open help.
      // Skip if any modifier is held so we don't hijack ⌘1 (tabs)
      // or ⌘? (search-find on some setups).
      if (!meta && !e.altKey && !e.shiftKey) {
        const modeMap = {
          "1": "chart",
          "2": "today",
          "3": "future_need",
          ...(canUseTxPlanCharting ? { "4": "tx_plan" } : {}),
        };
        if (modeMap[e.key]) {
          e.preventDefault();
          setChartMode(modeMap[e.key]);
          return;
        }
        if (e.key === "?") {
          e.preventDefault();
          setHelpOpen((s) => !s);
          return;
        }
      }
      // Shift+? still fires the help modal (some keyboard layouts
      // require shift to type "?"). Without shift+modifier check
      // above the bare-key branch already handles it.
      if (e.shiftKey && !meta && !e.altKey && (e.key === "?" || e.key === "/")) {
        e.preventDefault();
        setHelpOpen((s) => !s);
        return;
      }

      // ⌘Z / ⌘⇧Z / Ctrl+Y — undo / redo. No-op in chart mode (the
      // chart's own keyboard handler covers that).
      if (!meta) return;
      if (chartMode === "chart") return;
      const k = e.key.toLowerCase();
      const isUndo = k === "z" && !e.shiftKey;
      const isRedo = (k === "z" && e.shiftKey) || k === "y";
      if (!isUndo && !isRedo) return;
      e.preventDefault();
      if (chartMode === "today") {
        if (isUndo) draft.undo?.(); else draft.redo?.();
      } else {
        // tx_plan / future_need
        if (isUndo) ops.undo(); else ops.redo();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [canUseTxPlanCharting, chartMode, draft, ops]);

  if (loadingPatient) {
    return (
      <div className="bg-white rounded-2xl shadow-sm min-h-[400px] flex items-center justify-center" data-testid="doctor-patient-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (patientError || !patient) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-sm text-rose-700">
        {patientError ?? "Patient not found."}
      </div>
    );
  }

  // Negative-space layout: in non-chart modes the active mode's list
  // (Today's work / Tx Plan builder / Future Needs) renders INSIDE the
  // chart card's left column, BELOW the tooth chart canvas — fills the
  // empty space the procedures palette already creates by stretching
  // the chart row. Chart keeps full horizontal width; painting a tooth
  // lands the new line just below the chart, no scroll.
  //
  // Palette stays visible on the right rail at all times in non-chart
  // modes — armed or not. An earlier version hid the palette when a
  // procedure was armed (to widen the chart further), but that turned
  // every procedure switch into a two-step gesture (clear arm, then
  // re-pick) which dragged on rapid Future-Need / Tx-Plan painting.
  // The armed-procedure chip in the chart header still shows what's
  // loaded and offers a quick ✕ to clear; the palette itself is the
  // primary picker.
  const sideBySide = chartMode !== "chart";
  const armedProcedure = armedProcedureId
    ? procedures.find((p) => p.id === armedProcedureId) || null
    : null;
  const showPalette = sideBySide;

  // Booked-for-today reminders. Rendered in two ways from ONE prop set:
  //   • embedded — as the "Scheduled" band fused inside the Today card
  //     (Today mode), so the dentist reads one card: booked to-dos on
  //     top, recorded work below — where their eyes default.
  //   • standalone — its own card above the chart in the other modes.
  // The Ongoing treatment (multi-visit bundle) card. It belongs only in the
  // aligned Today row; other chart modes never mount this operational UI.
  const canOfferMvImport = ongoingTreatmentEnabled && !bundle
    && draft.loaded
    && draft.items.length === 0
    && Object.keys(draft.dispositions || {}).length === 0
    && ["clinic_admin", "doctor", "receptionist"].includes(clinicRole);
  const renderOngoingBundleCard = () => ongoingTreatmentEnabled && bundle ? (
    <OngoingBundleCard
      bundle={bundle}
      stagedDoneItemIds={stagedDoneBundleItemIds}
      onMarkDone={handleMarkBundleItemDone}
      onCompleteItem={handleCompleteBundleItem}
      onUndoItem={handleUndoBundleItem}
      showFinishControls={ongoingTreatmentEnabled}
      allowDentureActions={ongoingTreatmentEnabled}
      onRequestFinish={handleRequestFinish}
      onCancelFinish={handleCancelFinish}
      onRecordDentureVisit={handleRecordDentureVisit}
      onInstallDenture={handleInstallDenture}
      onCorrectMistake={["clinic_admin", "doctor"].includes(clinicRole)
        ? (item) => setMistakeCorrectionTarget({ item })
        : undefined}
      finishRequesting={finishRequesting}
      markingItemId={markingBundleItemId}
      completingItemId={completingBundleItemId}
      undoingItemId={undoingBundleItemId}
      notes={bundleNotes}
      currentAppointmentId={appt?.id ?? null}
      onSaveNote={handleSaveBundleNote}
      onAddNoteAddendum={handleAddBundleNoteAddendum}
    />
  ) : canOfferMvImport ? (
    <ChairsideMvImportPrompt onOpen={() => setMvImportOpen(true)} />
  ) : null;

  const renderTodaysPlanCard = (embedded) => (
    <ToDoTodayCard
      embedded={embedded}
      scheduled={draft.scheduled}
      currency={currency}
      doneWorkKeys={doneWorkKeysSet}
      chartReconciled={chartReconciledReminders}
      dispositions={draft.dispositions}
      orthoContext={{ ...chairsideOrtho, done: orthoPanelDone }}
      onDone={(s) => draft.addFromSchedule(s)}
      onUndoDone={(s) => draft.removeFromSchedule(s)}
      onDoneGroup={(rows) => draft.addScheduledGroup(rows)}
      onUndoDoneGroup={(rows) => draft.removeScheduledGroup(rows)}
      onNotToday={(s, reasonCode) => draft.unscheduleNeed(s, reasonCode)}
      onDecline={(s, reason) => draft.declineNeed(s, reason)}
      onClearDisposition={(s) => draft.clearDisposition(s)}
      onRemoveScheduledDenture={handleRemoveScheduledDenture}
      dentureProcedureIds={dentureProcedureIds}
      postOpByProcedure={postOpByProcedure}
      followupWaivers={followupWaivers}
      setFollowupWaivers={setFollowupWaivers}
    />
  );

  const modeListCard = chartMode === "today" ? (
    <TodayWorkCard
      items={effectiveDraftItems}
      saving={draft.saving}
      savedAt={draft.savedAt}
      saveError={draft.saveError}
      onReload={draft.reload}
      currency={currency}
      patient={patient}
      procedures={procedures}
      colorMap={procedureColorMap}
      onAdd={draft.addItem}
      onRemove={draft.removeItem}
      onSetOutcome={draft.setOutcome}
      onSetPrice={draft.setItemPrice}
      onSetDiscount={draft.setItemDiscount}
      onSetNote={draft.setItemNote}
      onUndo={draft.undo}
      onRedo={draft.redo}
      canUndo={draft.canUndo}
      canRedo={draft.canRedo}
      scheduledSlot={renderTodaysPlanCard(true)}
    />
  ) : chartMode === "tx_plan" && canUseTxPlanCharting ? (
    <div data-tutorial-id="charting-treatment-plan-work">
      <TreatmentTab
        ref={treatmentTabRef}
        patientId={patient.id}
        patient={patient}
        chartless
        hidePlanTabs
        onPlansChange={setTxTabsData}
        currentAppointmentId={appt.id}
        onPlanStartedToday={() => draft.reload()}
        onActivePlanIdChange={(id) => {
          if (id !== tx.activePlanId) tx.setActivePlanId(id);
        }}
        onActivePlanDataChange={tx.syncActivePlan}
      />
    </div>
  ) : chartMode === "future_need" ? (
    <FutureNeedsCard
      needs={future.needs}
      loaded={future.loaded}
      currency={currency}
      patient={patient}
      onRemove={future.removeNeed}
      onUpdate={future.updateNeed}
      onUndo={ops.undo}
      onRedo={ops.redo}
      canUndo={ops.canUndo}
      canRedo={ops.canRedo}
      sessionFollowUpDays={sessionFollowUpDays}
      setSessionFollowUpDays={setSessionFollowUpDays}
      onPropose={handleProposeNeedAsPlan}
      autoProposeNeedId={autoProposeNeedId}
      onAutoProposeConsumed={() => setAutoProposeNeedId(null)}
    />
  ) : null;

  const orthoCurrentVisitModule = chairsideOrtho.isOrthoVisit ? (
    <div
      className={chartMode === "today" ? "min-w-0" : "hidden"}
      aria-hidden={chartMode === "today" ? undefined : "true"}
      data-testid="doctor-ortho-current-visit-rail"
    >
      <OrthoChairsidePanel
        context={chairsideOrtho}
        appointmentId={chairsideAppointment.id}
        currency={currency}
        clinicRole={clinicRole}
        user={user}
        onSaved={() => setOrthoPanelDone(true)}
        open
        embedded
        onUndoConversion={handleUndoOrthoVisit}
        onVisitTypeChanged={handleChairsideOrthoVisitTypeChanged}
        undoingConversion={undoingOrthoVisit}
      />
    </div>
  ) : null;

  const doctorFinished = !!appt.doctor_finished_at;

  // No inner overflow — the page-level scroller handles height.
  // Two scrollers nested looks broken (and was).
  return (
    <div className="flex flex-col gap-3">
      <WorkspaceHeader
        appt={appt}
        patient={patient}
        clinicRole={clinicRole}
        onOpenPatient={onOpenPatient}
        onOpenMedicalHistory={onOpenMedicalHistory}
        onOpenOrtho={handleOpenOrtho}
        onSwitchPatient={onSwitchPatient}
        onMarkDone={handleMarkDoneWithNotePrompt}
        onReopenDone={onReopenDone}
        markingDone={markingDone}
        reopeningDone={reopeningDone}
        onPatientChanged={reloadPatient}
        queueCount={queueCount}
        currency={currency}
        todayTotals={todayTotals}
        lastVisit={lastVisit.last}
        lastVisitLoaded={lastVisit.loaded}
        patientContext={patientContext}
        showOrthoButton={showHeaderOrthoButton}
        handoffBlocked={!!primaryHandoffRequirement}
        handoffBlockMessage={primaryHandoffRequirement?.message || ""}
      />

      {primaryHandoffRequirement ? (
        <div
          id="doctor-handoff-requirement"
          role="alert"
          data-testid="doctor-handoff-requirement"
          className="sticky top-2 z-20 flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-amber-700">
                Required before Done with patient
              </p>
              <p className="mt-0.5 text-sm font-semibold text-amber-950">
                {primaryHandoffRequirement.procedureName}
              </p>
              <p className="text-xs text-amber-800">{primaryHandoffRequirement.message}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openHandoffRequirement(primaryHandoffRequirement)}
            data-testid="doctor-resolve-handoff-requirement"
            className="inline-flex shrink-0 items-center justify-center rounded-lg bg-amber-700 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-800"
          >
            {primaryHandoffRequirement.actionLabel}
          </button>
        </div>
      ) : null}

      {doctorFinished ? (
        <div
          className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-700 flex items-center justify-between gap-3"
          data-testid="doctor-workspace-finished-banner"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-slate-500" />
            <div>
              <p className="font-semibold text-slate-800">Doctor work is marked done.</p>
              <p className="text-xs text-slate-500">This chairside workspace is now locked visually. Checkout still completes the visit.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReopenDone}
            disabled={reopeningDone}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
            data-testid="doctor-reopen-chairside"
          >
            {reopeningDone ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Reopen chairside
          </button>
        </div>
      ) : null}

      <div
        className={doctorFinished ? "space-y-3 opacity-45 grayscale pointer-events-none select-none transition duration-200" : "space-y-3"}
        aria-disabled={doctorFinished}
        data-testid="doctor-workspace-body"
        data-tutorial-id="doctor-primary-surface"
      >
      {chairsideOrtho.isOrthoVisit ? (
        <ChairsideOrthoActiveBanner
          context={chairsideOrtho}
          recorded={orthoVisitRecorded}
          onContinue={handleContinueOrthoVisit}
        />
      ) : null}

      {/* Other modes keep today's booked reminders as context, but the MV
          recording card itself exists only in Today's aligned row below. */}
      {chartMode !== "today" ? renderTodaysPlanCard(false) : null}

      {showOrthoVisitModule && !chairsideOrtho.isOrthoVisit ? (
        <ChairsideOrthoVisitModule
          open={orthoVisitModuleOpen}
          onToggle={() => setOrthoVisitModuleOpen((open) => !open)}
        >
          {orthoVisitOptions.length > 0 ? (
            <ChairsideOrthoVisitPicker
              options={orthoVisitOptions}
              convertingKey={convertingOrthoVisit}
              onSelect={handleSelectOrthoVisit}
            />
          ) : (
            <ChairsideOrthoUnavailable
              row={chairsideOrtho.activeCase}
              settings={chairsideOrtho.settings}
              clinicRole={clinicRole}
              onSetupBilling={() => setSetupOrthoCase(chairsideOrtho.activeCase)}
            />
          )}
        </ChairsideOrthoVisitModule>
      ) : null}

      <div data-testid="doctor-chart-canvas" ref={chartCanvasRef} className="min-w-0">
        <ChartingTab
          key={`${patient.id}-${dentureReloadToken}`}
          patientId={patient.id}
          patient={patient}
          chairside
          chartMode={chartMode}
          onChartModeChange={setChartMode}
          disabledChairsideModes={disabledChairsideModes}
          paletteOverride={
            showPalette ? (
              <ProceduresPalette
                procedures={procedures}
                colorMap={procedureColorMap}
                armedProcedureId={armedProcedureId}
                onArm={onArmProcedure}
                onPickWholeMouth={onPickWholeMouth}
                mode={chartMode}
                currency={currency}
                fillHeight={chartMode === "today"}
                eraserOn={eraseArmed}
                onPickEraser={() => setEraseArmed((v) => !v)}
                // Multi-Tooth chart-canvas selection — mirrors the
                // patient-tab flow. Click Multi-Tooth → arms select
                // mode (tap teeth on chart). When buffer ≥ 1, the
                // button transforms into "Define (N)" which opens
                // the BuildMultiToothDialog with the picked teeth.
                multiToothOn={multiToothArmed}
                multiToothCount={multiToothBuffer.length}
                onPickMultiTooth={
                  chartMode === "tx_plan" && canUseTxPlanCharting
                    ? () => {
                        if (multiToothArmed) {
                          // Already armed — second click cancels and
                          // clears the buffer. Same affordance the
                          // patient view uses.
                          setMultiToothArmed(false);
                          setMultiToothBuffer([]);
                        } else {
                          // Disarm any procedure / erase tool first
                          // so paints don't conflict with selection.
                          setArmedProcedureId(null);
                          setEraseArmed(false);
                          setMultiToothArmed(true);
                        }
                      }
                    : null
                }
                onDefineMultiTooth={
                  chartMode === "tx_plan" && canUseTxPlanCharting && multiToothBuffer.length > 0
                    ? () => setMultiToothDialogOpen(true)
                    : null
                }
              />
            ) : null
          }
          belowChartCanvas={sideBySide && chartMode !== "today" ? modeListCard : null}
          belowChartLeft={chartMode === "today" ? modeListCard : null}
          belowChartRight={chairsideOrtho.isOrthoVisit
            ? orthoCurrentVisitModule
            : showOperationalMvInChartMode(chartMode)
              ? renderOngoingBundleCard()
              : null}
          belowChartEqualColumns={chartMode === "today" && (
            (chairsideOrtho.isOrthoVisit && !!orthoCurrentVisitModule)
            || (ongoingTreatmentEnabled && !!bundle)
          )}
          matchPaletteHeight={chartMode === "today"}
          belowChartCanvasFullWidth={chartMode === "tx_plan"}
          gapSlot={chartMode === "tx_plan" && canUseTxPlanCharting ? (
            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5" data-testid="doctor-txplan-selector">
              <PlanTabsRow
                plans={txTabsData.plans}
                activePlanId={txTabsData.activePlanId}
                busyNew={txTabsData.busyCreate}
                canCreate={txTabsData.canCreate}
                onPick={(id) => treatmentTabRef.current?.setActivePlanId?.(id)}
                onCreate={() => treatmentTabRef.current?.createPlan?.()}
              />
            </div>
          ) : null}
          chartFooter={
            chartMode === "today" ? (
              <ChairsideChartLegend>
                <WholeMouthRibbon
                  scheduled={draft.scheduled}
                  items={effectiveDraftItems}
                  doneWorkKeys={doneWorkKeysSet}
                  dispositions={draft.dispositions}
                  colorMap={procedureColorMap}
                  procedures={procedures}
                  bare
                />
                {/* Procedure legend follows the active chairside paint layer,
                    not the active plan fallback, so Today / Future Need show
                    the exact procedures currently painted on this chart. */}
                <TxPlanCategoryLegend
                  items={doctorProcedureLegendItems}
                  procedures={procedures}
                  showProcedures
                  bare
                />
              </ChairsideChartLegend>
            ) : chartMode === "future_need" ? (
              <ChairsideChartLegend>
                <FutureWholeMouthRibbon
                  needs={future.needs}
                  colorMap={procedureColorMap}
                  procedures={procedures}
                  bare
                />
                <TxPlanCategoryLegend
                  items={doctorProcedureLegendItems}
                  procedures={procedures}
                  showProcedures
                  bare
                />
              </ChairsideChartLegend>
            ) : chartMode === "tx_plan" ? (
              <TxPlanCategoryLegend
                items={doctorProcedureLegendItems}
                procedures={procedures}
                showProcedures
              />
            ) : null
          }
          onToothInterceptClick={sideBySide ? onToothInterceptClick : null}
          interceptEraseArmed={sideBySide && eraseArmed}
          onInterceptEraseChange={sideBySide ? setEraseArmed : null}
          onToothInterceptErase={sideBySide ? onToothInterceptErase : null}
          armedProcedureName={armedProcedure?.name ?? null}
          armedProcedureColor={armedProcedure ? procedureColor(armedProcedure, procedureColorMap) : null}
          onClearArmedProcedure={
            sideBySide && armedProcedureId ? () => setArmedProcedureId(null) : null
          }
          interceptOnUndo={
            chartMode === "today"      ? draft.undo
            : chartMode === "tx_plan"  ? ops.undo
            : chartMode === "future_need" ? ops.undo
            : null
          }
          interceptOnRedo={
            chartMode === "today"      ? draft.redo
            : chartMode === "tx_plan"  ? ops.redo
            : chartMode === "future_need" ? ops.redo
            : null
          }
          interceptCanUndo={
            chartMode === "today"      ? draft.canUndo
            : chartMode === "tx_plan"  ? ops.canUndo
            : chartMode === "future_need" ? ops.canUndo
            : false
          }
          interceptCanRedo={
            chartMode === "today"      ? draft.canRedo
            : chartMode === "tx_plan"  ? ops.canRedo
            : chartMode === "future_need" ? ops.canRedo
            : false
          }
          planDotsByTooth={planDotsByTooth}
          dimFindings={sideBySide}
          overlayState={sideBySide ? overlayState : null}
          overlayFindings={sideBySide ? overlayFindings : null}
          stackSummaryByTooth={sideBySide ? stackSummaryByTooth : null}
          interceptNoteTextByTooth={chartMode === "future_need" ? futureNeedNoteTextByTooth : null}
          onToothInterceptNoteSave={chartMode === "future_need" ? saveFutureNeedToothNote : null}
          multiToothGroups={multiToothGroups}
          chartToolbarSlot={
            multiToothArmed ? (
              <div
                className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-1.5 text-xs text-violet-800 flex items-center justify-between gap-3 w-full"
                data-testid="doctor-multitooth-banner"
              >
                <span className="min-w-0 truncate">
                  {multiToothBuffer.length === 0
                    ? "Tap adjacent teeth on the chart to build a Bridge / Denture / RPD. Pick at least 2 teeth, then click Define."
                    : `Selected: ${multiToothBuffer.length}. Click Define when ready.`}
                </span>
                <button
                  type="button"
                  onClick={() => { setMultiToothArmed(false); setMultiToothBuffer([]); }}
                  data-testid="doctor-multitooth-cancel"
                  className="text-xs font-semibold underline-offset-2 hover:underline shrink-0"
                >
                  Cancel
                </button>
              </div>
            ) : null
          }
          onBaseChartStateChange={setChartBaseState}
        />
      </div>

      {/* Visit Note is always-visible at the bottom regardless of
          mode — it's the visit's narrative, not mode-specific. */}
      <VisitNoteCard
        note={draft.note}
        onChange={draft.setNote}
        saving={draft.noteSaving}
        savedAt={draft.noteSavedAt}
        history={draft.noteHistory}
      />
      </div>

      {helpOpen ? (
        <HelpShortcutsDialog
          chartMode={chartMode}
          onClose={() => setHelpOpen(false)}
        />
      ) : null}

      {pendingDup ? (
        <DuplicatePaintDialog
          procName={pendingDup.proc.name}
          fdi={pendingDup.fdi}
          surface={pendingDup.surface}
          onCancel={() => setPendingDup(null)}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              try { localStorage.setItem("dos_v2_dupe_warning_dismissed", "1"); } catch {}
            }
            const { proc, fdi, surface, label } = pendingDup;
            setPendingDup(null);
            await executePaint(proc, fdi, surface, label);
          }}
        />
      ) : null}

      {pendingMultiVisit ? (
        <MultiVisitPaintDialog
          procName={pendingMultiVisit.proc.name}
          fdi={pendingMultiVisit.fdi}
          surface={pendingMultiVisit.surface}
          onCancel={() => setPendingMultiVisit(null)}
          onStartBundle={(ongoingTreatmentEnabled || pendingMultiVisit.rolloutEnabled) ? async () => {
            const { proc, fdi, surface } = pendingMultiVisit;
            setPendingMultiVisit(null);
            await startOrAddToBundle(proc, fdi, surface);
          } : undefined}
          onDoAllToday={async () => {
            const { proc, fdi, surface, label } = pendingMultiVisit;
            setPendingMultiVisit(null);
            await executePaint(proc, fdi, surface, label);
          }}
          onSetupPlan={async () => {
            const { proc, fdi, surface, label } = pendingMultiVisit;
            setPendingMultiVisit(null);
            const planId = await ensureDraftTxPlanForMultiTooth();
            if (!planId) return;
            const id = await tx.addItem(proc, { tooth_number: fdi, surface }, { id: planId, status: "Draft" });
            if (!id) return;
            treatmentTabRef.current?.setActivePlanId?.(planId);
            treatmentTabRef.current?.refresh?.();
            setChartMode("tx_plan");
            toast.success(`${label} added to the plan.`);
          }}
        />
      ) : null}

      {ongoingTreatmentEnabled ? <DentureTodayDialog
        open={!!dentureDialogProcedure}
        appointmentId={chairsideAppointment.id}
        procedure={dentureDialogProcedure}
        existingItemId={dentureDialogItemId}
        missingFdiSet={missingFdiSet}
        onClose={() => {
          setDentureDialogProcedure(null);
          setDentureDialogItemId(null);
        }}
        onStarted={async () => {
          setDentureDialogProcedure(null);
          setDentureDialogItemId(null);
          await loadBundle();
          draft.reload();
          tx.reloadActive?.();
          treatmentTabRef.current?.refresh?.();
          setDentureReloadToken((token) => token + 1);
        }}
      /> : null}

      {ongoingTreatmentEnabled && ["clinic_admin", "doctor"].includes(clinicRole) ? <MvMistakeCorrectionDialog
        open={!!mistakeCorrectionTarget}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setMistakeCorrectionTarget(null);
        }}
        planId={bundle?.plan_id ?? null}
        item={mistakeCorrectionTarget?.item ?? null}
        procedures={bundle?.procedures ?? []}
        onCorrected={handleBundleMistakeCorrected}
      /> : null}

      {ongoingTreatmentEnabled ? <MvCaseImportDialog
        open={mvImportOpen}
        patientId={patient.id}
        patientName={patient.full_name}
        sourceEntryPoint="chairside"
        appointmentId={chairsideAppointment.id}
        procedures={procedures}
        initialDoctorId={chairsideAppointment.doctor_id || ""}
        onClose={() => setMvImportOpen(false)}
        onImported={async () => {
          await loadBundle();
          draft.reload();
          tx.reloadActive?.();
          treatmentTabRef.current?.refresh?.();
        }}
      /> : null}

      {/* Multi-Tooth Tool dialog — preset with the teeth picked on
          the chairside chart. Server's add_procedure_to_plan validates
          adjacency + kind. On success, refresh both the local Tx Plan
          view (useTxPlan) and the embedded TreatmentTab so the items
          list below shows the new bridge immediately. */}
      <BuildMultiToothDialog
        open={multiToothDialogOpen}
        onOpenChange={(v) => {
          setMultiToothDialogOpen(v);
          // Cancel keeps the buffer so the dentist can keep tweaking.
          // Successful add clears via onAdded below.
        }}
        planId={tx.activePlan?.id}
        procedures={procedures}
        presetTeeth={multiToothBuffer}
        missingFdiSet={missingFdiSet}
        onEnsureDraftPlan={ensureDraftTxPlanForMultiTooth}
        onAdded={(_data, targetPlanId) => {
          setMultiToothBuffer([]);
          setMultiToothArmed(false);
          setMultiToothDialogOpen(false);
          if (targetPlanId) treatmentTabRef.current?.setActivePlanId?.(targetPlanId);
          tx.reloadActive?.(targetPlanId);
          treatmentTabRef.current?.refresh?.();
        }}
      />

      {startOrthoOpen ? (
        <StartOrthoCaseDialog
          patientId={patient.id}
          patient={patient}
          doctors={chairsideOrtho.doctors}
          systems={chairsideOrtho.systems}
          clinicRole={clinicRole}
          user={user}
          settings={chairsideOrtho.settings}
          activeBranchId={activeBranchId}
          onClose={() => {
            setPendingChairsideBondingCase(null);
            setContractSigningCase(null);
            setBondingDecisionCase(null);
            setStartOrthoOpen(false);
          }}
          onSaved={(next) => {
            setStartOrthoOpen(false);
            chairsideOrtho.reload?.();
            if (next?.openBilling && next?.row) {
              setPendingChairsideBondingCase(next.row);
              setSetupOrthoCase(next.row);
              return;
            }
            setPendingChairsideBondingCase(null);
            setContractSigningCase(null);
            setBondingDecisionCase(null);
          }}
        />
      ) : null}

      {setupOrthoCase ? (
        <SetupBillingDialog
          row={setupOrthoCase}
          patient={patient}
          doctors={chairsideOrtho.doctors}
          clinic={clinic}
          settings={chairsideOrtho.settings}
          onBack={() => {
            setPendingChairsideBondingCase(null);
            setContractSigningCase(null);
            setBondingDecisionCase(null);
            setSetupOrthoCase(null);
            toast.message("Case details are saved. Billing setup can be resumed from Patient record > Ortho.");
          }}
          onHandOff={() => {
            chairsideOrtho.reload?.();
          }}
          onClose={() => {
            setPendingChairsideBondingCase(null);
            setContractSigningCase(null);
            setBondingDecisionCase(null);
            setSetupOrthoCase(null);
          }}
          onSaved={() => {
            const bondingRow = pendingChairsideBondingCase;
            setPendingChairsideBondingCase(null);
            setSetupOrthoCase(null);
            if (bondingRow?.case?.id) {
              if (bondingRow.billing?.contract_signed_at) {
                setBondingDecisionCase(bondingRow);
              } else {
                setContractSigningCase(bondingRow);
              }
              chairsideOrtho.reload?.();
              return;
            }
            chairsideOrtho.reload?.();
          }}
        />
      ) : null}

      {contractSigningCase ? (
        <ChairsideContractSigningDialog
          row={contractSigningCase}
          submitting={contractSigningSubmitting}
          onMarkSigned={handleMarkChairsideContractSigned}
          onClose={() => {
            setContractSigningCase(null);
            chairsideOrtho.reload?.();
            toast.message("Complete case acceptance before starting bonding.");
          }}
        />
      ) : null}

      {bondingDecisionCase ? (
        <ChairsideBondingDecisionDialog
          row={bondingDecisionCase}
          submitting={bondingDecisionSubmitting || !!convertingOrthoVisit}
          onStartNow={handleStartBondingNow}
          onScheduleLater={handleScheduleBondingLater}
        />
      ) : null}
    </div>
  );
}

function ChairsideContractSigningDialog({ row, submitting, onMarkSigned, onClose }) {
  const caseRow = row?.case || {};
  const caseTypeLabel = caseRow.case_type
    ? String(caseRow.case_type).replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
    : "Case";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/40 px-4 py-8" data-testid="doctor-ortho-contract-signing">
      <div className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-700">Acceptance required</p>
          <h2 className="mt-2 text-xl font-bold text-slate-950">Mark accepted</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Confirm the patient accepted the Ortho case terms before starting the first bonding visit.
          </p>
        </div>
        <div className="px-6 py-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm font-bold text-slate-900">{caseRow.system_name || "Ortho case"}</p>
            <p className="mt-1 text-xs text-slate-500">
              {caseTypeLabel} · {Number(caseRow.estimated_total_visits || 0) || "Pending"} estimated adjustments
            </p>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            onClick={onClose}
            disabled={submitting}
            data-testid="doctor-ortho-contract-not-now"
          >
            Not now
          </button>
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onMarkSigned}
            disabled={submitting}
            data-testid="doctor-ortho-mark-contract-signed"
          >
            {submitting ? "Saving..." : "Mark accepted"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChairsideBondingDecisionDialog({ row, submitting, onStartNow, onScheduleLater }) {
  const caseRow = row?.case || {};
  const caseTypeLabel = caseRow.case_type
    ? String(caseRow.case_type).replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
    : "Case";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/40 px-4 py-8" data-testid="doctor-ortho-bonding-decision">
      <div className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-700">Ortho case ready</p>
          <h2 className="mt-2 text-xl font-bold text-slate-950">Start bonding now?</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Billing is saved. Choose whether this checked-in visit becomes the initial bonding visit now,
            or keep the original flow and schedule bonding for later.
          </p>
        </div>
        <div className="px-6 py-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm font-bold text-slate-900">{caseRow.system_name || "Ortho case"}</p>
            <p className="mt-1 text-xs text-slate-500">
              {caseTypeLabel} · {Number(caseRow.estimated_total_visits || 0) || "Pending"} estimated adjustments
            </p>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            onClick={onScheduleLater}
            disabled={submitting}
            data-testid="doctor-ortho-schedule-bonding-later"
          >
            Schedule for later
          </button>
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onStartNow}
            disabled={submitting}
            data-testid="doctor-ortho-start-bonding-now"
          >
            {submitting ? "Starting..." : "Start bonding now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function useChairsideOrtho(appt, patientId) {
  const [cases, setCases] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [systems, setSystems] = useState([]);
  const [wireOptions, setWireOptions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [timelineByCaseId, setTimelineByCaseId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      supabase.rpc("list_patient_ortho_cases", { p_patient_id: patientId }),
      supabase.rpc("list_ortho_doctor_records"),
      supabase.rpc("list_ortho_systems", { p_include_inactive: false }),
      supabase.rpc("list_ortho_wire_options", { p_include_inactive: false }),
      supabase.rpc("get_ortho_settings"),
    ]).then(([caseRes, doctorRes, systemRes, wireRes, settingsRes]) => {
      if (cancelled) return;
      setLoading(false);
      if (caseRes.error) {
        setError(caseRes.error.message || "Could not load ortho case.");
        setCases([]);
      } else {
        setCases(Array.isArray(caseRes.data) ? caseRes.data : []);
      }
      if (!doctorRes.error) setDoctors(Array.isArray(doctorRes.data) ? doctorRes.data : []);
      if (!systemRes.error) setSystems(Array.isArray(systemRes.data) ? systemRes.data : []);
      if (!wireRes.error) setWireOptions(Array.isArray(wireRes.data) ? wireRes.data : []);
      if (!settingsRes.error) setSettings(settingsRes.data || null);
    });
    return () => { cancelled = true; };
  }, [patientId, nonce]);

  useEffect(() => {
    const caseIds = (Array.isArray(cases) ? cases : [])
      .map((row) => row?.case?.id)
      .filter(Boolean);
    if (!caseIds.length) {
      setTimelineByCaseId({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(caseIds.map(async (caseId) => {
      const result = await supabase.rpc("get_ortho_case_timeline", { p_case_id: caseId });
      return [caseId, result.error ? [] : (Array.isArray(result.data) ? result.data : [])];
    })).then((entries) => {
      if (cancelled) return;
      setTimelineByCaseId(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [cases]);

  const activeCase = useMemo(() => {
    const rows = Array.isArray(cases) ? cases : [];
    const explicit = appt?.ortho_case_id
      ? rows.find((row) => row?.case?.id === appt.ortho_case_id)
      : null;
    if (explicit) return { ...explicit, timeline: timelineByCaseId[explicit.case?.id] || [] };
    const selected = rows.find((row) => [
      "active_pending_billing_setup",
      "active_pending_bonding",
      "active",
      "delinquent",
      "retention",
    ].includes(row?.case?.status)) || null;
    return selected ? { ...selected, timeline: timelineByCaseId[selected.case?.id] || [] } : null;
  }, [appt?.ortho_case_id, cases, timelineByCaseId]);

  const isOrthoVisit = !!appt?.ortho_case_id && !!appt?.ortho_visit_type && !!activeCase;

  return {
    rows: cases,
    activeCase,
    doctors,
    systems,
    wireOptions,
    settings,
    visitType: appt?.ortho_visit_type || null,
    appointmentId: appt?.id || null,
    isOrthoVisit,
    loading,
    error,
    reload: () => setNonce((n) => n + 1),
  };
}

function scrollToChairsideOrthoPanel() {
  const target = document.querySelector('[data-testid="doctor-ortho-inline-panel"], [data-testid="doctor-ortho-inline-saved"], [data-testid="doctor-ortho-visit-module"]');
  if (typeof target?.scrollIntoView === "function") {
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function buildChairsideOrthoVisitOptions(rows) {
  if (!Array.isArray(rows)) return [];
  const options = [];

  for (const row of rows) {
    const c = row?.case;
    if (!c?.id || !row?.billing) continue;
    const status = String(c.status || "").toLowerCase();
    const caseLabel = c.case_number || c.system_name || c.system || "Ortho case";
    const totalVisits = c.estimated_total_visits || row.billing?.estimated_total_visits || "?";

    if (status === "active_pending_bonding") {
      options.push({
        caseId: c.id,
        visitType: "bonding",
        label: "Bond",
        title: "Bond braces today",
        description: `${caseLabel} is ready for Installation Day 0.`,
      });
      continue;
    }

    if (status === "active" || status === "delinquent") {
      options.push({
        caseId: c.id,
        visitType: "adjustment",
        label: "Adjust",
        title: "Record adjustment",
        description: `${caseLabel} is active. Next adjustment ${Number(row.next_visit_number || 0) || "?"} of ${totalVisits}.`,
      });

      if (c.ready_for_debonding || c.debonding_timing) {
        options.push({
          caseId: c.id,
          visitType: "debonding",
          label: "Debond",
          title: "Remove braces",
          description: `${caseLabel} is flagged for debonding.`,
        });
      }
    }
  }

  return options;
}

function ChairsideOrthoActiveBanner({ context, recorded = false, onContinue }) {
  const row = context?.activeCase;
  const c = row?.case || {};
  const b = row?.billing || {};
  const visitType = context?.visitType || "adjustment";
  const current = currentOrthoVisitRow(row, visitType, context?.appointmentId);
  const visits = activeOrthoVisitRows(row);
  const adjustmentNumber = orthoAdjustmentNumber(current)
    || nextOrthoAdjustmentNumber(visits);
  const totalVisits = c.estimated_total_visits || b.estimated_total_visits || "?";
  const currentIsNoAdjustment = current?.ortho_visit
    && (
      ["emergency", "no_adjustment"].includes(String(current.ortho_visit.visit_type || "").toLowerCase())
      || current.ortho_visit.no_adjustment === true
      || String(current.ortho_visit.no_adjustment || "").toLowerCase() === "true"
    );
  const eventLabel = currentIsNoAdjustment
    ? orthoEventLabel(current)
    : orthoEventLabel({
      visit_type: visitType,
      adjustment_number: visitType === "adjustment" ? adjustmentNumber : null,
    });
  const continueLabel = currentIsNoAdjustment
    ? "no adjustment"
    : visitType === "bonding"
    ? "bonding"
    : visitType === "debonding"
      ? "debonding"
      : `adjustment ${adjustmentNumber || ""}`.trim();

  return (
    <section
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 shadow-sm"
      data-testid="doctor-ortho-active-banner"
      data-tutorial-id="doctor-ortho-visit-header"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-teal-950">Ortho Case Active</p>
          <span className="text-xs font-medium text-teal-700">
            {eventLabel}{visitType === "adjustment" && !currentIsNoAdjustment ? ` of ${totalVisits}` : ""}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-teal-700">
          {c.system_name || c.system || "Active ortho case"}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            recorded
              ? "bg-emerald-100 text-emerald-800"
              : "border border-amber-200 bg-amber-50 text-amber-800"
          }`}
          data-testid="doctor-ortho-active-banner-status"
        >
          {recorded ? "Recorded" : "Not saved"}
        </span>
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-teal-700 px-3 text-xs font-semibold text-white hover:bg-teal-800"
          data-testid="doctor-ortho-active-banner-continue"
        >
          {recorded ? "View visit" : `Continue ${continueLabel}`}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}

function ChairsideOrthoVisitModule({ open, onToggle, children }) {
  return (
    <section className="rounded-2xl border border-teal-200 bg-white shadow-sm" data-testid="doctor-ortho-visit-module">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center justify-between gap-3 px-4 py-1.5 text-left text-sm font-semibold text-teal-700 transition hover:bg-teal-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-200 ${open ? "border-b border-slate-100" : ""}`}
        data-testid="doctor-ortho-visit-toggle"
        aria-label={open ? "Collapse Ortho case actions" : "Open Ortho case actions"}
        aria-expanded={open}
      >
        <span className="min-w-0">
          Ortho Case Active
        </span>
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-teal-800">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open ? <div className="px-4 py-3">{children}</div> : null}
    </section>
  );
}

function ChairsideOrthoUnavailable({ row, settings, clinicRole, onSetupBilling }) {
  const needsBilling = row && (row.case?.status === "active_pending_billing_setup" || !row.billing);
  const canSetup = needsBilling && canCompleteOrthoBilling(settings, clinicRole);

  return (
    <div className="text-sm text-slate-600">
      {needsBilling ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>Billing setup is needed before this case can be added to today.</span>
          {canSetup ? (
            <button
              type="button"
              onClick={onSetupBilling}
              className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
            >
              Setup billing
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : (
        "This Ortho case has no chairside action available right now."
      )}
    </div>
  );
}

function ChairsideOrthoVisitPicker({ options, convertingKey, onSelect, onClose }) {
  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <div data-testid="doctor-ortho-visit-picker">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700">Choose Ortho action</p>
          <p className="mt-0.5 text-sm text-slate-600">Select the visit type before adding Ortho to today.</p>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800"
            aria-label="Close Ortho action picker"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {options.map((option) => {
          const pending = convertingKey === `${option.caseId}:${option.visitType}`;
          return (
            <button
              key={`${option.caseId}:${option.visitType}`}
              type="button"
              onClick={() => onSelect(option)}
              disabled={!!convertingKey}
              className="min-h-[84px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition hover:border-teal-300 hover:bg-teal-50 disabled:cursor-wait disabled:opacity-60"
              data-testid={`doctor-ortho-visit-option-${option.visitType}`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-950">{option.label}</span>
                {pending ? <Loader2 className="h-4 w-4 animate-spin text-teal-700" /> : <ChevronRight className="h-4 w-4 text-teal-700" />}
              </span>
              <span className="mt-1 block text-xs font-medium text-slate-700">{option.title}</span>
              <span className="mt-1 block text-xs leading-5 text-slate-500">{option.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ORTHO_PROCEDURE_OPTIONS = [
  { value: "tighten_existing", label: "Tighten existing" },
  { value: "archwire_change", label: "Archwire change" },
  { value: "bracket_rebond", label: "Bracket re-bond" },
  { value: "ipr", label: "IPR" },
  { value: "power_chain", label: "Power chain" },
  { value: "separators", label: "Separators" },
  { value: "bend_adjustment", label: "Bend/adjustment" },
  { value: "other", label: "Other" },
];
const ORTHO_ELASTICS_OPTIONS = [
  { value: "continuing_same", label: "Continuing same" },
  { value: "new_prescription", label: "New prescription" },
  { value: "discontinued", label: "Discontinued" },
  { value: "none_this_visit", label: "None this visit" },
  { value: "other", label: "Other" },
];
const ORTHO_NO_ADJUSTMENT_REASONS = [
  { value: "bracket_repair", label: "Bracket repair / rebond" },
  { value: "wire_repair", label: "Wire repair / replacement" },
  { value: "elastic_issue", label: "Elastic issue" },
  { value: "other", label: "Other non-adjustment issue" },
];
const ORTHO_LOCKABLE_SECTIONS = ["wire", "elastics"];

function OrthoChairsidePanel({
  context,
  appointmentId,
  currency,
  clinicRole,
  user,
  onSaved,
  open = true,
  onToggle,
  embedded = false,
  tutorialMode = false,
  onUndoConversion,
  onVisitTypeChanged,
  undoingConversion = false,
}) {
  const { activeClinicId } = useAuth();
  const row = context?.activeCase;
  const visitType = context?.visitType || "adjustment";
  const c = row?.case || {};
  const b = row?.billing || {};
  const visits = useMemo(() => activeOrthoVisitRows(row), [row]);
  const currentAppointmentVisit = useMemo(
    () => currentOrthoVisitRow(row, visitType, appointmentId),
    [appointmentId, row, visitType],
  );
  const currentOrthoVisitRecord = currentAppointmentVisit?.ortho_visit || null;
  const previousVisitRows = useMemo(
    () => visits
      .filter((visitRow) => visitRow !== currentAppointmentVisit)
      .filter((visitRow) => visitRow?.ortho_visit?.id)
      .filter((visitRow) => visitRow?.procedure_visit?.completed_at)
      .sort((a, b) => Number(b.ortho_visit?.visit_number || 0) - Number(a.ortho_visit?.visit_number || 0)),
    [currentAppointmentVisit, visits],
  );
  const previousVisitRow = previousVisitRows[0] || null;
  const latest = previousVisitRow?.ortho_visit || null;
  const rawWireOptions = useMemo(() => context?.wireOptions || [], [context?.wireOptions]);
  const wireOptionSignature = useMemo(
    () => rawWireOptions.map((option) => `${option.id}:${option.display_name || ""}`).join("|"),
    [rawWireOptions],
  );
  const wireOptionsRef = useRef({ signature: null, options: [] });
  if (wireOptionsRef.current.signature !== wireOptionSignature) {
    wireOptionsRef.current = { signature: wireOptionSignature, options: rawWireOptions };
  }
  const wireOptions = wireOptionsRef.current.options;
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noAdjustmentDialog, setNoAdjustmentDialog] = useState(false);
  const [noAdjustmentReason, setNoAdjustmentReason] = useState("bracket_repair");
  const [noAdjustmentSubmitting, setNoAdjustmentSubmitting] = useState(false);
  const [debondingDialog, setDebondingDialog] = useState(null);
  const [debondingTiming, setDebondingTiming] = useState("today");
  const [unflagReason, setUnflagReason] = useState("");
  const [debondingSubmitting, setDebondingSubmitting] = useState(false);
  const [debondingFlagOverride, setDebondingFlagOverride] = useState(null);
  const historicalForm = useMemo(
    () => buildOrthoVisitFormDefaults(
      visitType,
      latest,
      previousVisitRows,
      wireOptions,
      null,
    ),
    [latest, previousVisitRows, visitType, wireOptions],
  );
  const baseForm = useMemo(
    () => buildOrthoVisitFormDefaults(
      visitType,
      latest,
      previousVisitRows,
      wireOptions,
      currentOrthoVisitRecord,
    ),
    [currentOrthoVisitRecord, latest, previousVisitRows, visitType, wireOptions],
  );
  const [form, setForm] = useState(baseForm);
  const isLockedDefaultAdjustment = visitType === "adjustment" && !!latest && !currentAppointmentVisit?.ortho_visit?.id;
  const initialLocks = useMemo(
    () => ORTHO_LOCKABLE_SECTIONS.reduce((acc, key) => ({ ...acc, [key]: isLockedDefaultAdjustment }), {}),
    [isLockedDefaultAdjustment],
  );
  const [lockedSections, setLockedSections] = useState(initialLocks);
  // Wire / Elastics sections collapse by default. 80% of adjustments don't
  // change either, so showing them expanded turned the form into homework.
  // Bonding uses one honest summary line and only reveals the four wire
  // pickers when the dentist chooses or changes a wire.
  const [wiresExpanded, setWiresExpanded] = useState(false);
  const [elasticsExpanded, setElasticsExpanded] = useState(false);
  // Keep the everyday adjustment short. Extra procedures and the historical
  // notes rail stay collapsed until the dentist explicitly opens them.
  const [proceduresExpanded, setProceduresExpanded] = useState(false);
  const [notesTimelineExpanded, setNotesTimelineExpanded] = useState(false);

  const persistedDebondingScheduledToday = visitType === "debonding"
    && !currentAppointmentVisit?.ortho_visit?.id
    && !!c.ready_for_debonding
    && c.debonding_timing === "today";
  const isDebondingScheduledToday = visitType === "debonding"
    && !currentAppointmentVisit?.ortho_visit?.id
    && (debondingFlagOverride === true || persistedDebondingScheduledToday);

  useEffect(() => {
    setCollapsed(!!currentAppointmentVisit?.ortho_visit?.id || isDebondingScheduledToday);
    setForm(baseForm);
    setLockedSections(initialLocks);
    setWiresExpanded(false);
    setElasticsExpanded(false);
    setProceduresExpanded(false);
    setNotesTimelineExpanded(false);
  }, [appointmentId, baseForm, currentAppointmentVisit?.ortho_visit?.id, initialLocks, isDebondingScheduledToday, latest?.id, visitType]);

  useEffect(() => {
    setDebondingFlagOverride(null);
  }, [appointmentId]);

  const proceduresSummary = (() => {
    const selected = Array.isArray(form.procedures_this_visit) ? form.procedures_this_visit : [];
    const labels = ORTHO_PROCEDURE_OPTIONS.filter((o) => selected.includes(o.value)).map((o) => o.label);
    return labels.length ? labels.join(" · ") : "None recorded";
  })();

  const currentDoctor = useMemo(
    () => (context.doctors || []).find((doctor) => doctor.profile_id === user?.id) || null,
    [context.doctors, user?.id],
  );

  // Wire progression is a ladder — see suggestNextWire in the shared record
  // module. On bonding there is no "last wire", so it suggests the starting wire.
  const suggestedWire = useMemo(
    () => suggestNextWire(context?.wireOptions, latest?.upper_wire_option_id || latest?.lower_wire_option_id || null),
    [context?.wireOptions, latest],
  );
  const upperWireSelection = wireSelectionFromForm(form, "upper");
  const lowerWireSelection = wireSelectionFromForm(form, "lower");
  const previousUpperWireSelection = wireSelectionFromVisit(latest, "upper", wireOptions);
  const previousLowerWireSelection = wireSelectionFromVisit(latest, "lower", wireOptions);
  const upperWireLabel = formatWireSelection(upperWireSelection);
  const lowerWireLabel = formatWireSelection(lowerWireSelection);
  const hasBondingWireSelection = Boolean(upperWireLabel || lowerWireLabel);
  const bondingWireSummary = upperWireLabel && lowerWireLabel && upperWireLabel === lowerWireLabel
    ? `Starting wire · ${upperWireLabel}, both arches`
    : upperWireLabel || lowerWireLabel
      ? `Starting wire · ${[
        upperWireLabel ? `upper ${upperWireLabel}` : null,
        lowerWireLabel ? `lower ${lowerWireLabel}` : null,
      ].filter(Boolean).join(" · ")}`
      : suggestedWire
        ? `Suggested starting wire · ${suggestedWire.display_name}, both arches`
        : "Starting wire not selected";

  // Completed prior visits (newest first), excluding the visit being recorded now.
  const pastVisits = previousVisitRows;

  if (!context?.isOrthoVisit) return null;

  const moduleOpen = open !== false;
  const currentNoAdjustmentVisit = currentOrthoVisitRecord
    && (
      ["emergency", "no_adjustment"].includes(String(currentOrthoVisitRecord.visit_type || "").toLowerCase())
      || currentOrthoVisitRecord.no_adjustment === true
      || String(currentOrthoVisitRecord.no_adjustment || "").toLowerCase() === "true"
    );
  const displayAdjustmentNumber = orthoAdjustmentNumber(currentAppointmentVisit)
    || nextOrthoAdjustmentNumber(visits);
  const currentEventLabel = currentNoAdjustmentVisit
    ? orthoEventLabel(currentAppointmentVisit)
    : orthoEventLabel({
      visit_type: visitType,
      adjustment_number: visitType === "adjustment" ? displayAdjustmentNumber : null,
    });
  const title = currentNoAdjustmentVisit
    ? "No adjustment today"
    : visitType === "bonding" ? "Ortho Installation" : `Ortho ${labelize(visitType)}`;
  const caseSystemName = c.system_name || c.system || "Active ortho case";
  const requiresPhotos = visitType === "debonding";
  const previousGoal = latest?.next_visit_goal || "";
  const latestTimeline = orthoTimelineSnapshot(latest);
  const currentWearing = getLastActiveElasticsText(currentOrthoVisitRecord || latest);
  const resolvedUpperWireLabel = upperWireLabel || "Not recorded";
  const resolvedLowerWireLabel = lowerWireLabel || "Not recorded";
  // A reopened draft must still be compared with the previous completed
  // clinical visit. Comparing it with its own hydrated draft would make a
  // real wire change look like "Keeps" after refresh even though the saved
  // values themselves were correct.
  const wireChanged = isOrthoFormSectionChanged(form, historicalForm, "wire");
  const adjustmentWireSummary = resolvedUpperWireLabel === resolvedLowerWireLabel
    ? `${wireChanged ? "Changes to" : "Keeps"} ${resolvedUpperWireLabel}, both arches`
    : `${wireChanged ? "Changes to" : "Keeps"} U ${resolvedUpperWireLabel} · L ${resolvedLowerWireLabel}`;
  const elasticsChanged = isOrthoFormSectionChanged(form, historicalForm, "elastics");
  const ohChanged = isOrthoFormSectionChanged(form, historicalForm, "oh");
  const adjustmentElasticsSummary = elasticsChanged
    ? buildElasticsDisplayText(form, currentWearing)
    : currentWearing && currentWearing !== "None"
      ? `${currentWearing} — continue`
      : "None this visit";
  const timelineRows = Array.isArray(row?.timeline) && row.timeline.length
    ? row.timeline
    : pastVisits;
  const currentTimelineEventId = currentAppointmentVisit?.ortho_visit?.id || null;
  const notesTimelineVisits = timelineRows
    .filter((visitRow) => (visitRow?.event_id || visitRow?.ortho_visit?.id) !== currentTimelineEventId)
    .filter((visitRow) => hasOrthoTimelineContent(visitRow?.ortho_visit || visitRow));
  const isMigrated = c?.migration_source === "migrated";
  // Transfer (migrated) cases arrive already bonded at another clinic, so the
  // first visit at our clinic is legitimately an adjustment.
  const shouldBlockAdjustment = visitType === "adjustment" && !latest && !currentAppointmentVisit?.ortho_visit?.id && !isMigrated;
  const canUndoConversion = !!onUndoConversion;
  const canManageDebonding =
    c.status === "active"
    && (
      clinicRole === "clinic_admin"
      || (currentDoctor?.id && currentDoctor.id === c.primary_doctor_id)
    );
  const canShowDebondingAction = canManageDebonding && !["active_pending_bonding", "retention", "completed", "paused", "cancelled"].includes(c.status);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setWireSelection(side, selection) {
    const payload = wireSelectionPayload(selection, wireOptions);
    setForm((prev) => ({
      ...prev,
      [`${side}_wire_material`]: selection.material || "",
      [`${side}_wire_size`]: selection.size || "",
      [`${side}_wire_custom`]: selection.custom || "",
      [`${side}_wire_option_id`]: payload.optionId || "",
      [`${side}_wire_other_text`]: "",
    }));
  }

  function unlockSection(section) {
    setLockedSections((prev) => ({ ...prev, [section]: false }));
  }

  function resetSection(section) {
    setForm((prev) => resetOrthoFormSection(prev, baseForm, section));
    setLockedSections((prev) => ({ ...prev, [section]: true }));
  }

  function sectionChanged(section) {
    return isOrthoFormSectionChanged(form, baseForm, section);
  }

  function toggleProcedure(value) {
    setForm((prev) => {
      const current = Array.isArray(prev.procedures_this_visit) ? prev.procedures_this_visit : [];
      const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, procedures_this_visit: next };
    });
    if (value === "archwire_change" && !form.procedures_this_visit?.includes("archwire_change")) {
      unlockSection("wire");
      setWiresExpanded(true);
    }
  }

  function applySuggestedWire() {
    if (!suggestedWire) return;
    const selection = wireSelectionFromOption(suggestedWire);
    setForm((prev) => ({
      ...prev,
      upper_wire_option_id: suggestedWire.id,
      lower_wire_option_id: suggestedWire.id,
      upper_wire_other_text: "",
      lower_wire_other_text: "",
      upper_wire_material: selection.material,
      upper_wire_size: selection.size,
      upper_wire_custom: "",
      lower_wire_material: selection.material,
      lower_wire_size: selection.size,
      lower_wire_custom: "",
    }));
  }

  function handleBondingWireAction() {
    if (!hasBondingWireSelection && suggestedWire) {
      applySuggestedWire();
      return;
    }
    setWiresExpanded(true);
  }

  function addPhotos(e) {
    const files = Array.from(e.target.files || []);
    setForm((prev) => ({
      ...prev,
      photoNames: Array.from(new Set([...(prev.photoNames || []), ...files.map((file) => file.name)])),
      photoFiles: [...(prev.photoFiles || []), ...files],
    }));
    e.target.value = "";
  }

  async function uploadPendingVisitPhotos(targetVisitType) {
    let uploadedPhotos = [];
    const pendingFiles = Array.isArray(form.photoFiles) ? form.photoFiles : [];
    if (pendingFiles.length && c.patient_id && activeClinicId) {
      const stage = orthoStageForVisitType(targetVisitType);
      const uploads = await Promise.all(pendingFiles.map((file) => uploadPatientFile({
        patientId: c.patient_id,
        clinicId: activeClinicId,
        file,
        category: "ortho_case_photos",
        orthoCaseId: c.id,
        orthoStage: stage,
      })));
      uploadedPhotos = uploads.map((res, i) => ({
        name: pendingFiles[i]?.name || res?.original_filename || "photo",
        storage_path: res?.storage_path || null,
        file_id: res?.id || null,
        stage,
        captured_at: new Date().toISOString(),
        source: `doctor_inline_ortho_${targetVisitType}`,
      }));
    } else if (pendingFiles.length) {
      toast.error("Photos weren't attached (missing patient or clinic) — saving the visit without them.");
    }
    return uploadedPhotos;
  }

  function buildVisitPayload(targetVisitType, uploadedPhotos = []) {
    const idempotencyKey = buildOrthoVisitIdempotencyKey({
      caseId: c.id,
      appointmentId,
      visitType: targetVisitType,
    });
    const elasticsText = buildElasticsDisplayText(form, currentWearing);
    const upperWirePayload = wireSelectionPayload(upperWireSelection, wireOptions);
    const lowerWirePayload = wireSelectionPayload(lowerWireSelection, wireOptions);
    return {
      idempotency_key: idempotencyKey,
      appointment_id: appointmentId,
      visit_type: targetVisitType,
      arch_bonded_upper: !!form.arch_bonded_upper,
      arch_bonded_lower: !!form.arch_bonded_lower,
      procedures_this_visit: Array.isArray(form.procedures_this_visit) ? form.procedures_this_visit : [],
      procedures_other_text: form.procedures_other_text.trim() || null,
      upper_wire_option_id: upperWirePayload.optionId,
      lower_wire_option_id: lowerWirePayload.optionId,
      upper_wire_other_text: null,
      lower_wire_other_text: null,
      upper_wire_material: upperWirePayload.material,
      upper_wire_size: upperWirePayload.size,
      upper_wire_custom: upperWirePayload.custom,
      lower_wire_material: lowerWirePayload.material,
      lower_wire_size: lowerWirePayload.size,
      lower_wire_custom: lowerWirePayload.custom,
      elastics_status: form.elastics_status || "none_this_visit",
      elastics_detail: form.elastics_detail.trim() || null,
      elastics_prescribed: elasticsText,
      oh_status: form.oh_status,
      next_visit_interval_weeks: Number(form.next_visit_interval_weeks || 4),
      next_visit_goal: form.next_visit_goal.trim() || null,
      notes: form.notes.trim() || null,
      photos: [...(Array.isArray(form.savedPhotos) ? form.savedPhotos : []), ...uploadedPhotos],
    };
  }

  async function handleSave() {
    if (submitting) return;
    if (currentNoAdjustmentVisit) {
      toast.info("No adjustment is already recorded for this appointment.");
      return;
    }
    if (!appointmentId || !c.id) {
      toast.error("Open this ortho visit from the checked-in appointment.");
      return;
    }
    if (visitType === "bonding" && !form.arch_bonded_upper && !form.arch_bonded_lower) {
      toast.error("Select at least one bonded arch.");
      return;
    }
    if (requiresPhotos && form.photoNames.length === 0) {
      const ok = window.confirm(
        "Final photos are missing for this debonding visit. The DA can attach them later from the patient's Ortho tab.\n\nSave debonding without photos?",
      );
      if (!ok) return;
    }
    if (visitType === "adjustment" && !currentAppointmentVisit?.ortho_visit?.id && !latest && !isMigrated) {
      toast.error("Bond the case first before recording an adjustment visit.");
      return;
    }
    const procedures = Array.isArray(form.procedures_this_visit) ? form.procedures_this_visit : [];
    if (procedures.includes("archwire_change")) {
      const upperOk = !!formatWireSelection(upperWireSelection);
      const lowerOk = !!formatWireSelection(lowerWireSelection);
      if (!upperOk || !lowerOk) {
        toast.error("Archwire change requires upper and lower wire values.");
        return;
      }
    }
    // Bonding guard: don't silently save a bonded arch with no wire on record.
    // Soft confirm (not a hard block) — the dentist can still override.
    if (visitType === "bonding") {
      const upperMissing = form.arch_bonded_upper && !formatWireSelection(upperWireSelection);
      const lowerMissing = form.arch_bonded_lower && !formatWireSelection(lowerWireSelection);
      if (upperMissing || lowerMissing) {
        const which = upperMissing && lowerMissing ? "both arches" : upperMissing ? "the upper arch" : "the lower arch";
        const ok = window.confirm(`You bonded ${which} but didn't pick a wire for it.\n\nSave without recording the wire?`);
        if (!ok) return;
      }
    }
    if (tutorialMode) {
      toast.success(`${title} saved for checkout.`);
      setCollapsed(true);
      onSaved?.();
      return;
    }
    // A signed contract must be on record before the first bonding visit. Rather
    // than let the DB reject the save with a raw error, confirm the signature
    // here and record it (same RPC as the "Mark accepted" dialog) so the dentist
    // isn't bounced to the Ortho tab mid-bonding. The contract PDF is already
    // generated by billing setup, which mark_ortho_contract_signed requires.
    if (visitType === "bonding" && !b?.contract_signed_at) {
      const confirmedSigned = window.confirm(
        "A signed Ortho contract must be on record before bonding.\n\nHas the patient signed the printed contract? Confirming records the signature and starts bonding.",
      );
      if (!confirmedSigned) return;
      setSubmitting(true);
      const { error: signError } = await supabase.rpc("mark_ortho_contract_signed", { p_case_id: c.id });
      if (signError) {
        setSubmitting(false);
        toast.error(signError.message || "Could not record the signed contract. Generate the contract from the patient's Ortho tab first, then try again.");
        return;
      }
    }
    setSubmitting(true);

    // Upload any picked photos to Patient Files (real storage) first, so the
    // visit's photo entries carry a real storage path and show up in Files.
    let uploadedPhotos = [];
    try {
      uploadedPhotos = await uploadPendingVisitPhotos(visitType);
    } catch (uploadErr) {
      setSubmitting(false);
      toast.error(uploadErr?.message || "Could not upload photos. Try again.");
      return;
    }

    const visitPayload = buildVisitPayload(visitType, uploadedPhotos);
    const { error } = currentOrthoVisitRecord?.id
      ? await supabase.rpc("update_ortho_visit_draft", {
        p_visit_id: currentOrthoVisitRecord.id,
        p_input: visitPayload,
      })
      : await supabase.rpc("record_ortho_adjustment_visit", {
        p_case_id: c.id,
        p_input: visitPayload,
      });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not save ortho visit.");
      return;
    }
    toast.success(`${title} saved for checkout.`);
    setCollapsed(true);
    onSaved?.();
    context.reload?.();
  }

  async function handleNoAdjustment() {
    if (visitType !== "adjustment" || noAdjustmentSubmitting) return;
    if (!appointmentId || !c.id) {
      toast.error("Open this ortho visit from the checked-in appointment.");
      return;
    }
    if (!noAdjustmentReason) return;

    setNoAdjustmentSubmitting(true);
    const ohStatusCandidate = form.oh_status || latest?.oh_status || "good";
    const ohStatus = ["good", "fair", "poor"].includes(ohStatusCandidate) ? ohStatusCandidate : "good";
    const intervalCandidate = Number(
      form.next_visit_interval_weeks || latest?.next_visit_interval_weeks || 4,
    );
    const nextVisitIntervalWeeks = [4, 5, 6, 8].includes(intervalCandidate) ? intervalCandidate : 4;
    const idempotencyKey = buildOrthoVisitIdempotencyKey({
      caseId: c.id,
      appointmentId,
      visitType: "no_adjustment",
    });
    const { error } = await supabase.rpc("record_ortho_no_adjustment_visit", {
      p_case_id: c.id,
      p_input: {
        idempotency_key: idempotencyKey,
        appointment_id: appointmentId,
        non_adjustment_reason: noAdjustmentReason,
        oh_status: ohStatus,
        next_visit_interval_weeks: nextVisitIntervalWeeks,
      },
    });
    setNoAdjustmentSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not record no-adjustment visit.");
      return;
    }
    toast.success("No adjustment recorded for checkout.");
    setNoAdjustmentDialog(false);
    setCollapsed(true);
    onSaved?.();
    context.reload?.();
  }

  async function handleFlagDebonding() {
    if (!c.id || debondingSubmitting) return;
    if (debondingTiming === "today" && !appointmentId) {
      toast.error("Open the checked-in appointment before recording Debonding today.");
      return;
    }
    setDebondingSubmitting(true);
    let uploadedPhotos = [];
    if (debondingTiming === "today") {
      try {
        uploadedPhotos = await uploadPendingVisitPhotos("debonding");
      } catch (uploadErr) {
        setDebondingSubmitting(false);
        toast.error(uploadErr?.message || "Could not upload photos. Try again.");
        return;
      }
    }
    const { data, error } = debondingTiming === "today"
      ? await supabase.rpc("record_ortho_debonding_today", {
        p_case_id: c.id,
        p_appointment_id: appointmentId,
        p_input: buildVisitPayload("debonding", uploadedPhotos),
      })
      : await supabase.rpc("flag_case_for_debonding", {
        p_case_id: c.id,
        p_debonding_timing: debondingTiming,
        p_visit_id: currentAppointmentVisit?.ortho_visit?.id || null,
        p_appointment_id: appointmentId || null,
      });
    setDebondingSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not schedule debonding.");
      return;
    }
    if (debondingTiming === "today") {
      setDebondingFlagOverride(false);
      onVisitTypeChanged?.({
        caseId: c.id,
        appointmentId,
        visitType: "debonding",
      });
      setCollapsed(true);
      onSaved?.();
    } else {
      setDebondingFlagOverride(true);
    }
    toast.success(
      debondingTiming === "today"
        ? "Debonding draft saved for checkout."
        : "Case scheduled for debonding.",
    );
    setDebondingDialog(null);
    context.reload?.();
  }

  async function handleUnflagDebonding() {
    if (!c.id || debondingSubmitting) return;
    setDebondingSubmitting(true);
    const { data, error } = await supabase.rpc("unflag_case_for_debonding", {
      p_case_id: c.id,
      p_reason: unflagReason.trim() || null,
    });
    setDebondingSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not cancel debonding schedule.");
      return;
    }
    const currentAppointmentReverted = (data?.reverted_appointments || [])
      .some((appointment) => appointment?.id === appointmentId);
    setDebondingFlagOverride(false);
    if (currentAppointmentReverted || visitType === "debonding") {
      onVisitTypeChanged?.({
        caseId: c.id,
        appointmentId,
        visitType: "adjustment",
      });
      setCollapsed(false);
    }
    toast.success("Debonding schedule cancelled.");
    setDebondingDialog(null);
    setUnflagReason("");
    context.reload?.();
  }

  function openDebondingDialog(mode) {
    setDebondingDialog(mode);
    setDebondingTiming(c.debonding_timing || "today");
    setUnflagReason("");
  }

  function requestUndoConversion() {
    if (currentAppointmentVisit?.ortho_visit?.id) {
      const ok = window.confirm(
        visitType === "debonding"
          ? "Undo this Debonding decision and return today's appointment to Adjustment? The saved Debonding draft and its terminal charge will be reversed."
          : "Remove this saved Ortho visit from today? This will void the Ortho visit and reverse its charge/production credit.",
      );
      if (!ok) return;
    }
    onUndoConversion?.();
  }

  const debondingFlagged = debondingFlagOverride ?? !!c.ready_for_debonding;
  const treatmentStatusBlock = canShowDebondingAction && visitType !== "debonding" ? (
    <OrthoDebondingTreatmentStatus
      flagged={debondingFlagged}
      onFlag={() => openDebondingDialog("flag")}
      onUnflag={() => openDebondingDialog("unflag")}
      compact={visitType === "adjustment"}
    />
  ) : null;
  const debondingModal = debondingDialog === "flag" ? (
    <OrthoDebondingFlagModal
      timing={debondingTiming}
      onTimingChange={setDebondingTiming}
      submitting={debondingSubmitting}
      onCancel={() => setDebondingDialog(null)}
      onConfirm={handleFlagDebonding}
    />
  ) : debondingDialog === "unflag" ? (
    <OrthoDebondingUnflagModal
      reason={unflagReason}
      onReasonChange={setUnflagReason}
      submitting={debondingSubmitting}
      onCancel={() => setDebondingDialog(null)}
      onConfirm={handleUnflagDebonding}
    />
  ) : null;
  const noAdjustmentModal = noAdjustmentDialog ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" data-testid="doctor-ortho-no-adjustment-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-950">No adjustment today</h3>
        <p className="mt-2 text-sm text-slate-600">
          Record why the patient needed an Ortho visit without a routine adjustment. This does not save the normal adjustment or add a routine charge.
        </p>
        <label className="mt-4 block text-sm font-semibold text-slate-800" htmlFor="doctor-ortho-no-adjustment-reason">
          Reason
        </label>
        <select
          id="doctor-ortho-no-adjustment-reason"
          aria-label="No adjustment reason"
          value={noAdjustmentReason}
          onChange={(event) => setNoAdjustmentReason(event.target.value)}
          disabled={noAdjustmentSubmitting}
          className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:opacity-60"
        >
          {ORTHO_NO_ADJUSTMENT_REASONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setNoAdjustmentDialog(false)}
            disabled={noAdjustmentSubmitting}
            className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleNoAdjustment}
            disabled={noAdjustmentSubmitting || !noAdjustmentReason}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-teal-600 px-3 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            data-testid="doctor-ortho-no-adjustment-confirm"
          >
            {noAdjustmentSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Record no adjustment
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (collapsed) {
    if (isDebondingScheduledToday) {
      return (
        <section
          className="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3 shadow-sm"
          data-testid="doctor-ortho-debonding-today-scheduled"
          data-tutorial-id="doctor-ortho-debonding-today-scheduled"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-950">Debonding scheduled for today</p>
              <p className="text-xs text-teal-800">
                Today is now Debonding Day. Open the record only when you are ready to document removal.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-50"
              data-testid="doctor-ortho-open-debonding"
              data-tutorial-id="doctor-ortho-open-debonding"
            >
              Open debonding
            </button>
          </div>
          {treatmentStatusBlock ? <div className="mt-2">{treatmentStatusBlock}</div> : null}
          {debondingModal}
        </section>
      );
    }
    return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm" data-testid="doctor-ortho-inline-saved" data-tutorial-id="doctor-ortho-inline-saved">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-emerald-950">{title} draft saved</p>
            <p className="text-xs text-emerald-800">
              {currentEventLabel}{visitType === "adjustment" && !currentNoAdjustmentVisit ? ` of ${c.estimated_total_visits || b.estimated_total_visits || "?"}` : ""} is saved and editable until checkout. {currentNoAdjustmentVisit ? "The no-adjustment record is ready for checkout." : "Checkout will finalize this visit."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canUndoConversion ? (
              <button
                type="button"
                onClick={requestUndoConversion}
                disabled={undoingConversion || context.loading}
                className="inline-flex items-center gap-1.5 px-1 py-1 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:cursor-wait disabled:opacity-60"
                data-testid="doctor-ortho-undo-conversion"
              >
                {undoingConversion ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                {visitType === "debonding" ? "Undo Debonding" : "Remove from today"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
              data-testid="doctor-ortho-inline-reopen"
            >
              Reopen
            </button>
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          </div>
        </div>
        {c.ready_for_debonding ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
            <Flag className="mr-1 inline h-3.5 w-3.5" />
            This case is scheduled for debonding.
          </div>
        ) : null}
        {treatmentStatusBlock ? <div className="mt-3">{treatmentStatusBlock}</div> : null}
        {debondingModal}
        {noAdjustmentModal}
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-teal-200 bg-white shadow-sm" data-testid="doctor-ortho-visit-module">
      {!embedded ? (
        <button
          type="button"
          onClick={onToggle}
          className={`flex w-full items-center justify-between gap-3 px-4 py-1.5 text-left text-sm font-semibold text-teal-700 transition hover:bg-teal-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-200 ${moduleOpen ? "border-b border-slate-100" : ""}`}
          data-testid="doctor-ortho-visit-toggle"
          data-tutorial-id="doctor-ortho-visit-header"
          aria-label={moduleOpen ? "Collapse Ortho case visit" : "Open Ortho case visit"}
          aria-expanded={moduleOpen}
        >
          <span className="min-w-0">
            Ortho Case Active
          </span>
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-teal-800">
            <ChevronDown className={`h-4 w-4 transition-transform ${moduleOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
      ) : null}

      {moduleOpen ? (
        <>
          <div className="space-y-2.5 px-4 py-3" data-testid="doctor-ortho-inline-panel" data-tutorial-id="doctor-ortho-inline-panel">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-slate-950">
                {title}{visitType === "adjustment" ? <span className="font-medium text-slate-500"> · {caseSystemName}</span> : null}
              </h2>
              {isMigrated ? (
                <span
                  className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-violet-700"
                  title="Imported from another clinic. Bonding already happened, first visit here is an adjustment."
                >
                  Transfer case
                </span>
              ) : null}
            </div>
            {visitType === "adjustment" ? null : <p className="text-xs text-slate-500">{caseSystemName}</p>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="rounded-xl bg-teal-50 px-3 py-2 text-right text-xs" data-tutorial-id="doctor-ortho-visit-number">
              <p className="font-semibold uppercase tracking-[0.12em] text-teal-700">Case event</p>
              <p className="text-sm font-bold text-teal-950">
                {visitType === "adjustment"
                  ? `Adj ${displayAdjustmentNumber || "?"} of ${c.estimated_total_visits || b.estimated_total_visits || "?"}`
                  : currentEventLabel}
              </p>
            </div>
            {canUndoConversion ? (
              <button
                type="button"
                onClick={requestUndoConversion}
                disabled={undoingConversion || submitting || context.loading}
                className="inline-flex items-center gap-1 px-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 disabled:cursor-wait disabled:opacity-60"
                data-testid="doctor-ortho-undo-conversion"
              >
                {undoingConversion ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                {visitType === "debonding" ? "Undo Debonding" : "Remove from today"}
              </button>
            ) : null}
          </div>
        </div>

        {shouldBlockAdjustment ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Bond the case first before recording an adjustment visit.
          </div>
        ) : null}

        {latest ? visitType === "adjustment" ? (
          <>
          <div className="rounded-lg bg-teal-50 px-3.5 py-2.5 text-sm leading-relaxed text-slate-700" data-tutorial-id="doctor-ortho-last-visit" data-testid="doctor-ortho-last-visit">
            <span className="font-semibold text-teal-700">Last visit</span>
            <span> · {[
              latestTimeline.wire || null,
              `elastics ${latestTimeline.elastics || "none"}`,
              latestTimeline.oh ? `OH ${latestTimeline.oh}` : null,
            ].filter(Boolean).join(" · ")}</span>
          </div>
          <div className="mx-auto w-full max-w-4xl" data-testid="doctor-ortho-adjustment-flow">
            <section data-testid="doctor-ortho-adjustment-this-visit" data-tutorial-id="doctor-ortho-adjustment-this-visit">
              <p className="flex items-center gap-2 border-b border-slate-100 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <Stethoscope className="h-3.5 w-3.5" />
                This visit
              </p>

              <div className="pt-1" data-tutorial-id="doctor-ortho-adjustment-arches">
                <InlineRow label="Arch treated" compact>
                  <InlineToggle compact active={form.arch_bonded_upper} onClick={() => setField("arch_bonded_upper", !form.arch_bonded_upper)}>Upper</InlineToggle>
                  <InlineToggle compact active={form.arch_bonded_lower} onClick={() => setField("arch_bonded_lower", !form.arch_bonded_lower)}>Lower</InlineToggle>
                </InlineRow>
                {latest ? <p className="pb-2 text-xs text-slate-400">Carried from last visit — tap change only when treatment changes.</p> : null}
              </div>

              <AdjustmentCurrentStateRow
                label="Wire"
                summary={adjustmentWireSummary}
                changed={wireChanged}
                expanded={wiresExpanded}
                onChange={() => {
                  unlockSection("wire");
                  setWiresExpanded((value) => !value);
                }}
                testId="doctor-ortho-adjustment-wire-summary"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <OrthoWireGaugeSelect
                    label="Upper"
                    material={form.upper_wire_material}
                    size={form.upper_wire_size}
                    custom={form.upper_wire_custom}
                    options={context.wireOptions || []}
                    previous={previousUpperWireSelection}
                    onChange={(selection) => setWireSelection("upper", selection)}
                    disabled={false}
                  />
                  <OrthoWireGaugeSelect
                    label="Lower"
                    material={form.lower_wire_material}
                    size={form.lower_wire_size}
                    custom={form.lower_wire_custom}
                    options={context.wireOptions || []}
                    previous={previousLowerWireSelection}
                    onChange={(selection) => setWireSelection("lower", selection)}
                    disabled={false}
                  />
                </div>
              </AdjustmentCurrentStateRow>

              <AdjustmentCurrentStateRow
                label="Elastics"
                summary={adjustmentElasticsSummary}
                changed={elasticsChanged}
                expanded={elasticsExpanded}
                onChange={() => {
                  unlockSection("elastics");
                  setElasticsExpanded((value) => !value);
                }}
                testId="doctor-ortho-adjustment-elastics-summary"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[220px_1fr]">
                  <select
                    aria-label="Elastics change"
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    value={form.elastics_status}
                    onChange={(event) => setField("elastics_status", event.target.value)}
                  >
                    {ORTHO_ELASTICS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {["new_prescription", "other", "continuing_same"].includes(form.elastics_status) ? (
                    <input
                      aria-label="Elastics details"
                      className="h-10 min-w-0 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                      value={form.elastics_detail}
                      onChange={(event) => setField("elastics_detail", event.target.value)}
                      placeholder="Configuration and wear"
                    />
                  ) : null}
                </div>
              </AdjustmentCurrentStateRow>

              <div data-tutorial-id="doctor-ortho-oh-status">
                <InlineRow
                  label={(
                    <span className="inline-flex items-center gap-2">
                      Oral hygiene
                      {ohChanged ? <ChangedIndicator /> : null}
                    </span>
                  )}
                  compact
                >
                  <InlineSegmented
                    compact
                    value={form.oh_status}
                    options={[
                      { value: "good", label: "Good" },
                      { value: "fair", label: "Fair" },
                      { value: "poor", label: "Poor" },
                    ]}
                    onChange={(value) => setField("oh_status", value)}
                  />
                </InlineRow>
              </div>

              <div data-tutorial-id="doctor-ortho-photos">
                <InlineRow label="Photos" compact>
                  <button type="button" onClick={() => cameraInputRef.current?.click()} className="inline-flex h-8 items-center justify-center rounded-lg bg-teal-600 px-2.5 text-xs font-semibold text-white hover:bg-teal-700">
                    Take photo
                  </button>
                  <button type="button" onClick={() => uploadInputRef.current?.click()} className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                    Upload
                  </button>
                </InlineRow>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={addPhotos} className="sr-only" />
                <input ref={uploadInputRef} data-testid="doctor-ortho-photo-upload" type="file" multiple accept="image/*" onChange={addPhotos} className="sr-only" />
                <OrthoPhotoSelectionStatus
                  file={form.photoFiles?.[0]}
                  count={form.photoNames.length}
                  fallback="Optional"
                />
              </div>
            </section>

            <section className="mt-3 border-t border-slate-200 pt-3" data-testid="doctor-ortho-adjustment-next-visit" data-tutorial-id="doctor-ortho-adjustment-next-visit">
              <p className="flex items-center gap-2 border-b border-slate-100 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <ClipboardList className="h-3.5 w-3.5" />
                Next visit
              </p>
              <div className="pt-1" data-tutorial-id="doctor-ortho-next-visit-interval">
                <InlineRow label="Come back in" compact>
                  <InlineSegmented
                    compact
                    value={String(form.next_visit_interval_weeks)}
                    options={[4, 5, 6, 8].map((number) => ({ value: String(number), label: `${number}w` }))}
                    onChange={(value) => setField("next_visit_interval_weeks", Number(value))}
                  />
                </InlineRow>
              </div>
              <div className="pt-2" data-tutorial-id="doctor-ortho-notes">
                <textarea
                  aria-label="Note for this visit and goal for next time"
                  className="min-h-[84px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  value={form.notes}
                  onChange={(event) => setField("notes", event.target.value)}
                  placeholder="Note for this visit + goal for next time..."
                />
              </div>
            </section>

            <div className="mt-3 border-t border-slate-200" data-testid="doctor-ortho-adjustment-quiet-rows">
              <QuietOrthoDisclosure
                icon={ClipboardList}
                title="Procedures"
                hint={proceduresSummary === "None recorded" ? "None" : proceduresSummary}
                expanded={proceduresExpanded}
                onToggle={() => setProceduresExpanded((value) => !value)}
                testId="doctor-ortho-adjustment-procedures"
              >
                <div className="flex flex-wrap gap-2">
                  {ORTHO_PROCEDURE_OPTIONS.map((option) => (
                    <InlineToggle
                      compact
                      key={option.value}
                      active={form.procedures_this_visit?.includes(option.value)}
                      onClick={() => toggleProcedure(option.value)}
                    >
                      {option.label}
                    </InlineToggle>
                  ))}
                </div>
                {form.procedures_this_visit?.includes("other") ? (
                  <input
                    className="mt-2 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    value={form.procedures_other_text}
                    onChange={(event) => setField("procedures_other_text", event.target.value)}
                    placeholder="Specify extra procedure..."
                  />
                ) : null}
              </QuietOrthoDisclosure>

              <QuietOrthoDisclosure
                icon={History}
                title="Notes timeline"
                hint={`${notesTimelineVisits.length} entr${notesTimelineVisits.length === 1 ? "y" : "ies"}`}
                expanded={notesTimelineExpanded}
                onToggle={() => setNotesTimelineExpanded((value) => !value)}
                testId="doctor-ortho-notes-timeline"
              >
                <OrthoNotesTimeline visits={notesTimelineVisits} doctors={context.doctors || []} />
              </QuietOrthoDisclosure>
            </div>
          </div>
          </>
        ) : (
          <div className="rounded-lg bg-teal-50 px-3.5 py-2.5 text-xs leading-relaxed text-teal-900" data-tutorial-id="doctor-ortho-last-visit" data-testid="doctor-ortho-last-visit">
            <div className="flex items-center justify-between gap-2 border-b border-teal-100 pb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-700">Last visit</span>
              <span className="font-semibold">{orthoEventLabel(latest)}</span>
            </div>
            <div className="mt-1.5 grid grid-cols-[52px_1fr] gap-x-2 gap-y-1">
              <span className="font-semibold text-teal-700">Appliance</span>
              <span>{[
                latestTimeline.wire || null,
                `elastics ${latestTimeline.elastics || "none"}`,
                latestTimeline.oh ? `OH ${latestTimeline.oh}` : null,
              ].filter(Boolean).join(" · ")}</span>
              {previousGoal ? (<><span className="font-semibold text-teal-700">Goal</span><span>{previousGoal}</span></>) : null}
              {latest.notes ? (<><span className="font-semibold text-teal-700">Notes</span><span>{latest.notes}</span></>) : null}
            </div>
            {isLockedDefaultAdjustment ? (
              <p className="mt-1.5 text-[11px] italic text-teal-700">Kept unless you open a section below.</p>
            ) : null}
          </div>
        ) : null}

        {visitType === "bonding" ? (
          <div
            className="mx-auto w-full max-w-4xl"
            data-testid="doctor-ortho-bonding-flow"
          >
            <section data-testid="doctor-ortho-bonding-this-visit" data-tutorial-id="doctor-ortho-bonding-this-visit">
              <p className="flex items-center gap-2 border-b border-slate-100 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <Stethoscope className="h-3.5 w-3.5" />
                This visit
              </p>

              <div className="pt-1" data-tutorial-id="doctor-ortho-bonding-arches">
                <InlineRow label="Arch bonded">
                  <InlineToggle active={form.arch_bonded_upper} onClick={() => setField("arch_bonded_upper", !form.arch_bonded_upper)}>Upper</InlineToggle>
                  <InlineToggle active={form.arch_bonded_lower} onClick={() => setField("arch_bonded_lower", !form.arch_bonded_lower)}>Lower</InlineToggle>
                </InlineRow>
              </div>

              <div className="border-b border-slate-100 py-2" data-tutorial-id="doctor-ortho-wires">
                <button
                  type="button"
                  onClick={handleBondingWireAction}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-left text-sm font-semibold text-teal-950 hover:bg-teal-100"
                  data-testid="doctor-ortho-bonding-wire-summary"
                  data-tutorial-id="doctor-ortho-bonding-wire-summary"
                >
                  <span className="min-w-0 truncate">{bondingWireSummary}</span>
                  <span className="shrink-0 text-xs font-semibold text-teal-700">
                    {hasBondingWireSelection ? "Change" : suggestedWire ? "Use" : "Choose"}
                  </span>
                </button>
                {wiresExpanded ? (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="doctor-ortho-bonding-wire-editor">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <OrthoWireGaugeSelect
                        label="Upper"
                        material={form.upper_wire_material}
                        size={form.upper_wire_size}
                        custom={form.upper_wire_custom}
                        options={context.wireOptions || []}
                        previous={previousUpperWireSelection}
                        onChange={(selection) => setWireSelection("upper", selection)}
                        disabled={false}
                      />
                      <OrthoWireGaugeSelect
                        label="Lower"
                        material={form.lower_wire_material}
                        size={form.lower_wire_size}
                        custom={form.lower_wire_custom}
                        options={context.wireOptions || []}
                        previous={previousLowerWireSelection}
                        onChange={(selection) => setWireSelection("lower", selection)}
                        disabled={false}
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setWiresExpanded(false)}
                        className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div data-tutorial-id="doctor-ortho-elastics">
                <InlineRow label="Elastics">
                  <select
                    aria-label="Elastics this visit"
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    value={form.elastics_status}
                    onChange={(e) => setField("elastics_status", e.target.value)}
                  >
                    {ORTHO_ELASTICS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </InlineRow>
                {["new_prescription", "other"].includes(form.elastics_status) ? (
                  <input
                    aria-label="Elastics details"
                    className="mb-2 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    value={form.elastics_detail}
                    onChange={(e) => setField("elastics_detail", e.target.value)}
                    placeholder={form.elastics_status === "new_prescription" ? "Configuration & wear (e.g. Class II R 24/7)" : "Specify..."}
                  />
                ) : null}
              </div>

              <div data-tutorial-id="doctor-ortho-oh-status">
                <InlineRow label="Oral hygiene">
                  <InlineSegmented
                    value={form.oh_status}
                    options={[
                      { value: "good", label: "Good" },
                      { value: "fair", label: "Fair" },
                      { value: "poor", label: "Poor" },
                    ]}
                    onChange={(value) => setField("oh_status", value)}
                  />
                </InlineRow>
              </div>

              <div data-tutorial-id="doctor-ortho-photos">
                <InlineRow label="Photos">
                  <button type="button" onClick={() => cameraInputRef.current?.click()} className="inline-flex h-9 items-center justify-center rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700">
                    Take photo
                  </button>
                  <button type="button" onClick={() => uploadInputRef.current?.click()} className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                    Upload
                  </button>
                </InlineRow>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={addPhotos} className="sr-only" />
                <input ref={uploadInputRef} data-testid="doctor-ortho-photo-upload" type="file" multiple accept="image/*" onChange={addPhotos} className="sr-only" />
                <OrthoPhotoSelectionStatus
                  file={form.photoFiles?.[0]}
                  count={form.photoNames.length}
                  fallback="Optional"
                />
              </div>
            </section>

            <section className="mt-3 border-t border-slate-200 pt-3" data-testid="doctor-ortho-bonding-next-visit" data-tutorial-id="doctor-ortho-bonding-next-visit">
              <p className="flex items-center gap-2 border-b border-slate-100 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <ClipboardList className="h-3.5 w-3.5" />
                Next visit
              </p>
              <div className="pt-1" data-tutorial-id="doctor-ortho-next-visit-interval">
                <InlineRow label="Come back in">
                  <InlineSegmented
                    value={String(form.next_visit_interval_weeks)}
                    options={[4, 5, 6, 8].map((n) => ({ value: String(n), label: `${n}w` }))}
                    onChange={(value) => setField("next_visit_interval_weeks", Number(value))}
                  />
                </InlineRow>
              </div>
              <div className="pt-2" data-tutorial-id="doctor-ortho-next-visit-goal">
                <textarea
                  aria-label="Notes and goal for next time"
                  className="min-h-[72px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  value={form.next_visit_goal}
                  onChange={(e) => setField("next_visit_goal", e.target.value)}
                  placeholder="Notes & goal for next time..."
                />
              </div>
            </section>
          </div>
        ) : visitType === "adjustment" ? null : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div data-tutorial-id="doctor-ortho-left-column">
          {visitType === "bonding" || visitType === "adjustment" ? (
            <div data-tutorial-id={visitType === "bonding" ? "doctor-ortho-bonding-arches" : "doctor-ortho-adjustment-arches"}>
              <InlineRow label={visitType === "bonding" ? "Arch bonded" : "Arch treated"}>
                <InlineToggle active={form.arch_bonded_upper} onClick={() => setField("arch_bonded_upper", !form.arch_bonded_upper)}>Upper</InlineToggle>
                <InlineToggle active={form.arch_bonded_lower} onClick={() => setField("arch_bonded_lower", !form.arch_bonded_lower)}>Lower</InlineToggle>
              </InlineRow>
              {visitType === "adjustment" && latest ? (
                <p className="pb-1 text-[11px] text-slate-400">Carried from last visit — confirm before saving.</p>
              ) : null}
            </div>
          ) : null}
          <div data-tutorial-id="doctor-ortho-oh-status">
            <InlineRow label="OH status">
              <InlineSegmented
                value={form.oh_status}
                options={[
                  { value: "good", label: "Good" },
                  { value: "fair", label: "Fair" },
                  { value: "poor", label: "Poor" },
                ]}
                onChange={(value) => setField("oh_status", value)}
                disabled={false}
              />
            </InlineRow>
          </div>
          <div data-tutorial-id="doctor-ortho-next-visit-interval">
            <InlineRow label={visitType === "retention" ? "Next check interval" : "Next visit interval"}>
              <InlineSegmented
                value={String(form.next_visit_interval_weeks)}
                options={[4, 5, 6, 8].map((n) => ({ value: String(n), label: `${n}w` }))}
                onChange={(value) => setField("next_visit_interval_weeks", Number(value))}
                disabled={false}
              />
            </InlineRow>
          </div>
          <div data-tutorial-id="doctor-ortho-photos">
            <InlineRow label={requiresPhotos ? "Final photos required" : "Photos"}>
              <button type="button" onClick={() => cameraInputRef.current?.click()} className="inline-flex h-9 items-center justify-center rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700">
                Take photo
              </button>
              <button type="button" onClick={() => uploadInputRef.current?.click()} className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Upload
              </button>
            </InlineRow>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={addPhotos} className="sr-only" />
            <input ref={uploadInputRef} data-testid="doctor-ortho-photo-upload" type="file" multiple accept="image/*" onChange={addPhotos} className="sr-only" />
            <OrthoPhotoSelectionStatus
              file={form.photoFiles?.[0]}
              count={form.photoNames.length}
              fallback={requiresPhotos ? "Required" : "Optional"}
            />
          </div>
          <div data-tutorial-id="doctor-ortho-notes" className="pt-2">
            <span className="mb-1 block text-sm text-slate-600">Notes</span>
            <textarea
              className="min-h-[84px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder={`${title} notes...`}
            />
          </div>
        </div>
        <div className="space-y-2.5" data-tutorial-id="doctor-ortho-right-column">
        {visitType === "debonding" ? (
          <div data-tutorial-id="doctor-ortho-final-wire-retainer-notes" className="md:col-span-2">
          <LockableOrthoSection title="Final wire / retainer notes" locked={false} edited={false} onEdit={null} onReset={null}>
            <div className="grid grid-cols-1 gap-2">
              <OrthoWireGaugeSelect
                label="Upper"
                material={form.upper_wire_material}
                size={form.upper_wire_size}
                custom={form.upper_wire_custom}
                options={context.wireOptions || []}
                previous={previousUpperWireSelection}
                onChange={(selection) => setWireSelection("upper", selection)}
                disabled={false}
              />
              <OrthoWireGaugeSelect
                label="Lower"
                material={form.lower_wire_material}
                size={form.lower_wire_size}
                custom={form.lower_wire_custom}
                options={context.wireOptions || []}
                previous={previousLowerWireSelection}
                onChange={(selection) => setWireSelection("lower", selection)}
                disabled={false}
              />
            </div>
          </LockableOrthoSection>
          </div>
        ) : (
          <div data-tutorial-id="doctor-ortho-wires" className={wiresExpanded ? "md:col-span-2" : undefined}>
          <CollapsibleOrthoSection
            title="Wires changed today"
            hint={wiresExpanded ? null : (latest ? "Off — keeps last visit's wires." : "Off — no wire change recorded.")}
            expanded={wiresExpanded}
            onToggle={() => setWiresExpanded((v) => !v)}
          >
            {suggestedWire ? (
              <button
                type="button"
                onClick={applySuggestedWire}
                className="mb-2 inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100"
              >
                {latest ? "Next wire" : "Starting wire"}: {suggestedWire.display_name} · tap to apply to both arches
              </button>
            ) : (
              latest ? (
                <p className="mb-2 text-xs text-slate-500">Already at the last wire in the sequence — pick manually if changing.</p>
              ) : null
            )}
            <div className="grid grid-cols-1 gap-2">
              <OrthoWireGaugeSelect
                label="Upper"
                material={form.upper_wire_material}
                size={form.upper_wire_size}
                custom={form.upper_wire_custom}
                options={context.wireOptions || []}
                previous={previousUpperWireSelection}
                onChange={(selection) => setWireSelection("upper", selection)}
                disabled={false}
              />
              <OrthoWireGaugeSelect
                label="Lower"
                material={form.lower_wire_material}
                size={form.lower_wire_size}
                custom={form.lower_wire_custom}
                options={context.wireOptions || []}
                previous={previousLowerWireSelection}
                onChange={(selection) => setWireSelection("lower", selection)}
                disabled={false}
              />
            </div>
          </CollapsibleOrthoSection>
          </div>
        )}

        <div data-tutorial-id="doctor-ortho-elastics" className={elasticsExpanded ? "md:col-span-2" : undefined}>
        <CollapsibleOrthoSection
          title="Elastics changed today"
          hint={elasticsExpanded ? null : `Currently wearing: ${currentWearing || "None"}`}
          expanded={elasticsExpanded}
          onToggle={() => setElasticsExpanded((v) => !v)}
        >
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_1fr]">
            <select
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              value={form.elastics_status}
              onChange={(e) => setField("elastics_status", e.target.value)}
            >
              {ORTHO_ELASTICS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {["new_prescription", "other"].includes(form.elastics_status) ? (
              <input
                className="h-10 min-w-0 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                value={form.elastics_detail}
                onChange={(e) => setField("elastics_detail", e.target.value)}
                placeholder={form.elastics_status === "new_prescription" ? "Configuration & wear (e.g. Class II R 24/7)" : "Specify..."}
              />
            ) : null}
          </div>
        </CollapsibleOrthoSection>
        </div>

        </div>
        </div>
        )}

        {c.ready_for_debonding ? (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <Flag className="mr-1.5 inline h-4 w-4" />
            This case is scheduled for debonding. The next ortho visit will be recorded as debonding.
          </div>
        ) : null}
          </div>

          <div className={`flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 ${visitType === "adjustment" ? "px-3 py-2.5" : "px-4 py-3"}`}>
            <div className="flex min-w-0 items-center gap-2">
              {visitType === "debonding" && canUndoConversion ? (
                <button
                  type="button"
                  onClick={requestUndoConversion}
                  disabled={undoingConversion || submitting || context.loading}
                  className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                  data-testid="doctor-ortho-undo-debonding"
                  aria-label="Undo Debonding and return to Adjustment"
                  title="Cancel today's Debonding draft and return this appointment to Adjustment"
                >
                  {undoingConversion ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Undo Debonding
                </button>
              ) : null}
              {treatmentStatusBlock}
              {visitType === "adjustment" && !currentAppointmentVisit?.ortho_visit?.id ? (
                <button
                  type="button"
                  onClick={() => {
                    setNoAdjustmentReason("bracket_repair");
                    setNoAdjustmentDialog(true);
                  }}
                  disabled={submitting || noAdjustmentSubmitting || context.loading}
                  className="inline-flex h-8 shrink-0 items-center rounded-lg border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="doctor-ortho-no-adjustment"
                >
                  No adjustment today
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting || noAdjustmentSubmitting || context.loading}
              className={`inline-flex shrink-0 items-center rounded-lg bg-teal-600 font-semibold text-white hover:bg-teal-700 disabled:opacity-50 ${visitType === "adjustment" ? "h-9 gap-1.5 px-3 text-xs" : "h-10 gap-2 px-4 text-sm"}`}
              data-testid="doctor-ortho-inline-save"
              data-tutorial-id="doctor-ortho-save-visit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {visitType === "adjustment" ? "Save adjustment" : `Save ${labelize(visitType)}`}
            </button>
          </div>
          {debondingModal}
          {noAdjustmentModal}
        </>
      ) : null}
    </section>
  );
}

function OrthoDebondingTreatmentStatus({ flagged, onFlag, onUnflag, compact = false }) {
  return (
    <div data-tutorial-id="doctor-ortho-debonding-status">
      {compact ? null : <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Treatment status</p>}
      <button
        type="button"
        onClick={flagged ? onUnflag : onFlag}
        data-tutorial-id={flagged ? "doctor-ortho-cancel-debonding" : "doctor-ortho-schedule-debonding"}
        className={`inline-flex items-center rounded-lg border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 ${compact ? "h-8 gap-1.5 px-2.5 text-xs" : "h-9 gap-2 px-3 text-sm"}`}
      >
        {flagged ? <Undo2 className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}
        {flagged ? "Cancel debonding schedule" : "Schedule for removal"}
      </button>
    </div>
  );
}

function OrthoDebondingFlagModal({ timing, onTimingChange, submitting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" data-tutorial-id="doctor-ortho-debonding-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-950">Schedule this case for removal?</h3>
        <p className="mt-2 text-sm text-slate-600">
          Pick when the braces come off. Choosing today records an editable Debonding draft immediately, so Checkout can collect the final balance without a second doctor save.
        </p>
        <div className="mt-4 space-y-2">
          <p className="text-sm font-semibold text-slate-800">When should debonding happen?</p>
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
            <input
              type="radio"
              className="mt-1"
              checked={timing === "today"}
              onChange={() => onTimingChange("today")}
            />
            <span>
              <span className="block font-semibold text-slate-900">Today — remove during this visit</span>
              <span className="text-xs text-slate-500">This appointment becomes Debonding Day and its editable draft is saved now.</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
            <input
              type="radio"
              className="mt-1"
              checked={timing === "next_regular"}
              onChange={() => onTimingChange("next_regular")}
            />
            <span>
              <span className="block font-semibold text-slate-900">At the next regular visit</span>
              <span className="text-xs text-slate-500">The next existing or booked ortho appointment becomes debonding.</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
            <input
              type="radio"
              className="mt-1"
              checked={timing === "schedule_sooner"}
              onChange={() => onTimingChange("schedule_sooner")}
            />
            <span>
              <span className="block font-semibold text-slate-900">Schedule a sooner appointment</span>
              <span className="text-xs text-slate-500">Checkout will nudge front desk to book debonding sooner.</span>
            </span>
          </label>
        </div>
        <p className="mt-4 text-xs text-slate-500">You can undo this from the ortho panel if needed.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={submitting} className="inline-flex h-9 items-center gap-2 rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50" data-testid="doctor-ortho-debonding-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function OrthoDebondingUnflagModal({ reason, onReasonChange, submitting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" data-tutorial-id="doctor-ortho-cancel-debonding-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-950">Cancel debonding schedule?</h3>
        <p className="mt-2 text-sm text-slate-600">
          This case will return to active adjustment treatment. Any scheduled debonding appointment will be reverted to adjustment.
        </p>
        <label className="mt-4 block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Reason optional</span>
          <input
            className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. continue active treatment"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={submitting} className="inline-flex h-9 items-center gap-2 rounded-lg bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50" data-testid="doctor-ortho-cancel-debonding-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function InlineLastVisitSummary({ latest }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="doctor-ortho-last-visit" data-tutorial-id="doctor-ortho-last-visit">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Last visit</p>
        <p className="text-xs font-semibold text-slate-600">{orthoEventLabel(latest)}</p>
      </div>
      <p className="mt-1 text-xs text-slate-600">
        {[
          Array.isArray(latest.procedures_this_visit) && latest.procedures_this_visit.length ? `Procedures: ${latest.procedures_this_visit.map(labelize).join(", ")}` : null,
          orthoTimelineWireSideText(latest, "upper") ? `Upper ${orthoTimelineWireSideText(latest, "upper")}` : null,
          orthoTimelineWireSideText(latest, "lower") ? `Lower ${orthoTimelineWireSideText(latest, "lower")}` : null,
          latest.elastics_prescribed ? `Elastics: ${latest.elastics_prescribed}` : null,
          latest.oh_status ? `OH ${labelize(latest.oh_status)}` : null,
        ].filter(Boolean).join(" · ") || "No appliance details recorded."}
      </p>
      {latest.notes ? <p className="mt-1 text-xs italic text-slate-500">"{latest.notes}"</p> : null}
    </div>
  );
}

function InlineField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// Left-column checklist row: label on the left, control on the right, one row
// each with a hairline divider. The control sits in a fixed-width track whose
// buttons split it into equal columns, so every row's control group lines up to
// the same left and right edges regardless of how many buttons it has.
function InlineRow({ label, children, compact = false }) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-slate-100 last:border-b-0 ${compact ? "py-1.5" : "py-2"}`}>
      <span className="text-sm text-slate-600">{label}</span>
      <div className={`grid max-w-full grid-flow-col auto-cols-fr gap-1.5 ${compact ? "w-48" : "w-60"}`}>{children}</div>
    </div>
  );
}

function OrthoPhotoSelectionStatus({ file, count, fallback }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL?.(objectUrl);
  }, [file]);

  return (
    <div className="flex min-h-6 items-center justify-end gap-1.5 pb-1">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          aria-hidden="true"
          className="h-6 w-6 shrink-0 rounded border border-slate-200 object-cover"
          data-testid="doctor-ortho-photo-thumbnail"
        />
      ) : null}
      <p className="text-[11px] italic text-slate-400">{count ? `${count} selected` : fallback}</p>
    </div>
  );
}

function LockableOrthoSection({ title, locked, edited, onEdit, onReset, children }) {
  return (
    <section className={`rounded-xl border p-3 ${locked ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{title}</p>
          {locked ? <Lock className="h-3.5 w-3.5 text-slate-400" /> : null}
          {edited ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-800">Edited</span> : null}
        </div>
        {onEdit || onReset ? (
          locked ? (
            <button type="button" onClick={onEdit} className="text-xs font-semibold text-teal-700 hover:text-teal-900">
              Edit
            </button>
          ) : (
            <button type="button" onClick={onReset} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          )
        ) : null}
      </div>
      {children}
    </section>
  );
}

function CollapsibleOrthoSection({ title, hint, expanded, onToggle, children, showToggleBadge = true }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {hint ? <p className="mt-0.5 truncate text-xs text-slate-500">{hint}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {showToggleBadge ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] ${expanded ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-500"}`}
            >
              {expanded ? "Yes" : "No"}
            </span>
          ) : null}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>
      {expanded ? <div className="border-t border-slate-100 px-3 py-3">{children}</div> : null}
    </section>
  );
}

function AdjustmentCurrentStateRow({ label, summary, changed = false, expanded, onChange, children, testId }) {
  return (
    <section className="border-b border-slate-100 py-2" data-testid={testId} data-tutorial-id={testId}>
      <button
        type="button"
        onClick={onChange}
        className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left hover:border-teal-200 hover:bg-teal-50/40"
        aria-expanded={expanded}
      >
        <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{summary}</span>
        {changed ? <ChangedIndicator /> : null}
        <span className="shrink-0 text-xs font-semibold text-teal-700">{expanded ? "done" : "change"}</span>
      </button>
      {expanded ? <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">{children}</div> : null}
    </section>
  );
}

function ChangedIndicator({ label = "Changed" }) {
  return (
    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-800">
      {label}
    </span>
  );
}

function QuietOrthoDisclosure({ icon: Icon, title, hint, expanded, onToggle, children, testId }) {
  return (
    <section className="border-b border-slate-100 last:border-b-0" data-testid={testId} data-tutorial-id={testId}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 py-3 text-left"
        aria-expanded={expanded}
      >
        <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-800">
          {Icon ? <Icon className="h-4 w-4 shrink-0 text-slate-400" /> : null}
          {title}
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
          {hint}
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </span>
      </button>
      {expanded ? <div className="border-t border-slate-100 pb-3 pt-3">{children}</div> : null}
    </section>
  );
}

function OrthoNotesTimeline({ visits, doctors }) {
  if (!Array.isArray(visits) || visits.length === 0) {
    return <p className="text-sm text-slate-500">No past Ortho updates yet.</p>;
  }

  return (
    <div className="space-y-0" data-testid="doctor-ortho-notes-timeline-list">
      {visits.map((visitRow, index) => {
        const ov = visitRow?.ortho_visit || visitRow || {};
        const previousOv = visits[index + 1]?.ortho_visit || visits[index + 1] || null;
        const performedBy = visitRow?.procedure_visit?.performed_by || visitRow?.performed_by || null;
        const doctor = (doctors || []).find((candidate) => candidate?.id === performedBy) || null;
        const doctorName = formatDoctorName(doctor, "Unassigned doctor");
        const snapshot = orthoTimelineSnapshot(ov);
        const previousSnapshot = orthoTimelineSnapshot(previousOv);
        const stateRows = [
          { key: "upper-wire", label: "Upper wire", value: snapshot.upperWire, previous: previousSnapshot.upperWire },
          { key: "lower-wire", label: "Lower wire", value: snapshot.lowerWire, previous: previousSnapshot.lowerWire },
          { key: "elastics", label: "Elastics", value: snapshot.elastics, previous: previousSnapshot.elastics },
          { key: "oral-hygiene", label: "Oral hygiene", value: snapshot.oh, previous: previousSnapshot.oh },
        ].filter((row) => row.value);
        const paymentDetails = [
          Number.isFinite(Number(visitRow?.charge_centavos)) && Number(visitRow.charge_centavos) > 0
            ? `Charge ${formatMoney(visitRow.charge_centavos, "PHP")}`
            : null,
          Number.isFinite(Number(visitRow?.collected_centavos)) && Number(visitRow.collected_centavos) > 0
            ? `Collected ${formatMoney(visitRow.collected_centavos, "PHP")}${Number(visitRow?.ar_collected_centavos || 0) > 0 ? ` (AR ${formatMoney(visitRow.ar_collected_centavos, "PHP")})` : ""}`
            : null,
          visitRow?.payment_status && visitRow.payment_status !== "No charge"
            ? visitRow.payment_status
            : null,
        ].filter(Boolean).join(" · ");
        const note = String(ov.notes || "").trim();
        const goal = String(ov.next_visit_goal || "").trim();
        const photoCount = Math.max(
          Number(visitRow?.photo_count || 0),
          Array.isArray(ov.photos) ? ov.photos.length : 0,
        );
        const occurredAt = visitRow?.procedure_visit?.completed_at || visitRow?.completed_at || ov.created_at;

        return (
          <article key={ov.id || `${occurredAt}-${index}`} className="grid grid-cols-[40px_1fr] gap-3" data-testid="doctor-ortho-note-entry">
            <div className="relative flex justify-center">
              <span className="relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-teal-50 text-xs font-bold text-teal-700">
                {doctorInitials(doctorName)}
              </span>
              {index < visits.length - 1 ? <span className="absolute bottom-0 top-9 w-px bg-slate-200" /> : null}
            </div>
            <div className="border-b border-slate-100 pb-4 last:border-b-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm font-semibold text-slate-900">{ov.event_label || orthoEventLabel(ov)}</span>
                <span className="text-sm font-semibold text-teal-700">{doctorName}</span>
                <time className="text-xs text-slate-400" dateTime={occurredAt || undefined}>{formatOrthoTimelineDate(occurredAt)}</time>
              </div>
              {ov.non_adjustment_reason ? (
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="doctor-ortho-note-reason">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Reason</div>
                  <div className="mt-0.5 text-sm font-medium text-slate-800">{labelize(ov.non_adjustment_reason)}</div>
                </div>
              ) : null}
              {stateRows.length ? (
                <dl className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-100 bg-white px-3" data-testid="doctor-ortho-note-clinical-state">
                  {stateRows.map((row) => (
                    <OrthoTimelineStateRow
                      key={row.key}
                      label={row.label}
                      value={row.value}
                      previous={row.previous}
                      testId={`doctor-ortho-note-${row.key}`}
                    />
                  ))}
                </dl>
              ) : null}
              {note ? (
                <div className="mt-2 rounded-lg bg-teal-50/70 px-3 py-2 text-sm leading-5 text-slate-700" data-testid="doctor-ortho-note-text">
                  <span className="font-semibold text-teal-800">Note</span>
                  <p className="mt-0.5 whitespace-pre-wrap">{note}</p>
                </div>
              ) : null}
              {goal ? (
                <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-700" data-testid="doctor-ortho-note-goal">
                  <span className="font-semibold text-slate-800">Next-visit goal</span>
                  <p className="mt-0.5 whitespace-pre-wrap">{goal}</p>
                </div>
              ) : null}
              {(paymentDetails || photoCount > 0 || (Array.isArray(ov.procedures_this_visit) && ov.procedures_this_visit.length)) ? (
                <div className="mt-2 space-y-1 text-xs text-slate-500" data-testid="doctor-ortho-note-supporting-details">
                  {paymentDetails ? <div><span className="font-semibold text-slate-600">Payment:</span> {paymentDetails}</div> : null}
                  {photoCount > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      <span><span className="font-semibold text-slate-600">Photos:</span> {photoCount}</span>
                      <OrthoVisitPhotoPreview
                        photos={ov.photos}
                        visitLabel={ov.event_label || orthoEventLabel(ov)}
                      />
                    </div>
                  ) : null}
                  {Array.isArray(ov.procedures_this_visit) && ov.procedures_this_visit.length ? (
                    <div><span className="font-semibold text-slate-600">Procedures:</span> {ov.procedures_this_visit.map(labelize).join(", ")}</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function OrthoTimelineStateRow({ label, value, previous, testId }) {
  const change = orthoTimelineChangeMeta(value, previous);
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-2 py-2 text-xs" data-testid={testId}>
      <dt className="font-semibold text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-700">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-slate-800">{value}</span>
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.06em] ${change.tone}`}>
            {change.label}
          </span>
        </div>
        {change.previous ? <div className="mt-0.5 text-[11px] text-slate-400">Previously {change.previous}</div> : null}
      </dd>
    </div>
  );
}

function hasOrthoTimelineContent(visit) {
  if (!visit) return false;
  const snapshot = orthoTimelineSnapshot(visit);
  return Boolean(
    snapshot.wire
      || snapshot.elastics
      || snapshot.oh
      || snapshot.note
      || snapshot.goal
      || visit.event_label
      || visit.visit_type
      || visit.non_adjustment_reason
      || Number(visit.charge_centavos || 0) > 0
      || Number(visit.photo_count || 0) > 0
  );
}

function orthoTimelineSnapshot(visit) {
  if (!visit) return { wire: "", upperWire: "", lowerWire: "", elastics: "", oh: "", note: "", goal: "" };
  const upperWire = orthoTimelineWireSideText(visit, "upper");
  const lowerWire = orthoTimelineWireSideText(visit, "lower");
  return {
    wire: [upperWire ? `U ${upperWire}` : null, lowerWire ? `L ${lowerWire}` : null].filter(Boolean).join(" · ") || orthoTimelineWireText(visit),
    upperWire,
    lowerWire,
    elastics: orthoTimelineElasticsText(visit),
    oh: visit.oh_status ? labelize(visit.oh_status) : "",
    note: String(visit.notes || "").trim(),
    goal: String(visit.next_visit_goal || "").trim(),
  };
}

function orthoTimelineWireSideText(visit, side) {
  if (!visit) return "";
  const structured = formatWireSelection(wireSelectionFromVisit(visit, side));
  if (structured) return structured;
  return String(visit[`${side}_wire_label_snapshot`] || "").replace(/^Other:\s*/i, "").trim();
}

function orthoTimelineWireText(visit) {
  if (!visit) return "";
  const labels = [
    orthoTimelineWireSideText(visit, "upper") ? `U ${orthoTimelineWireSideText(visit, "upper")}` : null,
    orthoTimelineWireSideText(visit, "lower") ? `L ${orthoTimelineWireSideText(visit, "lower")}` : null,
  ].filter(Boolean);
  if (labels.length) return labels.join(" · ");
  const legacy = String(visit.wire_elastics_info || visit.wire_elastic_info?.text || "").trim();
  return legacy.replace(/\s*;?\s*Elastics:\s*.*$/i, "").trim();
}

function orthoTimelineElasticsText(visit) {
  if (!visit) return "";
  const explicit = String(visit.elastics_detail || visit.elastics_prescribed || "").trim();
  if (explicit) {
    const normalized = explicit.toLowerCase();
    if (["none", "none this visit", "discontinued"].includes(normalized)) return normalized === "discontinued" ? "Discontinued" : "None";
    return explicit;
  }
  const legacy = String(visit.wire_elastics_info || visit.wire_elastic_info?.text || "");
  const match = legacy.match(/Elastics:\s*(.+)$/i);
  return match?.[1]?.trim() || "";
}

function orthoTimelineChangeMeta(current, previous) {
  if (!previous) {
    return { label: "Recorded", previous: "", tone: "bg-slate-100 text-slate-500" };
  }
  if (current === previous) {
    return { label: "Carried forward", previous: "", tone: "bg-slate-100 text-slate-500" };
  }
  return { label: "Changed", previous, tone: "bg-amber-100 text-amber-800" };
}

function doctorInitials(value) {
  const clean = String(value || "").replace(/^Dr\.\s*/i, "").trim();
  const pieces = clean.split(/\s+/).filter(Boolean);
  if (!pieces.length) return "—";
  return `${pieces[0]?.[0] || ""}${pieces.length > 1 ? pieces[pieces.length - 1]?.[0] || "" : ""}`.toUpperCase();
}

function formatOrthoTimelineDate(value) {
  if (!value) return "Date not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date not recorded";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function InlineToggle({ active, onClick, children, disabled = false, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg border font-semibold disabled:cursor-not-allowed disabled:opacity-70 ${compact ? "h-8 px-2 text-xs" : "h-9 px-3 text-sm"} ${active ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
    >
      {children}
    </button>
  );
}

function InlineSegmented({ value, options, onChange, disabled = false, compact = false }) {
  return (
    <>
      {options.map((option) => (
        <InlineToggle compact={compact} key={option.value} active={value === option.value} disabled={disabled} onClick={() => onChange(option.value)}>
          {option.label}
        </InlineToggle>
      ))}
    </>
  );
}

function wireSelectionFromForm(form, side) {
  return {
    material: form?.[`${side}_wire_material`] || "",
    size: form?.[`${side}_wire_size`] || "",
    custom: form?.[`${side}_wire_custom`] || "",
    optionId: form?.[`${side}_wire_option_id`] || "",
  };
}

function buildOrthoVisitFormDefaults(
  visitType,
  latest,
  previousVisitRows = [],
  wireOptions = [],
  currentDraft = null,
) {
  // A visit can predate one of the structured fields. Merge the most recent
  // saved value for each field rather than falling back to a new default. This
  // keeps the chairside form faithful to the patient's last recorded state,
  // including imported and older cases.
  const history = [latest, ...(previousVisitRows || []).map((row) => row?.ortho_visit)]
    .filter(Boolean)
    .filter((visit, index, rows) => rows.findIndex((candidate) => candidate.id === visit.id) === index);
  const firstWith = (predicate) => history.find(predicate) || null;
  const state = {
    arch: latest || null,
    upperWire: firstWith((visit) => visit.upper_wire_material || visit.upper_wire_size || visit.upper_wire_custom || visit.upper_wire_option_id || visit.upper_wire_other_text || visit.upper_wire_label_snapshot),
    lowerWire: firstWith((visit) => visit.lower_wire_material || visit.lower_wire_size || visit.lower_wire_custom || visit.lower_wire_option_id || visit.lower_wire_other_text || visit.lower_wire_label_snapshot),
    elastics: firstWith((visit) => visit.elastics_status || visit.elastics_detail || visit.elastics_prescribed),
    oh: firstWith((visit) => visit.oh_status),
  };
  const upperWire = state.upperWire || latest || {};
  const lowerWire = state.lowerWire || latest || {};
  const elasticsVisit = state.elastics || latest || {};
  const ohVisit = state.oh || latest || {};
  const upperSelection = wireSelectionFromVisit(upperWire, "upper", wireOptions);
  const lowerSelection = wireSelectionFromVisit(lowerWire, "lower", wireOptions);
  const previousActiveElastics = getLastActiveElasticsText(elasticsVisit);
  const elasticsStatus = visitType === "adjustment" && (latest || history.length)
    ? (previousActiveElastics && previousActiveElastics !== "None" ? "continuing_same" : "none_this_visit")
    : normalizeElasticsStatus(elasticsVisit);
  const historicalDefaults = {
    arch_bonded_upper: latest ? !!latest.arch_bonded_upper : visitType === "bonding",
    arch_bonded_lower: latest ? !!latest.arch_bonded_lower : visitType === "bonding",
    // Routine tightening is the adjustment itself, not an extra procedure.
    // Keep this empty until the dentist opens Procedures and records an add-on.
    procedures_this_visit: [],
    procedures_other_text: "",
    upper_wire_option_id: upperSelection.optionId || "",
    lower_wire_option_id: lowerSelection.optionId || "",
    upper_wire_other_text: "",
    lower_wire_other_text: "",
    upper_wire_material: upperSelection.material,
    upper_wire_size: upperSelection.size,
    upper_wire_custom: upperSelection.custom,
    lower_wire_material: lowerSelection.material,
    lower_wire_size: lowerSelection.size,
    lower_wire_custom: lowerSelection.custom,
    elastics_status: elasticsStatus,
    elastics_detail: elasticsStatus === "continuing_same"
      ? (previousActiveElastics === "None" ? "" : previousActiveElastics)
      : (elasticsVisit.elastics_detail || elasticDetailFromLegacy(elasticsVisit, elasticsStatus)),
    elastics_prescribed: elasticsVisit.elastics_prescribed || "None this visit",
    oh_status: ohVisit.oh_status || "good",
    next_visit_interval_weeks: latest?.next_visit_interval_weeks || 6,
    next_visit_goal: "",
    notes: "",
    photoNames: [],
    photoFiles: [],
    savedPhotos: [],
  };

  if (!currentDraft?.id) return historicalDefaults;

  const draftPhotos = Array.isArray(currentDraft.photos) ? currentDraft.photos : [];
  const draftUpperWire = wireSelectionFromVisit(currentDraft, "upper", wireOptions);
  const draftLowerWire = wireSelectionFromVisit(currentDraft, "lower", wireOptions);
  const draftElasticsStatus = normalizeElasticsStatus(currentDraft);

  return {
    ...historicalDefaults,
    arch_bonded_upper: !!currentDraft.arch_bonded_upper,
    arch_bonded_lower: !!currentDraft.arch_bonded_lower,
    procedures_this_visit: normalizeOrthoProcedures(currentDraft.procedures_this_visit),
    procedures_other_text: currentDraft.procedures_other_text || "",
    upper_wire_option_id: draftUpperWire.optionId || "",
    lower_wire_option_id: draftLowerWire.optionId || "",
    upper_wire_other_text: "",
    lower_wire_other_text: "",
    upper_wire_material: draftUpperWire.material,
    upper_wire_size: draftUpperWire.size,
    upper_wire_custom: draftUpperWire.custom,
    lower_wire_material: draftLowerWire.material,
    lower_wire_size: draftLowerWire.size,
    lower_wire_custom: draftLowerWire.custom,
    elastics_status: draftElasticsStatus,
    elastics_detail: currentDraft.elastics_detail
      || elasticDetailFromLegacy(currentDraft, draftElasticsStatus),
    elastics_prescribed: currentDraft.elastics_prescribed || "None this visit",
    oh_status: currentDraft.oh_status || historicalDefaults.oh_status,
    next_visit_interval_weeks: currentDraft.next_visit_interval_weeks
      || historicalDefaults.next_visit_interval_weeks,
    next_visit_goal: currentDraft.next_visit_goal || "",
    notes: currentDraft.notes || "",
    photoNames: draftPhotos
      .map((photo) => photo?.name || photo?.original_filename || photo?.filename)
      .filter(Boolean),
    photoFiles: [],
    savedPhotos: draftPhotos,
  };
}

function normalizeOrthoProcedures(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeElasticsStatus(latest) {
  const status = latest?.elastics_status;
  if (ORTHO_ELASTICS_OPTIONS.some((option) => option.value === status)) return status;
  const legacy = String(latest?.elastics_prescribed || "").trim().toLowerCase();
  if (!legacy || legacy === "none" || legacy === "none this visit") return "none_this_visit";
  if (legacy === "discontinued") return "discontinued";
  return "continuing_same";
}

function elasticDetailFromLegacy(latest, status) {
  if (!latest?.elastics_prescribed) return "";
  if (status !== "continuing_same") return "";
  const value = String(latest.elastics_prescribed || "").trim();
  return ["none", "none this visit", "discontinued"].includes(value.toLowerCase()) ? "" : value;
}

function getLastActiveElasticsText(latest) {
  if (!latest) return "";
  const explicit = latest.elastics_detail || latest.elastics_prescribed || "";
  const normalized = String(explicit).trim();
  if (!normalized || ["none", "none this visit", "discontinued"].includes(normalized.toLowerCase())) return "None";
  return normalized;
}

function buildElasticsDisplayText(form, currentWearing) {
  const detail = String(form.elastics_detail || "").trim();
  switch (form.elastics_status) {
    case "continuing_same":
      return detail || currentWearing || "Continuing same";
    case "new_prescription":
      return detail || "New prescription";
    case "discontinued":
      return "Discontinued";
    case "other":
      return detail || "Other";
    case "none_this_visit":
    default:
      return "None this visit";
  }
}

function resetOrthoFormSection(form, baseForm, section) {
  const fields = {
    procedures: ["procedures_this_visit", "procedures_other_text"],
    wire: [
      "upper_wire_option_id", "lower_wire_option_id", "upper_wire_other_text", "lower_wire_other_text",
      "upper_wire_material", "upper_wire_size", "upper_wire_custom",
      "lower_wire_material", "lower_wire_size", "lower_wire_custom",
    ],
    elastics: ["elastics_status", "elastics_detail", "elastics_prescribed"],
    oh: ["oh_status"],
    interval: ["next_visit_interval_weeks"],
  }[section] || [];
  const next = { ...form };
  fields.forEach((field) => { next[field] = baseForm[field]; });
  return next;
}

function isOrthoFormSectionChanged(form, baseForm, section) {
  const fields = {
    procedures: ["procedures_this_visit", "procedures_other_text"],
    wire: [
      "upper_wire_option_id", "lower_wire_option_id", "upper_wire_other_text", "lower_wire_other_text",
      "upper_wire_material", "upper_wire_size", "upper_wire_custom",
      "lower_wire_material", "lower_wire_size", "lower_wire_custom",
    ],
    elastics: ["elastics_status", "elastics_detail", "elastics_prescribed"],
    oh: ["oh_status"],
    interval: ["next_visit_interval_weeks"],
  }[section] || [];
  return fields.some((field) => JSON.stringify(form[field] ?? null) !== JSON.stringify(baseForm[field] ?? null));
}

function orthoTodayLabel(context) {
  const row = context?.activeCase;
  const visits = activeOrthoVisitRows(row);
  const current = currentOrthoVisitRow(row, context?.visitType, context?.appointmentId);
  const adjustmentNumber = orthoAdjustmentNumber(current)
    || nextOrthoAdjustmentNumber(visits);
  const total = row?.case?.estimated_total_visits || row?.billing?.estimated_total_visits || "?";
  const visitType = context?.visitType || "adjustment";
  const eventLabel = orthoEventLabel({
    visit_type: visitType,
    adjustment_number: visitType === "adjustment" ? adjustmentNumber : null,
  });
  return `Ortho ${eventLabel}${visitType === "adjustment" ? ` of ${total}` : ""}`;
}

// Future Needs card — patient's Open standalone treatment_needs.
// Painting a tooth in Future Need mode appends a row here; the ✕
// button erases an Open need (whiteboard layer). Once a need gets
// scheduled / declined / completed elsewhere, it drops off this list.
function FutureNeedsCard({
  needs, loaded, currency, patient,
  onRemove, onUpdate, onUndo, onRedo, canUndo, canRedo,
  sessionFollowUpDays, setSessionFollowUpDays,
  onPropose,   // async (needId, bundleNeedIds[]) => planId | null
  autoProposeNeedId = null,   // parent asks to auto-open the gate on this need
  onAutoProposeConsumed,      // () => void — clear the parent's request
}) {
  const [removingId, setRemovingId] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  // "Propose as a plan" gate: which need is being proposed, and which of
  // the patient's OTHER open needs are ticked to bundle into the same plan
  // (pre-checked — a proposal is usually the whole treatment picture).
  const [proposing, setProposing] = useState(null);       // the need row
  const [bundleIds, setBundleIds] = useState(() => new Set());
  const [proposeBusy, setProposeBusy] = useState(false);

  function openPropose(need) {
    setProposing(need);
    setBundleIds(new Set(needs.filter((x) => x.id !== need.id).map((x) => x.id)));
  }
  function toggleBundle(id) {
    setBundleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  async function confirmPropose() {
    if (!proposing || !onPropose) return;
    setProposeBusy(true);
    try {
      const planId = await onPropose(proposing.id, Array.from(bundleIds));
      if (planId) setProposing(null);
    } finally {
      setProposeBusy(false);
    }
  }

  // Parent asked us to auto-open the gate on a freshly-painted multi-visit
  // need (mirrors the Today-chart prompt). Wait until that need is actually
  // in the list, open the modal once, then clear the parent's request.
  useEffect(() => {
    if (!autoProposeNeedId || proposing) return;
    const need = needs.find((x) => x.id === autoProposeNeedId);
    if (!need) return;
    openPropose(need);
    onAutoProposeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoProposeNeedId, needs]);

  async function handleRemove(id) {
    setRemovingId(id);
    await onRemove(id);
    setRemovingId(null);
  }

  // Doctor changed the session pick (e.g. "all of these in 2 weeks
  // instead of 1 week"). Stamp every existing Open need with the new
  // value. Best-effort, in parallel — Promise.all so the receptionist
  // doesn't have to wait through serial RPC round-trips.
  async function applyToAll() {
    if (!onUpdate) return;
    const target = sessionFollowUpDays;
    const targets = needs.filter(
      (n) => (n.follow_up_after_days ?? null) !== (target ?? null),
    );
    if (targets.length === 0) return;
    setBulkApplying(true);
    try {
      await Promise.all(
        targets.map((n) => onUpdate(n.id, { follow_up_after_days: target }))
      );
      toast.success(
        target == null
          ? `Cleared follow-up window on ${targets.length} need${targets.length === 1 ? "" : "s"}.`
          : `Set "${describeFollowUpDays(target)}" on ${targets.length} need${targets.length === 1 ? "" : "s"}.`,
      );
    } finally {
      setBulkApplying(false);
    }
  }

  async function handleSessionFollowUpPick(days) {
    setSessionFollowUpDays(days);
    if (!onUpdate || needs.length === 0) return;
    const targets = needs.filter(
      (n) => (n.follow_up_after_days ?? null) !== (days ?? null),
    );
    if (targets.length === 0) return;
    setBulkApplying(true);
    try {
      await Promise.all(
        targets.map((n) => onUpdate(n.id, { follow_up_after_days: days }))
      );
      toast.success(
        days == null
          ? `Cleared follow-up window on ${targets.length} need${targets.length === 1 ? "" : "s"}.`
          : `Doctor return window saved: ${describeFollowUpDays(days)}.`,
      );
    } finally {
      setBulkApplying(false);
    }
  }

  // Are any existing needs out of sync with the current session pick?
  // Drives the "Apply to all" button visibility. NULL session pick is
  // a real value (means "Doctor's choice"); compare with == null.
  const outOfSyncCount = useMemo(() => {
    return needs.filter((n) => (n.follow_up_after_days ?? null) !== (sessionFollowUpDays ?? null)).length;
  }, [needs, sessionFollowUpDays]);

  // Group by tooth (matches Today's Work + Tx Plan builder).
  // Non-tooth needs collect into "General" at the bottom.
  const grouped = useMemo(() => {
    const byTooth = new Map();
    for (const n of needs) {
      const key = n.tooth_number ? String(n.tooth_number) : "_general";
      if (!byTooth.has(key)) byTooth.set(key, []);
      byTooth.get(key).push(n);
    }
    const teethKeys = Array.from(byTooth.keys())
      .filter((k) => k !== "_general")
      .sort((a, b) => Number(a) - Number(b));
    const groups = teethKeys.map((k) => ({ tooth: k, items: byTooth.get(k) }));
    if (byTooth.has("_general")) groups.push({ tooth: null, items: byTooth.get("_general") });
    return groups;
  }, [needs]);

  // Per-need effective price = override OR catalog default. Discount
  // (if set) reduces it. Render BOTH gross and net at the totals row.
  const needGross = (n) => Number(n.effective_fee_centavos ?? n.default_fee_centavos ?? 0);
  const needDiscount = (n) => {
    const gross = needGross(n);
    if (n.discount_pct != null && Number(n.discount_pct) > 0) {
      return Math.round(gross * (Number(n.discount_pct) / 100));
    }
    if (n.discount_amount_centavos != null && Number(n.discount_amount_centavos) > 0) {
      return Number(n.discount_amount_centavos);
    }
    return 0;
  };
  const grossTotal    = needs.reduce((s, n) => s + needGross(n), 0);
  const discountTotal = needs.reduce((s, n) => s + needDiscount(n), 0);
  const netTotal      = Math.max(0, grossTotal - discountTotal);

  return (
    <section
      className="bg-white rounded-2xl shadow-sm"
      data-testid="doctor-future-needs-card"
      data-tutorial-id="charting-future-need-work"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Future Needs</h2>
          <span className="text-xs text-slate-400">· Open ({needs.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            data-testid="doctor-future-needs-undo"
            title="Undo (⌘Z / Ctrl+Z)"
            aria-label="Undo"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            data-testid="doctor-future-needs-redo"
            title="Redo (⌘⇧Z / Ctrl+Y)"
            aria-label="Redo"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Session-level "When should they come back?" picker. Doctor sets
          this once per visit; new paints inherit it. When changed AFTER
          some needs were painted, the "Apply to all" button surfaces a
          one-tap bulk update. NULL = Doctor's choice (booking dialog
          uses category default). */}
      <div className="px-4 pt-3 pb-2 border-b border-slate-100 bg-amber-50/40">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-900">
            When should they come back?
          </p>
          {needs.length > 0 && outOfSyncCount > 0 ? (
            <button
              type="button"
              onClick={applyToAll}
              disabled={bulkApplying || !onUpdate}
              data-testid="doctor-future-needs-apply-all"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 px-2 py-1 rounded"
            >
              {bulkApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Apply to all {outOfSyncCount} need{outOfSyncCount === 1 ? "" : "s"}
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="doctor-future-needs-window-chips">
          {SESSION_FOLLOW_UP_OPTIONS.map((opt) => {
            const active = (sessionFollowUpDays ?? null) === (opt.days ?? null);
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => handleSessionFollowUpPick(opt.days)}
                disabled={bulkApplying}
                data-testid={`doctor-future-needs-window-${opt.days ?? "default"}`}
                className={[
                  "px-2.5 py-1 text-[12px] rounded-full border transition-colors",
                  active
                    ? "bg-teal-600 border-teal-600 text-white font-semibold"
                    : "bg-white border-slate-200 text-slate-700 hover:border-teal-400",
                ].join(" ")}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-amber-900/70 mt-1.5">
          {sessionFollowUpDays == null
            ? "No preference set — the front desk will use the system default when booking."
            : `Open future needs are tagged "${describeFollowUpDays(sessionFollowUpDays)}". Front desk sees this in checkout.`}
        </p>
      </div>

      <div className="p-4">
        {!loaded ? (
          <div className="text-xs text-slate-400 inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : needs.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500">
              Pick a procedure from the palette and tap a tooth to flag it for a future visit.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {grouped.map(({ tooth, items: groupItems }) => {
              const groupNet = groupItems.reduce((s, n) => s + Math.max(0, needGross(n) - needDiscount(n)), 0);
              return (
                <li
                  key={tooth ?? "_general"}
                  className="border border-slate-200 rounded-lg bg-white overflow-hidden"
                  data-testid={`doctor-future-needs-group-${tooth ?? "general"}`}
                >
                  <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50/70 border-b border-slate-100 text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                    <span>
                      {tooth ? <>🦷 Tooth #{tooth}</> : "General"}
                      <span className="ml-1.5 text-slate-400 normal-case font-normal">· {groupItems.length} {groupItems.length === 1 ? "item" : "items"}</span>
                    </span>
                    <span className="text-slate-700 normal-case tabular-nums">{formatMoney(groupNet, currency)}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {groupItems.map((n) => (
                      <li
                        key={n.id}
                        className="px-3 py-2 flex flex-col gap-1.5 text-sm"
                        data-testid={`doctor-future-need-${n.id}`}
                        data-tutorial-id={n.tutorial_id || n.tutorialId || undefined}
                      >
                        <div className="min-w-0 flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                            style={{ background: n.color_hex || "#94a3b8" }}
                            aria-hidden="true"
                          />
                          <p className="text-slate-800 truncate">
                            {n.procedure_name || "Procedure"}
                            {n.surface ? <span className="text-slate-400"> · {n.surface}</span> : null}
                          </p>
                        </div>
                        <SurfaceCountSubline
                          surfaceCount={Number(n.surface_count ?? splitSurfaces(n.surface).length)}
                          unitPriceCentavos={Number(n.default_fee_centavos || 0)}
                          currency={currency}
                        />
                        <RowTimingControl
                          value={n.follow_up_after_days ?? null}
                          sessionDefault={sessionFollowUpDays ?? null}
                          onChange={onUpdate ? async (days) =>
                            onUpdate(n.id, { follow_up_after_days: days }) : null}
                          testid={`doctor-future-need-${n.id}-row-timing`}
                        />
                        <LineEditControls
                          grossCentavos={needGross(n)}
                          currency={currency}
                          discountPct={n.discount_pct ?? null}
                          discountAmountCentavos={n.discount_amount_centavos ?? null}
                          discountReason={n.discount_reason ?? null}
                          notes={n.notes ?? null}
                          presets={buildLinePresets({
                            patientSeniorPwdTag: patient?.senior_pwd_tag ?? null,
                          })}
                          onPriceChange={onUpdate ? async (cents) => onUpdate(n.id, { fee_centavos_override: cents }) : null}
                          onDiscountChange={onUpdate ? async ({ pct, amount, reason }) => onUpdate(n.id, {
                            discount_pct:             pct,
                            discount_amount_centavos: amount,
                            discount_reason:          reason,
                          }) : null}
                          onNoteChange={onUpdate ? async (note) => onUpdate(n.id, { notes: note }) : null}
                          onRemove={() => handleRemove(n.id)}
                          testIdPrefix={`doctor-future-need-${n.id}`}
                        />
                        {onPropose ? (
                          <button
                            type="button"
                            onClick={() => openPropose(n)}
                            data-testid={`doctor-future-need-${n.id}-propose`}
                            className="mt-0.5 inline-flex items-center gap-1.5 self-start rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-[12px] font-semibold text-teal-800 hover:bg-teal-100"
                            title="Turn this into a treatment plan the front desk can present"
                          >
                            <ClipboardList className="w-3.5 h-3.5" /> Propose as a plan
                          </button>
                        ) : null}
                        {removingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : null}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
        {needs.length > 0 ? (
          <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md text-xs space-y-0.5" data-testid="doctor-future-needs-subtotals">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Gross · {needs.length} {needs.length === 1 ? "need" : "needs"}</span>
              <span className="text-slate-700 tabular-nums">{formatMoney(grossTotal, currency)}</span>
            </div>
            {discountTotal > 0 ? (
              <div className="flex items-center justify-between">
                <span className="text-amber-700">Discount</span>
                <span className="text-amber-700 tabular-nums">−{formatMoney(discountTotal, currency)}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between pt-1 border-t border-slate-200">
              <span className="text-slate-600 font-medium">Estimated net</span>
              <span className="font-semibold text-slate-900 tabular-nums">{formatMoney(netTotal, currency)}</span>
            </div>
          </div>
        ) : null}
      </div>
      <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400 space-y-0.5">
        <p>
          <span className="text-slate-500 font-medium">Saved — these appear in Recall</span> so the front desk follows up. Tap <span className="text-teal-700 font-medium">Propose as a plan</span> on anything the patient is ready to commit to.
        </p>
        <p>Open needs are erasable. Once scheduled, they live in the appointment.</p>
      </div>

      {proposing ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="doctor-future-need-propose-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                <ClipboardList className="w-4 h-4 text-teal-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">Propose as a treatment plan</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Starts a draft plan for the front desk to present.{" "}
                  <span className="font-medium text-slate-700">
                    {proposing.procedure_name || "Procedure"}
                    {proposing.tooth_number ? ` · #${proposing.tooth_number}` : ""}
                    {proposing.surface ? ` ${proposing.surface}` : ""}
                  </span>{" "}
                  goes in.
                </p>
              </div>
            </div>

            {needs.filter((x) => x.id !== proposing.id).length > 0 ? (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                  Include the patient's other needs?
                </p>
                <ul className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                  {needs.filter((x) => x.id !== proposing.id).map((x) => (
                    <li key={x.id} className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={bundleIds.has(x.id)}
                        onChange={() => toggleBundle(x.id)}
                        data-testid={`doctor-future-need-propose-bundle-${x.id}`}
                        className="h-4 w-4 accent-teal-600"
                      />
                      <span className="text-sm text-slate-700 truncate">
                        {x.procedure_name || "Procedure"}
                        {x.tooth_number ? <span className="text-slate-400"> · #{x.tooth_number}{x.surface ? ` ${x.surface}` : ""}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-400 mt-1.5">Unchecked needs stay on the Future Needs list (and in Recall).</p>
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setProposing(null)}
                disabled={proposeBusy}
                data-testid="doctor-future-need-propose-note"
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                title="Leave it as a Future Need — it stays in Recall for follow-up"
              >
                Note it → Recall
              </button>
              <button
                type="button"
                onClick={confirmPropose}
                disabled={proposeBusy}
                data-testid="doctor-future-need-propose-confirm"
                className="px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {proposeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
                Propose plan{bundleIds.size > 0 ? ` (${bundleIds.size + 1})` : ""}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// Keyboard shortcuts help dialog — opened via "?" key. Lists the
// chairside-only shortcuts grouped so they're scannable.
function HelpShortcutsDialog({ chartMode, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const mod = isMac ? "⌘" : "Ctrl";

  const sections = [
    {
      title: "Switch mode",
      rows: [
        { keys: ["1"], label: "Chart (paint findings)" },
        { keys: ["2"], label: "Tx Plan" },
        { keys: ["3"], label: "Today" },
        { keys: ["4"], label: "Future Need" },
      ],
    },
    {
      title: chartMode === "today" ? "Undo / redo (visit draft)"
            : chartMode === "tx_plan" ? "Undo / redo (plan items)"
            : chartMode === "future_need" ? "Undo / redo (future needs)"
            : "Undo / redo",
      rows: [
        { keys: [mod, "Z"], label: "Undo" },
        { keys: [mod, "⇧", "Z"], label: "Redo" },
      ],
    },
    {
      title: "Help",
      rows: [
        { keys: ["?"], label: "Toggle this cheatsheet" },
        { keys: ["Esc"], label: "Close" },
      ],
    },
  ];

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="help-shortcuts-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="help-shortcuts-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <p id="help-shortcuts-title" className="text-sm font-semibold text-slate-900">
            Keyboard shortcuts
          </p>
          <button
            type="button" onClick={onClose}
            data-testid="help-shortcuts-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {sections.map((sec) => (
            <div key={sec.title}>
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                {sec.title}
              </p>
              <ul className="space-y-1">
                {sec.rows.map((row) => (
                  <li key={row.label} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-700">{row.label}</span>
                    <span className="inline-flex items-center gap-1">
                      {row.keys.map((k, i) => (
                        <kbd
                          key={`${row.label}-${i}`}
                          className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700 shadow-sm"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="px-5 py-2 border-t border-slate-100 text-[11px] text-slate-400">
          Skipped while typing in inputs / textareas.
        </p>
      </div>
    </div>
  );
}

// Duplicate-paint warning. Fired when the dentist tries to paint
// the same procedure on a tooth/surface that already has it.
// "Don't show again" silences the dialog for the rest of the
// browser session via localStorage.
function DuplicatePaintDialog({ procName, fdi, surface, onCancel, onConfirm }) {
  const [dontShow, setDontShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const where = surface ? `tooth #${fdi} surface ${surface}` : `tooth #${fdi}`;
  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="dup-paint-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={() => !busy && onCancel()}
      data-testid="duplicate-paint-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div className="min-w-0">
            <p id="dup-paint-title" className="text-sm font-semibold text-slate-900">
              Same procedure already on {where}
            </p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              <span className="font-medium text-slate-700">{procName}</span> is already painted here.
              Adding another creates a duplicate entry.
            </p>
          </div>
        </div>
        <div className="px-5 py-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              data-testid="duplicate-paint-dont-show"
              className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
            />
            Don't show this again
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button" onClick={onCancel} disabled={busy}
            data-testid="duplicate-paint-cancel"
            className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(dontShow); }
              finally { setBusy(false); }
            }}
            disabled={busy}
            data-testid="duplicate-paint-confirm"
            className="px-3 py-2 text-xs font-semibold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Add anyway
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceHeader({ appt, patient, clinicRole, onOpenPatient, onOpenMedicalHistory, onOpenOrtho, onSwitchPatient, onMarkDone, markingDone = false, onPatientChanged, queueCount, currency = "PHP", todayTotals = null, lastVisit = null, lastVisitLoaded = false, patientContext = null, showOrthoButton = true, handoffBlocked = false, handoffBlockMessage = "" }) {
  const [contextOpen, setContextOpen] = useState(false);
  const canEditTag = clinicRole === "clinic_admin" || clinicRole === "doctor";
  // Chief complaint / booking note the front desk typed when booking — shown
  // prominently below so the doctor reads it the moment the patient sits down.
  const chiefComplaint = (appt.notes || "").trim() || null;
  const patientAge = formatPatientAge(patient?.date_of_birth);
  const showTally = todayTotals && todayTotals.count > 0;
  // "Last seen Apr 12 · 3 procedures · Dr. Reyes" — only render once
  // the lookup has resolved AND there's a finalized prior visit. New
  // patients (no prior visits) get nothing in this slot.
  const lastVisitLine = lastVisitLoaded && lastVisit ? formatLastVisitLine(lastVisit) : null;
  const medicalHistoryPayload = Array.isArray(patientContext?.medicalHistory)
    ? patientContext.medicalHistory[0]
    : patientContext?.medicalHistory;
  const medicalHistoryData = medicalHistoryPayload?.data ?? null;
  const medicalAlerts = useMemo(() => computeMedicalHistoryAlerts(medicalHistoryData, {
    history: medicalHistoryPayload,
    patient,
  }), [medicalHistoryData, medicalHistoryPayload, patient]);
  const contextItems = useMemo(() => buildChairsideContextItems({
    patient,
    medicalHistoryData,
    lastVisit,
    lastVisitLoaded,
    currency,
  }), [patient, medicalHistoryData, lastVisit, lastVisitLoaded, currency]);

  useEffect(() => {
    setContextOpen(false);
  }, [patient?.id]);

  return (
    <header className="bg-white rounded-2xl shadow-sm px-5 py-3" data-testid="doctor-workspace-header">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex items-start gap-3 flex-1">
        {/* Switch patient lives here as a small back affordance.
            Replaces the old floating pill that was orphaned above the
            workspace. Only shown when there's at least one queued
            patient to swap to. */}
        {onSwitchPatient && queueCount > 0 ? (
          <button
            type="button"
            onClick={onSwitchPatient}
            className="text-slate-400 hover:text-slate-700 p-1 -ml-1 shrink-0"
            data-testid="doctor-open-queue"
            title={`Switch patient (${queueCount})`}
            aria-label={`Switch patient (${queueCount})`}
          >
            <Menu className="w-4 h-4" />
          </button>
        ) : null}
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 shrink-0">
          {(patient.full_name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Patient name clickable — primary route to the full
                record. Convention: the name itself is the link. */}
            <button
              type="button"
              onClick={onOpenPatient}
              data-testid="doctor-patient-name"
              className="text-lg font-bold text-slate-900 truncate hover:text-teal-700 hover:underline decoration-teal-300 underline-offset-4 text-left"
            >
              {patient.full_name}
            </button>
            {patient.patient_number ? (
              <span className="text-[11px] text-slate-400">{patient.patient_number}</span>
            ) : null}
            <span
              className="text-[11px] font-semibold text-slate-500"
              data-testid="doctor-patient-age"
            >
              Age: {patientAge}
            </span>
            <button
              type="button"
              onClick={() => setContextOpen((v) => !v)}
              data-testid="doctor-patient-context-toggle"
              aria-expanded={contextOpen}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
            >
              Context
              <ChevronDown className={`w-3 h-3 transition-transform ${contextOpen ? "rotate-180" : ""}`} />
            </button>
            {/* MH "not on file" pill is now a clickable CTA — opens
                the Medical History tab on the patient page so the
                dentist can act on the warning chairside. */}
            {!patient.has_medical_history ? (
              <button
                type="button"
                onClick={onOpenPatient}
                data-testid="doctor-mh-cta"
                className="inline-flex items-center gap-1 text-[11px] font-medium bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5 transition-colors"
              >
                <AlertTriangle className="w-3 h-3" />
                Medical history not on file
                <ChevronRight className="w-3 h-3 -mr-0.5" />
              </button>
            ) : null}
            <SeniorPwdTagControl
              patient={patient}
              canEdit={canEditTag}
              onChanged={onPatientChanged}
              testIdPrefix="doctor"
            />
          </div>
          <p className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1 mt-0.5">
            <Stethoscope className="w-3 h-3" /> {formatDoctorName(appt.doctor_name, "Unassigned")}
            <span className="text-slate-300">·</span>
            <MapPin className="w-3 h-3" /> {appt.chair_name}
          </p>
          {lastVisitLine ? (
            <p
              className="text-[11px] text-slate-400 truncate inline-flex items-center gap-1 mt-0.5"
              data-testid="doctor-last-visit-hint"
              title={lastVisitLine.tooltip}
            >
              <History className="w-3 h-3" />
              {lastVisitLine.text}
            </p>
          ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {showTally ? (
            <div
              className="hidden md:inline-flex items-center gap-2 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-3 py-1.5 text-xs"
              data-testid="doctor-today-tally"
              title={
                todayTotals.discount > 0
                  ? `${todayTotals.count} item${todayTotals.count === 1 ? "" : "s"} done — ₱${Math.round(todayTotals.gross / 100)} before ₱${Math.round(todayTotals.discount / 100)} discount`
                  : `${todayTotals.count} item${todayTotals.count === 1 ? "" : "s"} done today`
              }
            >
              <span className="uppercase tracking-wider text-[10px] font-bold text-slate-500">Today</span>
              <span className="font-semibold text-slate-900 tabular-nums" data-testid="doctor-today-tally-net">
                {formatHeaderMoney(todayTotals.net, currency)}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onOpenPatient}
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="doctor-open-patient-record"
          >
            Patient record <ChevronRight className="w-3 h-3" />
          </button>
          {showOrthoButton ? (
            <button
              type="button"
              onClick={onOpenOrtho || onOpenPatient}
              className="text-xs font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5"
              data-testid="doctor-open-ortho-panel"
            >
              Ortho <ChevronRight className="w-3 h-3" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onMarkDone}
            disabled={markingDone || !!appt.doctor_finished_at || handoffBlocked}
            aria-describedby={handoffBlocked ? "doctor-handoff-requirement" : undefined}
            title={handoffBlocked ? handoffBlockMessage : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            data-testid="doctor-mark-patient-done"
            data-tutorial-id="doctor-mark-patient-done"
          >
            {markingDone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {appt.doctor_finished_at ? "Done" : "Done with patient"}
          </button>
        </div>
      </div>

      {chiefComplaint || medicalAlerts.length > 0 ? (
        <ChairsideClinicalBrief
          chiefComplaint={chiefComplaint}
          alerts={medicalAlerts}
          onOpen={onOpenMedicalHistory || onOpenPatient}
        />
      ) : null}
      {contextOpen ? (
        <div
          className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-2"
          data-testid="doctor-patient-context-strip"
        >
          {contextItems.map((item) => (
            <PatientContextFact key={item.label} label={item.label} value={item.value} tone={item.tone} />
          ))}
        </div>
      ) : null}
    </header>
  );
}

function ChairsideClinicalBrief({ chiefComplaint, alerts, onOpen }) {
  const hasBlockingAlert = alerts.some((alert) => alert.severity === "blocking");
  const hasAlerts = alerts.length > 0;
  const alertTone = hasBlockingAlert
    ? "bg-red-50 text-red-950"
    : "bg-rose-50/70 text-rose-950";
  const alertIconTone = hasBlockingAlert
    ? "bg-red-100 text-red-700"
    : "bg-rose-100 text-rose-600";

  return (
    <div
      className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
      data-testid="doctor-clinical-brief"
    >
      {chiefComplaint ? (
        <div
          className="flex min-w-0 items-center gap-2 bg-slate-50/60 px-3 py-2"
          data-testid="doctor-chief-complaint"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-700">
            <ClipboardList className="h-3.5 w-3.5" />
          </span>
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
            Reason for visit
          </span>
          <span className="text-slate-300">·</span>
          <p className="min-w-0 truncate text-[12px] font-semibold text-slate-800" title={chiefComplaint}>
            {chiefComplaint}
          </p>
        </div>
      ) : null}

      {hasAlerts ? (
        <div
          className={`flex min-w-0 items-start gap-2.5 px-3 py-2.5 ${alertTone} ${chiefComplaint ? "border-t border-slate-200" : ""}`}
          data-testid="doctor-medical-alerts"
          role="alert"
        >
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${alertIconTone}`}>
            <AlertTriangle className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-[12px] font-bold">
                {hasBlockingAlert
                  ? "Blocking medical alert"
                  : `${alerts.length} medical ${alerts.length === 1 ? "alert" : "alerts"}`}
              </p>
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                hasBlockingAlert ? "bg-red-200/70 text-red-800" : "bg-rose-100 text-rose-700"
              }`}>
                {hasBlockingAlert ? "Stop treatment" : "Review first"}
              </span>
            </div>
            <ul className="mt-1 flex flex-wrap gap-x-5 gap-y-0.5 text-[11px] leading-4">
              {alerts.map((alert, index) => (
                <li key={`${alert.label}-${index}`} data-testid={`doctor-medical-alert-${index}`}>
                  <span className="font-semibold">{alert.label}:</span>{" "}
                  <span className="text-slate-600">{alert.detail}</span>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 shadow-sm hover:border-slate-300 hover:text-slate-900"
            data-testid="doctor-open-medical-history"
          >
            View history <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PatientContextFact({ label, value, tone = "default" }) {
  const toneClass =
    tone === "warning" ? "text-amber-800" :
    tone === "danger" ? "text-rose-700" :
    tone === "money" ? "text-emerald-700" :
    "text-slate-900";
  return (
    <div className="min-w-0 rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-100">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-0.5 truncate text-xs font-semibold ${toneClass}`} title={value}>
        {value}
      </p>
    </div>
  );
}

function buildChairsideContextItems({ patient, medicalHistoryData, lastVisit, lastVisitLoaded, currency }) {
  const balance = Number(patient?.balance_centavos || 0);
  return [
    { label: "Age", value: formatPatientAge(patient?.date_of_birth) },
    {
      label: "Last visit",
      value: lastVisitLoaded
        ? formatContextDate(lastVisit?.finalized_at || patient?.last_visit_date)
        : "Loading...",
    },
    {
      label: "Allergies",
      value: summarizeAllergies(medicalHistoryData),
      tone: allergyTone(medicalHistoryData),
    },
    {
      label: "Medications",
      value: summarizeMedications(medicalHistoryData),
      tone: medsTone(medicalHistoryData),
    },
    {
      label: "Balance",
      value: balance > 0 ? formatMoney(balance, currency) : "None",
      tone: balance > 0 ? "warning" : "money",
    },
  ];
}

function formatPatientAge(dob) {
  if (!dob) return "Not on file";
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return "Not on file";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? `${age} yrs` : "Not on file";
}

function formatContextDate(value) {
  if (!value) return "No prior visit";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "No prior visit";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function summarizeAllergies(data) {
  if (!data?.allergies) return "Not on file";
  const a = data.allergies;
  if (a.none) return "None reported";
  const items = [];
  if (a.latex) items.push("Latex");
  if (a.anesthetic?.yes) items.push(a.anesthetic.specify ? `Anesthetic: ${a.anesthetic.specify}` : "Anesthetic");
  if (a.drug?.yes) items.push(a.drug.specify ? `Drug: ${a.drug.specify}` : "Drug");
  if (a.food?.yes) items.push(a.food.specify ? `Food: ${a.food.specify}` : "Food");
  if (a.other) items.push(a.other);
  return compactContextList(items);
}

function summarizeMedications(data) {
  if (!data) return "Not on file";
  const meds = data.meds || {};
  const items = [];
  if (meds.blood_thinners?.yes) items.push(meds.blood_thinners.specify ? `Blood thinner: ${meds.blood_thinners.specify}` : "Blood thinner");
  if (meds.bisphosphonates?.yes) items.push(meds.bisphosphonates.specify ? `Bisphosphonate: ${meds.bisphosphonates.specify}` : "Bisphosphonate");
  if (meds.steroids_long_term?.yes) items.push(meds.steroids_long_term.specify ? `Steroid: ${meds.steroids_long_term.specify}` : "Long-term steroid");
  if (meds.chemo_radiation?.yes) items.push(meds.chemo_radiation.specify ? `Cancer tx: ${meds.chemo_radiation.specify}` : "Chemo/radiation");
  if (data.medications) items.push(data.medications);
  if (meds.other) items.push(meds.other);
  if (items.length === 0 && meds.none) return "None reported";
  return compactContextList(items);
}

function compactContextList(items) {
  const clean = items.map((item) => String(item || "").trim()).filter(Boolean);
  if (clean.length === 0) return "None reported";
  if (clean.length <= 2) return clean.join(", ");
  return `${clean.slice(0, 2).join(", ")} +${clean.length - 2}`;
}

function allergyTone(data) {
  if (!data?.allergies) return "default";
  if (data.allergies.none) return "money";
  return summarizeAllergies(data) === "None reported" ? "money" : "danger";
}

function medsTone(data) {
  if (!data) return "default";
  return summarizeMedications(data) === "None reported" ? "money" : "warning";
}

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// "Last seen Apr 12 · 3 procedures · Dr. Reyes" — formats a finalized
// list_patient_visits row into a tight chairside-header subtitle.
// Returns { text, tooltip } so the visible line stays compact while
// the tooltip carries the precise full date for verification.
function formatLastVisitLine(v) {
  if (!v?.finalized_at) return null;
  const d = new Date(v.finalized_at);
  if (isNaN(d.getTime())) return null;
  const monthDay = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const dateLabel = sameYear ? monthDay : `${monthDay}, ${d.getFullYear()}`;
  const count = Number(v.procedure_count || 0);
  const procPart = count > 0
    ? `${count} procedure${count === 1 ? "" : "s"}`
    : null;
  const docName = v.doctor_name && v.doctor_name !== "Unassigned" ? formatDoctorName(v.doctor_name) : null;
  const docPart = docName || null;
  const parts = ["Last seen " + dateLabel, procPart, docPart].filter(Boolean);
  return {
    text: parts.join(" · "),
    tooltip: `Last finalized visit: ${d.toLocaleDateString("en-US", { dateStyle: "medium" })}${docName ? ` by Dr. ${docName.replace(/^Dr\.?\s*/i, "")}` : ""}`,
  };
}

// Header tally money formatter — uses the same currency-symbol map as
// the rest of the doctor view but rounds to whole pesos for a tight
// chip. Centavos in the header would dominate the visual at a glance.
function formatHeaderMoney(centavos, currency) {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency === "INR" ? "₹" : currency ? `${currency} ` : "";
  return symbol + Math.round((Number(centavos) || 0) / 100).toLocaleString("en-US");
}


// ---------------------------------------------------------------------
// useVisitDraft — coordinates the per-appointment Chairside workspace.
// Performed Today items live in checkout_drafts.draft_data. The clinical
// narrative lives separately in clinical_visit_notes, where Checkout can
// lock the same row permanently and later appointments can read its timeline.
//
// Draft shape lines up with what frontend/src/pages/Checkout.jsx
// writes, so the front desk's Step 1 hydrates straight from this:
//   { items: [...seedItemFromSchedule shape...], last_step: "today" }
// ---------------------------------------------------------------------
function useVisitDraft(appointmentId) {
  const [items, setItems]   = useState([]);
  const [note, setNote]     = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [draftConflict, setDraftConflict] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSavedAt, setNoteSavedAt] = useState(null);
  const [noteHistory, setNoteHistory] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const noteSaveRef = useRef(null);
  const draftRevisionRef = useRef(0);
  const draftSaveChainRef = useRef(Promise.resolve());
  const currentNoteIdRef = useRef(null);
  const lastPersistedNoteRef = useRef("");
  // Procedures the front desk scheduled for this appointment. Used
  // by the Doctor view to surface a "Suggested" group at the top
  // of the procedures palette so the dentist doesn't hunt for
  // what they already know they're going to perform today.
  const [scheduled, setScheduled] = useState([]);
  // Per-tn_id disposition the doctor (or receptionist) staged during
  // the visit, BEFORE the visit is committed at checkout. Shape:
  //   { [tn_id]: { kind: 'not_today' | 'declined', reason?: string } }
  // 'done' isn't tracked here — that lives in `items` because billing
  // rides on the items list. This map covers the not-billed states
  // that still need to land on the DB at commit time:
  //   not_today → unschedule_treatment_need (returns to Open pool;
  //               receptionist can rebook in Step 3 follow-up)
  //   declined  → decline_treatment_need with the supplied reason
  // Persists in checkout_drafts.draft_data.dispositions so the
  // receptionist's Step 1 / Step 3 see the same state.
  const [dispositions, setDispositions] = useState({});
  // Undo/redo stacks scoped to items only — the visit note stays
  // text-typed, browsers handle ctrl-Z there. Stacks reset whenever
  // the appointment changes (different patient = different draft).
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Hydrate on mount via get_checkout_hydration so a fresh visit
  // pre-loads the procedures the front desk scheduled.
  const reload = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setNote("");
    setNoteHistory([]);
    setNoteSavedAt(null);
    setNoteSaving(false);
    setSaveError(null);
    setDraftConflict(false);
    draftRevisionRef.current = 0;
    currentNoteIdRef.current = null;
    lastPersistedNoteRef.current = "";
    setUndoStack([]);
    setRedoStack([]);
    (async () => {
      // Make sure every unfinished ongoing (MV) procedure has a workable visit
      // on THIS appointment, so it shows in Today's plan (one session per visit).
      // Idempotent — re-running creates nothing new. Never blocks hydration.
      if (cancelled) return;
      const [hydrationResult, noteResult] = await Promise.all([
        supabase.rpc("get_checkout_hydration", { p_appointment_id: appointmentId }),
        supabase.rpc("get_chairside_visit_notes", { p_appointment_id: appointmentId }),
      ]);
      if (cancelled) return;
      const data = hydrationResult.data;
      if (hydrationResult.error) {
        toast.error(hydrationResult.error.message ?? "Could not load today's procedures.");
        return;
      }
      const draft = data?.draft ?? {};
      draftRevisionRef.current = normalizeCheckoutDraftRevision(data?.draft_revision);
      const currentNote = noteResult.data?.current_note ?? null;
      const hydratedNote = currentNote
        ? (typeof currentNote.note_text === "string" ? currentNote.note_text : "")
        : (typeof draft.visit_note === "string" ? draft.visit_note : "");
      if (noteResult.error) {
        toast.error(noteResult.error.message ?? "Could not load visit-note history.");
      }
      const scheduledList = Array.isArray(data?.scheduled_items) ? data.scheduled_items : [];
      setScheduled(scheduledList);
      // Hydrate Today's Work strictly from the saved draft. Auto-seed
      // from scheduled_items used to fire here — but the doctor never
      // got an explicit "Done" gesture for booked procedures, so the
      // bill silently included things they hadn't actually performed.
      // Booked procedures now live in the To Do Today card; "Done"
      // there populates this draft via addFromSchedule.
      if (Array.isArray(draft.items) && draft.items.length > 0) {
        setItems(draft.items.map(ensureDraftLineId));
      } else {
        setItems([]);
      }
      setNote(hydratedNote);
      currentNoteIdRef.current = currentNote?.id ?? null;
      lastPersistedNoteRef.current = hydratedNote;
      setNoteHistory(Array.isArray(noteResult.data?.history) ? noteResult.data.history : []);
      setNoteSavedAt(currentNote?.updated_at ? new Date(currentNote.updated_at) : null);
      setDispositions(
        draft.dispositions && typeof draft.dispositions === "object"
          ? draft.dispositions
          : {}
      );
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [appointmentId, reloadKey]);

  const persistDraft = useCallback((draft) => {
    const save = async () => {
      const saved = await saveCheckoutDraftWithRevision({
        rpc: supabase.rpc.bind(supabase),
        appointmentId,
        draft,
        expectedRevision: draftRevisionRef.current,
      });
      draftRevisionRef.current = saved.revision;
      return saved;
    };
    const queued = draftSaveChainRef.current.catch(() => undefined).then(save);
    draftSaveChainRef.current = queued;
    return queued;
  }, [appointmentId]);

  const reportDraftSaveFailure = useCallback((draftError, fallback) => {
    const conflict = isCheckoutDraftRevisionConflict(draftError);
    const message = conflict
      ? "This visit changed in another screen. Reload the latest saved procedures before continuing."
      : (draftError?.message || fallback || "Could not save today's procedures.");
    setSaveError(message);
    if (conflict) setDraftConflict(true);
    toast.error(conflict ? "This visit changed in another screen" : message, {
      id: conflict ? "doctor-draft-conflict" : "doctor-draft-save-failed",
      description: conflict
        ? "Your screen was not allowed to overwrite it. Reload and review the saved procedures."
        : undefined,
    });
  }, []);

  // 700ms debounce for today's billable work. The clinical visit note has its
  // own database row and save cycle below; it no longer lives in this JSON.
  const saveRef = useRef(null);
  useEffect(() => {
    if (!loaded || draftConflict) return;
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const saved = await persistDraft({
          items,
          dispositions,
          last_step: "today",
        });
        setItems((current) => {
          const merged = mergeCheckoutDraftItems(
            current,
            Array.isArray(saved.draftData?.items) ? saved.draftData.items : [],
          ).map(ensureDraftLineId);
          return merged.length === current.length
            && merged.every((item, index) => item === current[index])
            ? current
            : merged;
        });
        setSaveError(null);
        setSavedAt(new Date(saved.updatedAt || Date.now()));
      } catch (draftError) {
        reportDraftSaveFailure(draftError);
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(saveRef.current);
  }, [dispositions, draftConflict, items, loaded, persistDraft, reportDraftSaveFailure]);

  const persistVisitNote = useCallback(async (nextNote) => {
    if (!loaded) return true;
    const value = typeof nextNote === "string" ? nextNote : "";
    if (!value.trim() && !currentNoteIdRef.current) {
      lastPersistedNoteRef.current = "";
      return true;
    }

    setNoteSaving(true);
    const { data, error } = await supabase.rpc("save_clinical_visit_note", {
      p_appointment_id: appointmentId,
      p_note_text: value,
    });
    setNoteSaving(false);
    if (error) {
      toast.error(error.message ?? "Could not save the visit note.");
      return false;
    }

    currentNoteIdRef.current = data?.id ?? currentNoteIdRef.current;
    lastPersistedNoteRef.current = value;
    setNoteSavedAt(new Date());
    return true;
  }, [appointmentId, loaded]);

  // The textarea feels immediate, but database truth follows within 700ms.
  // "Done with patient" calls saveNoteNow below and waits for confirmation,
  // so the last keystroke cannot be lost to the debounce window.
  useEffect(() => {
    if (!loaded || note === lastPersistedNoteRef.current) return undefined;
    if (noteSaveRef.current) clearTimeout(noteSaveRef.current);
    noteSaveRef.current = setTimeout(() => {
      noteSaveRef.current = null;
      void persistVisitNote(note);
    }, 700);
    return () => {
      if (noteSaveRef.current) clearTimeout(noteSaveRef.current);
    };
  }, [loaded, note, persistVisitNote]);

  const saveNoteNow = useCallback(async () => {
    if (noteSaveRef.current) {
      clearTimeout(noteSaveRef.current);
      noteSaveRef.current = null;
    }
    if (note === lastPersistedNoteRef.current) return true;
    return persistVisitNote(note);
  }, [note, persistVisitNote]);

  // Mutating wrapper — every items change is captured on the undo
  // stack first so the doctor can ctrl-Z mistaken paints chairside.
  // Redo stack clears on a fresh edit — same convention as the
  // chart-side undo (Photoshop-style linear history).
  const mutateItems = useCallback((updater) => {
    setItems((prev) => {
      setUndoStack((u) => [...u, prev]);
      setRedoStack([]);
      return typeof updater === "function" ? updater(prev) : updater;
    });
  }, []);

  const undo = useCallback(() => {
    setUndoStack((u) => {
      if (u.length === 0) return u;
      const prev = u[u.length - 1];
      setItems((cur) => {
        setRedoStack((r) => [...r, cur]);
        return prev;
      });
      return u.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((r) => {
      if (r.length === 0) return r;
      const next = r[r.length - 1];
      setItems((cur) => {
        setUndoStack((u) => [...u, cur]);
        return next;
      });
      return r.slice(0, -1);
    });
  }, []);

  // proc = procedure row from list_clinic_procedures.
  // ctx (optional) = { tooth_number, surface } from a chart paint click.
  // Bundling rule: same tooth + same procedure merges the surface into
  // an existing draft item. Bundled fee = base + (count-1) * base * pct/100.
  // The doctor's price override (when performed_price differs from the
  // bundled catalog price) is preserved across surface adds/strips.
  const addItem = useCallback((proc, ctx = {}) => {
    const tooth = ctx.tooth_number ? String(ctx.tooth_number) : "";
    const surfChar = ctx.surface ?? null;
    const base   = Number(proc.default_fee_centavos ?? 0);
    const pct    = Number(proc.additional_surface_pct ?? 100);

    mutateItems((prev) => {
      const existingIdx = prev.findIndex((it) =>
        it.procedure_id === proc.id
        && (String(it.tooth_number || "") === tooth)
        && (it.outcome || "done") === "done");

      if (existingIdx >= 0) {
        const existing = prev[existingIdx];
        const merged   = surfChar ? mergeSurfaces(existing.surface, surfChar) : existing.surface;
        if (merged === existing.surface) return prev;
        const oldCount = Math.max(1, splitSurfaces(existing.surface).length);
        const newCount = Math.max(1, splitSurfaces(merged).length);
        const baseFee  = Number(existing.procedure_base_centavos ?? base);
        const pctFee   = Number(existing.additional_surface_pct ?? pct);
        // Settings-driven surface pricing: clinic bundle policy
        // (procedures.additional_surface_pct) decides the extra-surface rate.
        const oldHonest = bundledFeeCentavos(baseFee, pctFee, oldCount);
        const newHonest = bundledFeeCentavos(baseFee, pctFee, newCount);
        const wasCustom = Number(existing.performed_price_centavos || 0) !== oldHonest;
        const next = prev.slice();
        next[existingIdx] = {
          ...existing,
          surface: merged,
          default_fee_centavos: newHonest,
          performed_price_centavos: wasCustom
            ? Number(existing.performed_price_centavos || 0)
            : newHonest,
        };
        return next;
      }

      const initialCount = Math.max(1, splitSurfaces(surfChar).length);
      const initialPrice = bundledFeeCentavos(base, pct, initialCount);
      return [
        ...prev,
        {
          draft_line_id: createDraftLineId(),
          procedure_id: proc.id,
          procedure_code: proc.code,
          procedure_name: proc.name,
          procedure_base_centavos: base,
          additional_surface_pct: pct,
          default_fee_centavos: initialPrice,
          performed_price_centavos: initialPrice,
          item_discount_centavos: 0,
          qty: 1,
          tooth_number: tooth,
          surface: surfChar ? normalizeSurfaceStr(surfChar) : null,
          outcome: "done",
        },
      ];
    });
  }, [mutateItems]);

  const removeItem = useCallback((lineId) => {
    if (!lineId) return;
    mutateItems((prev) => prev.filter((it) => !sameDraftLine(it, lineId)));
  }, [mutateItems]);

  // Strip ONE surface char from a bundled draft item. If it was the
  // last surface, the row is removed.
  const removeSurface = useCallback((lineId, surfChar) => {
    if (!lineId) return;
    mutateItems((prev) => {
      const idx = prev.findIndex((it) => sameDraftLine(it, lineId));
      if (idx < 0) return prev;
      const existing = prev[idx];
      const after = stripSurface(existing.surface, surfChar);
      if (!after) return prev.filter((it) => !sameDraftLine(it, lineId));
      if (after === existing.surface) return prev;
      const baseFee  = Number(existing.procedure_base_centavos ?? existing.default_fee_centavos ?? 0);
      const pctFee   = Number(existing.additional_surface_pct ?? 100);
      const oldCount = Math.max(1, splitSurfaces(existing.surface).length);
      const newCount = Math.max(1, splitSurfaces(after).length);
      const oldHonest = bundledFeeCentavos(baseFee, pctFee, oldCount);
      const newHonest = bundledFeeCentavos(baseFee, pctFee, newCount);
      const wasCustom  = Number(existing.performed_price_centavos || 0) !== oldHonest;
      const next = prev.slice();
      next[idx] = {
        ...existing,
        surface: after,
        default_fee_centavos: newHonest,
        performed_price_centavos: wasCustom
          ? Number(existing.performed_price_centavos || 0)
          : newHonest,
      };
      return next;
    });
  }, [mutateItems]);

  const setOutcome = useCallback((lineId, outcome) => {
    if (!lineId) return;
    mutateItems((prev) => prev.map((it) => (sameDraftLine(it, lineId) ? { ...it, outcome } : it)));
  }, [mutateItems]);

  // Per-line price override (single-surface line). Persists as
  // performed_price_centavos in the draft JSON; checkout reads it.
  const setItemPrice = useCallback((lineId, centavos) => {
    if (!lineId) return;
    mutateItems((prev) => prev.map((it) =>
      sameDraftLine(it, lineId) ? { ...it, performed_price_centavos: Number(centavos || 0) } : it));
  }, [mutateItems]);

  // Per-line discount. {pct, amount, reason}. Stored shape matches the
  // commit_checkout_with_followup input contract: item_discount_centavos
  // (already in draft) + the new pct + reason fields.
  const setItemDiscount = useCallback((lineId, { pct, amount, reason }) => {
    if (!lineId) return;
    mutateItems((prev) => prev.map((it) => {
      if (!sameDraftLine(it, lineId)) return it;
      const gross = Number(it.qty || 1) * Number(it.performed_price_centavos || 0);
      let amtCents = 0;
      if (pct != null && Number(pct) > 0) {
        amtCents = Math.round(gross * (Number(pct) / 100));
      } else if (amount != null && Number(amount) > 0) {
        amtCents = Number(amount);
      }
      return {
        ...it,
        item_discount_centavos: amtCents,
        item_discount_pct:      pct != null && Number(pct) > 0 ? Number(pct) : null,
        item_discount_reason:   reason || null,
      };
    }));
  }, [mutateItems]);

  const setItemNote = useCallback((lineId, noteText) => {
    if (!lineId) return;
    mutateItems((prev) => prev.map((it) =>
      sameDraftLine(it, lineId) ? { ...it, line_note: noteText || null } : it));
  }, [mutateItems]);

  // Move a scheduled work row from the To Do Today list into the
  // Today's Work draft. Idempotent by scheduledWorkKey so a plan
  // sub-visit and a legacy treatment_need don't collide.
  // Clears any existing disposition on this key so done supersedes
  // a previously-staged not_today / declined.
  const addFromSchedule = useCallback((sched) => {
    if (!sched) return;
    const key = scheduledWorkKey(sched);
    // Bare tooth-based booking: "Done" checks it off WITHOUT a charge — the
    // real (surface-priced) work is charted below and that's what bills. A
    // "logged" disposition has no procedure_visit_id, so checkout's commit
    // ignores it (no bill, no decline).
    if (isBareToothBooking(sched)) {
      mutateItems((prev) => prev.filter((it) => !sameScheduledWork(it, sched)));
      if (key) setDispositions((prev) => ({ ...prev, [key]: { kind: "logged" } }));
      return;
    }
    mutateItems((prev) => {
      if (key && prev.some((it) => sameScheduledWork(it, sched))) return prev;
      return [...prev, seedItemFromSchedule(sched)];
    });
    if (key) {
      setDispositions((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [mutateItems]);

  // A two-arch denture is still two clinical / billable plan items, but it is
  // one chairside case action. Persist every linked scheduled row in a single
  // checkout-draft write, then render the database-confirmed draft. This keeps
  // one click from showing only the first arch while the second waits for a
  // separate click, without collapsing the two arch records in the SSOT.
  const addScheduledGroup = useCallback(async (scheduledRows) => {
    const rows = (Array.isArray(scheduledRows) ? scheduledRows : [])
      .filter((row) => row && scheduledWorkKey(row));
    if (rows.length === 0) return false;

    const nextItems = [...items];
    for (const row of rows) {
      if (!nextItems.some((item) => sameScheduledWork(item, row))) {
        nextItems.push(seedItemFromSchedule(row));
      }
    }
    const nextDispositions = { ...dispositions };
    for (const row of rows) {
      const key = scheduledWorkKey(row);
      if (key) delete nextDispositions[key];
      if (row?.tn_id) delete nextDispositions[`tn:${row.tn_id}`];
    }

    if (saveRef.current) {
      clearTimeout(saveRef.current);
      saveRef.current = null;
    }
    setSaving(true);
    let saved;
    try {
      saved = await persistDraft({
        items: nextItems,
        dispositions: nextDispositions,
        last_step: "today",
      });
    } catch (error) {
      saved = null;
      reportDraftSaveFailure(error, "Could not record the booked denture case.");
    }
    setSaving(false);
    if (!saved) return false;

    const persistedDraft = saved.draftData;
    if (!persistedDraft || !Array.isArray(persistedDraft.items)) {
      reload();
      return true;
    }
    setUndoStack((stack) => [...stack, items]);
    setRedoStack([]);
    setItems(persistedDraft.items);
    setDispositions(
      persistedDraft.dispositions && typeof persistedDraft.dispositions === "object"
        ? persistedDraft.dispositions
        : {},
    );
    setSaveError(null);
    setSavedAt(new Date(saved.updatedAt || Date.now()));
    return true;
  }, [dispositions, items, persistDraft, reload, reportDraftSaveFailure]);

  const removeScheduledGroup = useCallback(async (scheduledRows) => {
    const rows = (Array.isArray(scheduledRows) ? scheduledRows : [])
      .filter((row) => row && scheduledWorkKey(row));
    if (rows.length === 0) return false;

    const nextItems = items.filter(
      (item) => !rows.some((row) => sameScheduledWork(item, row)),
    );
    if (saveRef.current) {
      clearTimeout(saveRef.current);
      saveRef.current = null;
    }
    setSaving(true);
    let saved;
    try {
      saved = await persistDraft({
        items: nextItems,
        dispositions,
        last_step: "today",
      });
    } catch (error) {
      saved = null;
      reportDraftSaveFailure(error, "Could not undo the booked denture case.");
    }
    setSaving(false);
    if (!saved) return false;

    const persistedDraft = saved.draftData;
    if (!persistedDraft || !Array.isArray(persistedDraft.items)) {
      reload();
      return true;
    }
    setUndoStack((stack) => [...stack, items]);
    setRedoStack([]);
    setItems(persistedDraft.items);
    setDispositions(
      persistedDraft.dispositions && typeof persistedDraft.dispositions === "object"
        ? persistedDraft.dispositions
        : {},
    );
    setSaveError(null);
    setSavedAt(new Date(saved.updatedAt || Date.now()));
    return true;
  }, [dispositions, items, persistDraft, reload, reportDraftSaveFailure]);

  // Pull a draft item back out of Today's Work by its stable scheduled
  // work key. Plan visits key by procedure_visit_id; legacy needs key
  // by tn_id.
  // the "Done ✓" pill on the Today's Plan card — clicking the pill
  // un-marks the procedure so the doctor can re-decide. Local-only;
  // the treatment_need's DB status stays 'Scheduled'.
  const removeFromSchedule = useCallback((scheduledRow) => {
    const key = scheduledWorkKey(scheduledRow);
    if (!key) return;
    mutateItems((prev) => prev.filter((it) => !sameScheduledWork(it, scheduledRow)));
  }, [mutateItems]);

  // Stage a "Not today" disposition — the scheduled need stays
  // visible in Today's Plan AND in the receptionist's Step 1 with
  // a "Not today" badge. The DB call (unschedule_treatment_need)
  // fires at commit time as part of the atomic checkout, so a
  // mid-visit cancel doesn't leave half-state behind. Also makes
  // sure no `done` item for this tn_id is still in the bill —
  // not_today + done are mutually exclusive.
  const unscheduleNeed = useCallback(async (scheduledRow, reasonCode) => {
    const key = scheduledWorkKey(scheduledRow);
    if (!key) return false;
    const normalizedReason = normalizeNotTodayReasonCode(reasonCode);
    if (!normalizedReason) {
      toast.error("Choose why this treatment was not done today.");
      return false;
    }
    setDispositions((prev) => ({
      ...prev,
      [key]: { kind: "not_today", reason_code: normalizedReason },
    }));
    mutateItems((prev) => prev.filter((it) => !sameScheduledWork(it, scheduledRow)));
    return true;
  }, [mutateItems]);

  // Stage a "Declined" disposition with a reason. Same deferred-
  // commit pattern as not_today; decline_treatment_need fires at
  // commit. Reason still required at click time so the receptionist
  // and the audit log know WHY this got dropped.
  const declineNeed = useCallback(async (scheduledRow, reason) => {
    const key = scheduledWorkKey(scheduledRow);
    if (!key) return false;
    const trimmed = (reason || "").trim();
    if (!trimmed) { toast.error("Decline reason is required."); return false; }
    setDispositions((prev) => ({ ...prev, [key]: { kind: "declined", reason: trimmed } }));
    mutateItems((prev) => prev.filter((it) => !sameScheduledWork(it, scheduledRow)));
    return true;
  }, [mutateItems]);

  // Clear any disposition staged on this scheduled need. Used by
  // the Today's Plan card's per-row "undo" affordance — flips a
  // not_today / declined chip back to pending without committing.
  const clearDisposition = useCallback((scheduledRow) => {
    const key = scheduledWorkKey(scheduledRow);
    if (!key) return;
    setDispositions((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return {
    items, setItems, note, setNote, scheduled,
    dispositions,
    loaded, saving, savedAt, saveError,
    noteSaving, noteSavedAt, noteHistory, saveNoteNow,
    addItem, removeItem, removeSurface, setOutcome,
    setItemPrice, setItemDiscount, setItemNote,
    addFromSchedule, removeFromSchedule,
    addScheduledGroup, removeScheduledGroup,
    unscheduleNeed, declineNeed, clearDisposition,
    reload,
    undo, redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
  };
}

// ---------------------------------------------------------------------
// 3. Today's Work card — Performed Today only. Visit Note moved to
// its own bottom-of-page card per the Doctor view wireframe.
// ---------------------------------------------------------------------
function ChairsideMvImportPrompt({ onOpen }) {
  return (
    <section className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/40 p-4" data-testid="chairside-mv-import-entry">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-violet-600 shadow-sm">
          <History className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Treatment started before Dental OS?</p>
          <p className="mt-0.5 text-xs text-slate-500">Record its opening package and prior paid amount before recording today&apos;s work.</p>
          <button type="button" onClick={onOpen} className="mt-3 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700" data-testid="chairside-import-ongoing-case">
            Import case history
          </button>
        </div>
      </div>
    </section>
  );
}

function TodayWorkCard({ items, saving, savedAt, saveError, onReload, currency, patient, procedures = [], colorMap = null, onAdd, onRemove, onSetOutcome, onSetPrice, onSetDiscount, onSetNote, onUndo, onRedo, canUndo, canRedo, scheduledSlot = null }) {
  const [adding, setAdding] = useState(false);
  // Keyboard handler lives at PatientWorkspace level now so ⌘Z works
  // in Tx Plan / Future Need too (used to be silent there).

  return (
    <section
      className="h-full bg-white rounded-2xl shadow-sm"
      data-testid="doctor-today-panel"
      data-tutorial-id="charting-today-work"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Today</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            data-testid="doctor-today-undo"
            data-tutorial-id="doctor-today-undo"
            title="Undo (⌘Z / Ctrl+Z)"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
            aria-label="Undo"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            data-testid="doctor-today-redo"
            title="Redo (⌘⇧Z / Ctrl+Y)"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
            aria-label="Redo"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <SaveIndicator saving={saving} savedAt={savedAt} error={saveError} onReload={onReload} />
        </div>
      </header>
      {/* Fused "Scheduled" section (booked to-dos) sits above the
          recorded work — one card, two labeled bands. Renders nothing
          when there are no booked items for today. */}
      {scheduledSlot}
      <div className="px-4 py-2 bg-slate-50/70 border-b border-slate-100 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recorded</span>
        <span className="text-[11px] text-slate-400 truncate">· what bills at checkout</span>
      </div>
      <div className="p-4">
        <PerformedTodaySection
          items={items}
          currency={currency}
          patient={patient}
          procedures={procedures}
          colorMap={colorMap}
          onAddClick={() => setAdding(true)}
          onRemove={onRemove}
          onSetOutcome={onSetOutcome}
          onSetPrice={onSetPrice}
          onSetDiscount={onSetDiscount}
          onSetNote={onSetNote}
        />
      </div>
      {/* Footer note moved here as the single home for "live to
          checkout" — dropped the duplicate page-level note. */}
      <p className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
        Live to checkout — front desk closes the visit there.
      </p>
      {adding ? (
        <AddProcedureDialog
          onClose={() => setAdding(false)}
          onPick={(proc) => { onAdd(proc); setAdding(false); }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------
// 4. Tx Plan Builder card — presentational. Consumes the lifted
// useTxPlan() hook from PatientWorkspace so the chart-click handler
// in Tx Plan mode and this card share the same plans + active plan.
// Painting on a tooth in Tx Plan mode adds an item to the Draft plan;
// "Open full editor" still routes out to the patient page Treatment
// tab for notes / propose / PDF export.
// ---------------------------------------------------------------------
function TxPlanCard({ tx, currency, onOpenPatient, onUndo, onRedo, canUndo, canRedo }) {
  const { visiblePlans, activePlanId, setActivePlanId, activePlan, checked, creating, createPlan, setItemDiscount, setItemPrice, setItemNote } = tx;

  async function handleCreatePlan() {
    const id = await createPlan();
    if (id) toast.success("New treatment plan started.");
  }

  if (!checked) return null;

  const items = Array.isArray(activePlan?.items) ? activePlan.items : [];
  const planIsDraft = activePlan?.status === "Draft";
  const itemGross = (it) => Number(it.fee_centavos || 0) * Number(it.qty || 1);
  const itemDiscount = (it) => {
    if (it.discount_pct != null && Number(it.discount_pct) > 0) {
      return Math.round(itemGross(it) * (Number(it.discount_pct) / 100));
    }
    if (it.discount_amount_centavos != null && Number(it.discount_amount_centavos) > 0) {
      return Number(it.discount_amount_centavos);
    }
    return 0;
  };
  const grossTotal    = items.reduce((s, it) => s + itemGross(it), 0);
  const discountTotal = items.reduce((s, it) => s + itemDiscount(it), 0);
  const netTotal      = Math.max(0, grossTotal - discountTotal);

  return (
    <section className="bg-white rounded-2xl shadow-sm" data-testid="doctor-tx-plan-card">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-900">Tx Plan Builder</h2>
          {visiblePlans.length === 0 ? (
            <span className="text-xs text-slate-400">· no plans yet</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            data-testid="doctor-tx-plan-undo"
            title="Undo (⌘Z / Ctrl+Z)"
            aria-label="Undo"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            data-testid="doctor-tx-plan-redo"
            title="Redo (⌘⇧Z / Ctrl+Y)"
            aria-label="Redo"
            className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 p-1"
          >
            <Redo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleCreatePlan}
            disabled={creating}
            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900 disabled:opacity-40"
            data-testid="doctor-tx-plan-new"
          >
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            New plan
          </button>
          {visiblePlans.length > 0 ? (
            <button
              type="button"
              onClick={onOpenPatient}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
              data-testid="doctor-tx-plan-open-full"
            >
              Open full editor <ChevronRight className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      </header>

      {visiblePlans.length === 0 ? (
        <div className="px-4 py-5 text-center text-xs text-slate-500">
          No treatment plan in progress.
          <p className="mt-1 text-[11px] text-slate-400">
            Tap <span className="font-semibold">New plan</span> to start one. Once the 4-mode chart picker lands, painting in Tx Plan mode adds items here automatically.
          </p>
        </div>
      ) : (
        <>
          {visiblePlans.length > 1 ? (
            <div className="px-4 pt-3 flex flex-wrap gap-1.5">
              {/* Plan labels are sequence numbers (Plan 1 / Plan 2)
                  ordered oldest → newest, mapping to the dentist's
                  mental model. Status renders as a tinted pill,
                  separated visually so it doesn't look glued to the
                  plan name. */}
              {visiblePlans.slice().reverse().map((p, idx) => {
                const active = p.id === activePlanId;
                const seqNumber = idx + 1;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActivePlanId(p.id)}
                    data-testid={`doctor-tx-plan-pill-${p.id}`}
                    className={[
                      "text-[11px] font-medium rounded-full border px-2.5 py-1 inline-flex items-center gap-1.5",
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400",
                    ].join(" ")}
                  >
                    Plan {seqNumber}
                    <PlanStatusBadge status={p.status} active={active} />
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>
                {items.length} {items.length === 1 ? "item" : "items"}
              </span>
              <span>
                Net <span className="font-semibold text-slate-900 tabular-nums">{formatMoney(netTotal, currency)}</span>
                {discountTotal > 0 ? (
                  <span className="text-amber-700 ml-1.5">(−{formatMoney(discountTotal, currency)})</span>
                ) : null}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">No items in this plan yet.</p>
              </div>
            ) : (
              <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                {items.map((it) => (
                  <li
                    key={it.id}
                    className="px-3 py-2 flex flex-col gap-1.5 text-sm"
                    data-testid={`doctor-tx-plan-item-${it.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-slate-800 truncate">
                          {it.procedure_name || "Procedure"}
                          {it.tooth_number ? <span className="text-slate-400"> · #{it.tooth_number}</span> : null}
                          {it.surface ? <span className="text-slate-400"> · {it.surface}</span> : null}
                        </p>
                        {it.phase_number ? (
                          <p className="text-[11px] text-slate-400 mt-0.5">Phase {it.phase_number}</p>
                        ) : null}
                      </div>
                      <ItemStatusPill status={it.status} />
                    </div>
                    <LineEditControls
                      grossCentavos={itemGross(it)}
                      currency={currency}
                      discountPct={it.discount_pct ?? null}
                      discountAmountCentavos={it.discount_amount_centavos ?? null}
                      discountReason={it.discount_reason ?? null}
                      notes={it.notes ?? null}
                      onPriceChange={(planIsDraft && setItemPrice) ? async (cents) => setItemPrice(it.id, cents) : null}
                      onDiscountChange={setItemDiscount ? async (vals) => setItemDiscount(it.id, vals) : null}
                      onNoteChange={(planIsDraft && setItemNote) ? async (n) => setItemNote(it.id, n) : null}
                      testIdPrefix={`doctor-tx-plan-item-${it.id}`}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// Plan-level status pill rendered alongside the sequence-numbered
// plan name in the switcher. Active pill (dark background) gets a
// muted-light variant so the status reads against dark; inactive
// pills get the colored variant.
function PlanStatusBadge({ status, active }) {
  const tone = STATUS_TONES[status] ?? STATUS_TONES.default;
  return (
    <span
      className={[
        "text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5",
        active ? "bg-white/15 text-white/90" : tone.bg + " " + tone.text,
      ].join(" ")}
    >
      {status}
    </span>
  );
}

// Per-item status pill in the items list — tinted background so the
// dentist reads state at a glance, not as small uppercase column text.
function ItemStatusPill({ status }) {
  const tone = STATUS_TONES[status] ?? STATUS_TONES.default;
  return (
    <span className={`text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 ${tone.bg} ${tone.text}`}>
      {status}
    </span>
  );
}

const STATUS_TONES = {
  Draft:               { bg: "bg-slate-100",   text: "text-slate-600" },
  Proposed:            { bg: "bg-blue-50",     text: "text-blue-700" },
  Accepted:            { bg: "bg-teal-50",     text: "text-teal-700" },
  "Partially Accepted":{ bg: "bg-teal-50",     text: "text-teal-700" },
  Candidate:           { bg: "bg-slate-100",   text: "text-slate-600" },
  Deferred:            { bg: "bg-amber-50",    text: "text-amber-700" },
  Declined:            { bg: "bg-rose-50",     text: "text-rose-700" },
  Scheduled:           { bg: "bg-indigo-50",   text: "text-indigo-700" },
  Completed:           { bg: "bg-emerald-50",  text: "text-emerald-700" },
  Rejected:            { bg: "bg-rose-50",     text: "text-rose-700" },
  Superseded:          { bg: "bg-slate-100",   text: "text-slate-500" },
  default:             { bg: "bg-slate-100",   text: "text-slate-600" },
};

// ---------------------------------------------------------------------
// 5. Visit Note card — pulled out of the Today's Work panel so the
// dentist's narrative for this visit gets its own breathing room
// per the wireframe. Same draft, same autosave (handled in the hook
// one level up).
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// ToDoTodayCard — booked-for-this-appointment treatment_needs that the
// doctor hasn't yet acted on. Closes the loop:
//   Future Need → checkout Step 3 Book → schedules a treatment_need
//   to a future appointment → that appointment shows up in the doctor's
//   queue → opening the patient surfaces the booked items here.
// Three actions per row:
//   Done       → adds the row to Today's Work (auto-saves to draft);
//                bill builds organically as the doctor performs each one.
//   Not today  → unschedules the need; it returns to the patient's Open
//                pool and can be rebooked later.
//   Decline    → decline_treatment_need with a reason; need closes out.
// Card hides itself when there's nothing left to act on.
// ---------------------------------------------------------------------
function isOrthoCheckoutDraftRow(row) {
  if (!row) return false;
  if (row.source === "ortho_checkout" || String(row.work_key || "").startsWith("ortho:")) return true;
  if (row.ortho_case_id || row.ortho_visit_type) return true;
  return false;
}

function isScheduledOrthoProcedureForVisit(row, orthoContext) {
  if (!row) return false;
  if (isOrthoCheckoutDraftRow(row)) return true;
  if (!orthoContext?.isOrthoVisit) return false;
  const visitType = String(orthoContext.visitType || "").toLowerCase();
  const rowVisitType = String(row.ortho_visit_type || row.orthoVisitType || "").toLowerCase();
  if (rowVisitType && rowVisitType === visitType) return true;

  const code = String(row.procedure_code || row.code || "").toUpperCase();
  const name = String(row.display_label || row.procedure_name || row.name || "").toLowerCase();
  const codesByVisitType = {
    bonding: ["BRKT-MC", "BRKT-MS", "BRKT-CC", "BRKT-CS", "ALGN-START"],
    adjustment: ["ADJ", "ALGN-CHK"],
    debonding: ["DBND", "ALGN-END"],
    retention: [],
  };
  if ((codesByVisitType[visitType] || []).includes(code)) return true;
  if (visitType === "bonding") return name.includes("bracket installation") || name.includes("aligner treatment start");
  if (visitType === "adjustment") return name.includes("adjustment visit") || name.includes("aligner check");
  if (visitType === "debonding") return name.includes("debonding") || name.includes("treatment completion");
  if (visitType === "retention") return name.includes("retention");
  return false;
}

function linkedScheduledDentureRows(row, scheduledRows, dentureProcedureIds) {
  if (!row || !dentureProcedureIds?.has?.(row.procedure_id)) return row ? [row] : [];
  const planId = row.treatment_plan_id;
  if (!planId) return [row];

  const seen = new Set();
  const linked = [];
  for (const candidate of (scheduledRows || [])) {
    if (candidate?.treatment_plan_id !== planId) continue;
    if (!dentureProcedureIds.has(candidate.procedure_id)) continue;
    const key = scheduledWorkKey(candidate);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    linked.push(candidate);
  }
  return linked.length > 0 ? linked : [row];
}

function ToDoTodayCard({
  scheduled, currency, doneWorkKeys, chartReconciled = new Map(), dispositions = {}, orthoContext = null,
  onDone, onUndoDone, onDoneGroup, onUndoDoneGroup,
  onNotToday, onDecline, onClearDisposition,
  onRemoveScheduledDenture, dentureProcedureIds = new Set(),
  // post-op follow-up chair-override props (chief-dentist clinical rule)
  postOpByProcedure = {}, followupWaivers = {}, setFollowupWaivers,
  // Fused mode: drop this card's own shell + title and render only a
  // compact "Scheduled" band, so it nests inside the Today card as the
  // top section above the recorded work.
  embedded = false,
}) {
  // Pre-canned wave-off reasons + the picker modal state
  const WAIVE_REASONS = [
    "Simple case, no sutures",
    "Patient declined return",
    "No clinical indication",
    "Other",
  ];
  const [waiving, setWaiving] = useState(null); // { row, key, name }
  const [waiveReason, setWaiveReason] = useState("");
  // Render EVERY scheduled item — the card is a clipboard the doctor
  // glances at first thing on opening a checked-in patient. Each
  // row's state is one of:
  //   done       → green ✓ Done pill, click to undo (pulls back to pending)
  //   not_today  → blue Not-today chip, click to revert (no DB hit; the
  //                disposition is staged in draft.dispositions and
  //                applied at commit)
  //   declined   → rose Declined chip, click to revert
  //   pending    → action buttons: Done / Not today / Decline
  // Card hides only when there are no scheduled items at all (ad-hoc
  // walk-in with no booked procedures).
  const stateOf = useCallback((s) => {
    const key = scheduledWorkKey(s);
    if (key && doneWorkKeys.has(key)) return "done";
    // Auto-done: the dentist charted matching work, so the reminder is complete.
    if (key && chartReconciled?.has?.(key)) return "done";
    const d = dispositions?.[key] || (s?.tn_id ? dispositions?.[`tn:${s.tn_id}`] : null);
    if (d?.kind === "not_today") return "not_today";
    if (d?.kind === "declined")  return "declined";
    if (d?.kind === "logged")    return "logged";
    return "pending";
  }, [doneWorkKeys, chartReconciled, dispositions]);

  const orthoCurrentVisit = orthoContext?.isOrthoVisit
    ? currentOrthoVisitRow(orthoContext?.activeCase, orthoContext?.visitType, orthoContext?.appointmentId)
    : null;
  const orthoRecordedToday = !!orthoCurrentVisit?.ortho_visit?.id || !!orthoContext?.done;
  const orthoScheduled = !!orthoContext?.isOrthoVisit && orthoRecordedToday;
  const orthoDone = orthoScheduled;
  const visibleScheduled = useMemo(
    () => (scheduled || []).filter((s) => scheduledWorkKey(s) && !isScheduledOrthoProcedureForVisit(s, orthoContext)),
    [scheduled, orthoContext],
  );
  const pending = useMemo(
    () => visibleScheduled.filter((s) => stateOf(s) === "pending"),
    [visibleScheduled, stateOf],
  );
  const procedureDoneCount = useMemo(
    () => visibleScheduled.reduce((n, s) => n + (stateOf(s) === "done" ? 1 : 0), 0),
    [visibleScheduled, stateOf],
  );
  const procedureTotal = visibleScheduled.length;
  const total = procedureTotal + (orthoScheduled ? 1 : 0);
  const doneCount = procedureDoneCount + (orthoScheduled && orthoDone ? 1 : 0);
  const orthoChargeDue = orthoScheduled
    ? orthoVisitChargeDueCentavos(orthoContext?.activeCase, orthoCurrentVisit)
    : 0;
  const allDone = total > 0 && pending.length === 0 && doneCount === total;
  const [declining, setDeclining] = useState(null);  // { tn_id, name }
  const [reason, setReason]       = useState("");
  const [deferring, setDeferring] = useState(null);  // scheduled row awaiting a reason
  const [busy, setBusy]           = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  if (procedureTotal === 0 && !orthoScheduled) return null;

  function fmtFee(c) {
    const sym = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency ? `${currency} ` : "";
    return sym + Math.round((Number(c) || 0) / 100).toLocaleString("en-US");
  }

  async function handleMarkAllDone() {
    setBusy(true);
    const groupedRows = [];
    const singleRows = [];
    const handledKeys = new Set();
    for (const row of pending) {
      const rowKey = scheduledWorkKey(row);
      if (!rowKey || handledKeys.has(rowKey)) continue;
      const group = linkedScheduledDentureRows(row, visibleScheduled, dentureProcedureIds)
        .filter((candidate) => stateOf(candidate) === "pending");
      if (group.length > 1 && onDoneGroup) {
        for (const candidate of group) {
          const key = scheduledWorkKey(candidate);
          if (key) handledKeys.add(key);
          groupedRows.push(candidate);
        }
      } else {
        handledKeys.add(rowKey);
        singleRows.push(row);
      }
    }
    if (groupedRows.length > 0) {
      const saved = await onDoneGroup(groupedRows);
      if (!saved) {
        setBusy(false);
        return;
      }
    }
    for (const row of singleRows) await onDone?.(row);
    setBusy(false);
  }

  async function handleDone(row) {
    const linkedRows = linkedScheduledDentureRows(row, visibleScheduled, dentureProcedureIds)
      .filter((candidate) => ["pending", "done"].includes(stateOf(candidate)));
    if (linkedRows.length < 2 || !onDoneGroup) {
      await onDone?.(row);
      return;
    }
    setBusy(true);
    const saved = await onDoneGroup(linkedRows);
    setBusy(false);
    if (saved) toast.success("Both booked denture arches were recorded together.");
  }

  async function handleUndoDone(row) {
    const linkedDoneRows = linkedScheduledDentureRows(row, visibleScheduled, dentureProcedureIds)
      .filter((candidate) => stateOf(candidate) === "done");
    if (linkedDoneRows.length < 2 || !onUndoDoneGroup) {
      await onUndoDone?.(row);
      return;
    }
    setBusy(true);
    await onUndoDoneGroup(linkedDoneRows);
    setBusy(false);
  }

  async function handleConfirmDecline() {
    if (!declining) return;
    setBusy(true);
    const ok = await onDecline?.(declining, reason);
    setBusy(false);
    if (ok) { setDeclining(null); setReason(""); }
  }

  async function handleConfirmNotToday(reasonCode) {
    if (!deferring) return;
    setBusy(true);
    const ok = await onNotToday?.(deferring, reasonCode);
    setBusy(false);
    if (ok) setDeferring(null);
  }

  async function handleRemoveDenture(scheduledRow, key) {
    const name = scheduledRow?.display_label || scheduledRow?.procedure_name || "this denture";
    const confirmed = window.confirm(
      `Remove ${name} from today's visit? If it belongs to an existing ongoing case, the case and its balance will stay.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setRemovingKey(key);
    await onRemoveScheduledDenture?.(scheduledRow);
    setRemovingKey(null);
    setBusy(false);
  }

  return (
    <section
      className={embedded ? "" : `bg-white rounded-2xl shadow-sm ring-1 ${allDone ? "ring-emerald-100" : "ring-teal-200"}`}
      data-testid="doctor-todo-today-card"
    >
      {embedded ? (
        <div className="px-4 py-2 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardList className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Scheduled</span>
            <span className="text-[11px] text-slate-400 truncate">
              · booked on the calendar · {doneCount}/{total} done{allDone ? " ✓" : ""}
            </span>
          </div>
          {pending.length > 1 ? (
            <button
              type="button"
              onClick={handleMarkAllDone}
              disabled={busy}
              data-testid="doctor-todo-today-all-done"
              className="text-[11px] font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 disabled:opacity-40 shrink-0"
              title="Mark every still-pending procedure as done"
            >
              <Check className="w-3 h-3" /> Mark all done
            </button>
          ) : null}
        </div>
      ) : (
        <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardList className={`w-4 h-4 ${allDone ? "text-emerald-600" : "text-teal-600"}`} />
            <h2 className="text-sm font-semibold text-slate-900">Today's plan</h2>
            <span className={`text-[11px] tabular-nums ${allDone ? "text-emerald-700" : "text-slate-500"}`}>
              ({doneCount}/{total} done){allDone ? " ✓" : ""}
            </span>
          </div>
          {pending.length > 1 ? (
            <button
              type="button"
              onClick={handleMarkAllDone}
              disabled={busy}
              data-testid="doctor-todo-today-all-done"
              className="text-[11px] font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 disabled:opacity-40"
              title="Mark every still-pending procedure as done"
            >
              <Check className="w-3 h-3" /> Mark all done
            </button>
          ) : null}
        </header>
      )}

      <ul className="divide-y divide-slate-100">
        {orthoScheduled ? (
          <li
            className={`px-4 py-2.5 flex items-center justify-between gap-3 ${orthoDone ? "bg-emerald-50/40" : ""}`}
            data-testid="doctor-todo-today-ortho-item"
          >
            <div className="min-w-0">
              <p className={`text-sm truncate ${orthoDone ? "text-slate-500" : "text-slate-900"}`}>
                <span className={`font-medium ${orthoDone ? "line-through decoration-emerald-300" : ""}`}>
                  {orthoTodayLabel(orthoContext)}
                </span>
              </p>
              <p className="text-[11px] text-slate-400">
                {formatMoney(orthoChargeDue, currency)}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {orthoDone ? (
                <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded px-2.5 py-1 inline-flex items-center gap-1">
                  <Check className="w-3 h-3" /> Done
                </span>
              ) : (
                <span className="text-[11px] font-semibold bg-teal-50 text-teal-800 rounded px-2.5 py-1">
                  Save in Ortho panel
                </span>
              )}
            </div>
          </li>
        ) : null}
        {visibleScheduled.map((s) => {
          const key = scheduledWorkKey(s);
          const testId = safeTestId(key);
          const surfaceLabel = s.surface ? ` ${s.surface}` : "";
          const toothLabel   = s.tooth_number ? `#${s.tooth_number}${surfaceLabel}` : "General";
          const state = stateOf(s);
          const bareTooth = isBareToothBooking(s);
          const reconciled = chartReconciled?.get?.(key) || null;
          const rowBg =
            state === "done"      ? "bg-emerald-50/40"
            : state === "logged"    ? "bg-slate-50"
            : state === "not_today" ? "bg-sky-50/40"
            : state === "declined"  ? "bg-rose-50/40"
            : "";
          const nameTone =
            state === "pending" ? "text-slate-900" : "text-slate-500";
          const strike =
            state === "done"     ? "line-through decoration-emerald-300"
            : state === "declined" ? "line-through decoration-rose-300"
            : "";
          // Chief-dentist clinical rule: when the doctor marks a
          // procedure Done at chairside and that procedure has
          // post_op_followup_enabled, show a chip with one-tap wave-off.
          const postOp = s?.procedure_id ? postOpByProcedure[s.procedure_id] : null;
          const waiverKey = key;
          const waiver = followupWaivers?.[waiverKey] || null;
          const showPostOpChip = !!postOp && state === "done";
          const linkedDentureRows = linkedScheduledDentureRows(s, visibleScheduled, dentureProcedureIds)
            .filter((candidate) => ["pending", "done"].includes(stateOf(candidate)));
          const marksMultipleDentureArches = linkedDentureRows.length > 1;
          return (
            <li
              key={key}
              className={`px-4 py-2.5 ${rowBg}`}
              data-testid={`doctor-todo-today-item-${testId}`}
            >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-sm truncate ${nameTone}`}>
                  <span className={`font-medium ${strike}`}>
                    {s.display_label || s.procedure_name || "Procedure"}
                  </span>
                  <span className="text-slate-400"> · {toothLabel}</span>
                  {s.template_label && s.visit_count > 1 && !s.is_informal ? (
                    <span className="text-slate-400">
                      {" · Visit "}{s.visit_sequence ?? "?"} of {s.visit_count}
                      {" · "}{s.template_label}
                    </span>
                  ) : null}
                </p>
                {/* Bare tooth-based bookings carry no real price yet (the chart
                    prices them), so we don't show the flat catalog guess. */}
                {!bareTooth ? (
                  <p className="text-[11px] text-slate-400">
                    {fmtFee(s.default_fee_centavos)}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {state === "done" && reconciled ? (
                  <span
                    data-testid={`doctor-todo-today-reconciled-${testId}`}
                    title="Auto-checked from the chart — this booked work was recorded on the tooth chart"
                    className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded px-2.5 py-1 inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    <Check className="w-3 h-3" /> Done · from chart{reconciled.tooth ? ` #${reconciled.tooth}` : ""}
                  </span>
                ) : state === "done" ? (
                  <button
                    type="button"
                    onClick={() => handleUndoDone(s)}
                    disabled={busy}
                    data-testid={`doctor-todo-today-undo-${testId}`}
                    className="text-[11px] font-semibold bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                    title="Click to undo — pulls this back out of Today's Work"
                  >
                    <Check className="w-3 h-3" /> Done
                  </button>
                ) : state === "logged" ? (
                  <button
                    type="button"
                    onClick={() => onClearDisposition?.(s)}
                    disabled={busy}
                    data-testid={`doctor-todo-today-clear-logged-${testId}`}
                    className="text-[11px] font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                    title="Logged with no charge — chart the real work to bill it"
                  >
                    <Check className="w-3 h-3" /> Logged
                  </button>
                ) : state === "not_today" ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setDeferring(s)}
                      disabled={busy}
                      data-testid={`doctor-todo-today-edit-not-today-${testId}`}
                      className="text-[11px] font-semibold bg-sky-100 hover:bg-sky-200 text-sky-800 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                      title="Review why this treatment was not done today"
                    >
                      Not today · {notTodayReasonLabel(dispositions?.[key]?.reason_code, { short: true })}
                    </button>
                    <button
                      type="button"
                      onClick={() => onClearDisposition?.(s)}
                      disabled={busy}
                      data-testid={`doctor-todo-today-clear-not-today-${testId}`}
                      className="text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-40"
                    >
                      Undo
                    </button>
                  </div>
                ) : state === "declined" ? (
                  <button
                    type="button"
                    onClick={() => onClearDisposition?.(s)}
                    disabled={busy}
                    data-testid={`doctor-todo-today-clear-declined-${testId}`}
                    className="text-[11px] font-semibold bg-rose-100 hover:bg-rose-200 text-rose-800 rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                    title={dispositions[key]?.reason
                      ? `Declined: ${dispositions[key].reason}. Click to revert.`
                      : "Click to revert — drops the Declined disposition"}
                  >
                    Declined
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleDone(s)}
                      disabled={busy}
                      data-testid={`doctor-todo-today-done-${testId}`}
                      className="text-[11px] font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                      title={marksMultipleDentureArches
                        ? "Mark every denture arch booked from this case as performed today"
                        : "Mark as performed today"}
                    >
                      <Check className="w-3 h-3" /> {marksMultipleDentureArches ? "Done both arches" : "Done"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeferring(s)}
                      disabled={busy}
                      data-testid={`doctor-todo-today-not-today-${testId}`}
                      className="text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-2 py-1 disabled:opacity-40"
                      title="Push back — need returns to the open pool at commit"
                    >
                      Not today
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDeclining({ ...s, name: s.procedure_name || "this need" }); setReason(""); }}
                      disabled={busy}
                      data-testid={`doctor-todo-today-decline-${testId}`}
                      className="text-[11px] text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded px-2 py-1 disabled:opacity-40"
                      title="Decline — need closes with a reason at commit"
                    >
                      Decline
                    </button>
                    {dentureProcedureIds.has(s.procedure_id) && s.tn_id ? (
                      <button
                        type="button"
                        onClick={() => handleRemoveDenture(s, key)}
                        disabled={busy}
                        data-testid={`doctor-todo-today-trash-${testId}`}
                        className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                        title="Remove mistaken denture booking"
                        aria-label={`Remove ${s.display_label || s.procedure_name || "denture"} from today`}
                      >
                        {removingKey === key
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
              {showPostOpChip ? (
                <div className="mt-1.5 flex items-center gap-2 text-[11px]"
                     data-testid={`doctor-todo-today-postop-${testId}`}>
                  {waiver ? (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 line-through">
                        ↪ {postOp.followup_procedure_name} in {postOp.after_days}d
                      </span>
                      <span className="text-slate-500 italic">Waived: {waiver.reason}</span>
                      <button type="button" onClick={() => setFollowupWaivers?.((prev) => {
                          const next = { ...prev };
                          delete next[waiverKey];
                          return next;
                        })}
                        title="Restore — re-enable post-op follow-up"
                        className="ml-auto text-[11px] text-slate-500 hover:text-slate-700">
                        Undo
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        ↪ {postOp.followup_procedure_name} in {postOp.after_days}d ✓
                      </span>
                      <button type="button" onClick={() => {
                          setWaiveReason(WAIVE_REASONS[0]);
                          setWaiving({ row: s, key: waiverKey, name: s.procedure_name || "this procedure" });
                        }}
                        title="Wave off — doctor judges no follow-up needed (requires a reason)"
                        data-testid={`doctor-todo-today-postop-wave-${testId}`}
                        className="text-[11px] text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline">
                        Wave off
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <NotTodayReasonDialog
        open={!!deferring}
        procedureName={deferring?.procedure_name || deferring?.display_label || "this treatment"}
        initialReasonCode={deferring ? dispositions?.[scheduledWorkKey(deferring)]?.reason_code : null}
        onCancel={() => setDeferring(null)}
        onConfirm={handleConfirmNotToday}
        testIdPrefix="doctor-not-today"
      />

      {declining ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="doctor-todo-today-decline-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">Decline {declining.name}?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Closes the booked need. The patient won't be billed and it won't surface for rebooking.
            </p>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              placeholder="Reason (required)…"
              data-testid="doctor-todo-today-decline-reason"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-rose-500"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setDeclining(null); setReason(""); }}
                disabled={busy}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDecline}
                disabled={busy || !reason.trim()}
                data-testid="doctor-todo-today-decline-confirm"
                className="px-3 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded disabled:opacity-40"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Wave-off reason picker for the post-op follow-up chip.
          Required reason because waiving a clinical default is a
          chartable decision, not a silent skip. */}
      {waiving ? (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             data-testid="doctor-todo-today-postop-waive-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <p className="text-sm font-semibold text-slate-900">Skip the post-op follow-up?</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Defaults are conservative — most {String(waiving.name || "this procedure").toLowerCase()} cases benefit from a follow-up.
              Pick a reason; the chart will keep the record.
            </p>
            <div className="flex flex-col gap-1.5">
              {WAIVE_REASONS.map((r) => (
                <label key={r} className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input type="radio" name="doctor-waive-reason" value={r}
                    checked={waiveReason === r}
                    onChange={() => setWaiveReason(r)} />
                  {r}
                </label>
              ))}
              {waiveReason === "Other" ? (
                <input type="text" autoFocus
                  placeholder="Type the reason…"
                  onChange={(e) => setWaiveReason(e.target.value)}
                  className="mt-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-teal-500" />
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setWaiving(null); setWaiveReason(""); }}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" disabled={!waiveReason.trim() || waiveReason === "Other"}
                onClick={() => {
                  setFollowupWaivers?.((prev) => ({
                    ...prev,
                    [waiving.key]: { waived: true, reason: waiveReason.trim() },
                  }));
                  setWaiving(null);
                  setWaiveReason("");
                }}
                data-testid="doctor-todo-today-postop-waive-confirm"
                className="px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded disabled:opacity-40">
                Skip follow-up
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------
// WholeMouthRibbon — chip strip for procedures that don't attach to a
// tooth (Oral Prophylaxis, Comprehensive Oral Exam, Pano X-ray, etc.).
// ChairsideChartLegend — one cohesive box under the chart that holds
// BOTH footer rows: the whole-mouth status ribbon and the procedure
// colour key. They used to render as two mismatched strips (a bare
// row plus a separately-bordered card) that repeated the same
// procedure names in two visual languages. Wrapping them in a single
// divided container makes them read as one legend. `empty:hidden`
// drops the whole box when both child rows render null (nothing
// painted and no whole-mouth work), and `divide-y` draws the divider
// only when both rows are actually present.
// ---------------------------------------------------------------------
function ChairsideChartLegend({ children }) {
  return (
    <div
      data-testid="chairside-chart-legend"
      className="mt-2 empty:hidden rounded-lg bg-slate-50 border border-slate-200 divide-y divide-slate-200"
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
// Lives inside the chart card via ChartingTab.chartFooter so the
// doctor's "what's on today's agenda" canvas is complete: tooth-
// attached procedures paint on the chart, whole-mouth procedures
// surface as chips just below the tooth grid.
//
// Sources both scheduled (booked-for-this-visit treatment_needs with
// no tooth_number) and Today's Work draft items with no tooth_number.
// Renders one chip per procedure with an emerald ✓ when done, an
// amber dot when still pending. Hides itself when there's nothing
// to show — keeps the chart card clean for tooth-only visits.
// ---------------------------------------------------------------------
function WholeMouthRibbon({ scheduled, items, doneWorkKeys, dispositions = {}, colorMap, procedures, bare = false }) {
  const chips = useMemo(() => {
    const out = [];
    const seenWork = new Set();
    // Booked but no tooth → whole-mouth needs. Skip rows the doctor
    // staged as Not today / Declined — those leave today's agenda
    // and shouldn't ride the chart's ribbon either.
    for (const s of (scheduled || [])) {
      if (s?.tooth_number) continue;
      const workKey = scheduledWorkKey(s);
      if (!workKey) continue;
      const disp = dispositions?.[workKey] || (s.tn_id ? dispositions?.[`tn:${s.tn_id}`] : null);
      if (disp?.kind === "not_today" || disp?.kind === "declined") continue;
      const isDone = doneWorkKeys.has(workKey);
      out.push({
        key:    workKey,
        name:   s.procedure_name || "Procedure",
        proc:   procedures.find((p) => p.id === s.procedure_id) || null,
        status: isDone ? "done" : "pending",
      });
      seenWork.add(workKey);
    }
    // Ad-hoc whole-mouth items in the draft (added directly via the
    // palette without a chart click).
    for (const it of (items || [])) {
      if (it?.tooth_number) continue;
      if ((it.outcome || "done") !== "done") continue;
      const workKey = scheduledWorkKey(it);
      if (workKey && seenWork.has(workKey)) continue;
      out.push({
        key:    workKey || it.id || `${it.procedure_id}-${it.tn_id || "adhoc"}`,
        name:   it.procedure_name || "Procedure",
        proc:   procedures.find((p) => p.id === it.procedure_id) || null,
        status: "done",
      });
    }
    return out;
  }, [scheduled, items, doneWorkKeys, dispositions, procedures]);

  if (chips.length === 0) return null;
  return (
    <div className={`flex items-center gap-2 flex-wrap${bare ? " px-3 py-2" : ""}`} data-testid="doctor-whole-mouth-ribbon" data-tutorial-id="doctor-whole-mouth-ribbon">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mr-1">
        Whole-mouth
      </span>
      {chips.map((c) => {
        const color = c.proc ? procedureColor(c.proc, colorMap) : "#94a3b8";
        const isDone = c.status === "done";
        return (
          <span
            key={c.key}
            data-testid={`doctor-whole-mouth-chip-${c.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 ${
              isDone ? "bg-emerald-50 ring-emerald-200 text-emerald-800" : "bg-amber-50 ring-amber-200 text-amber-800"
            }`}
            title={isDone ? `${c.name} — done` : `${c.name} — booked, not yet done`}
          >
            <span
              className="w-2 h-2 rounded-full ring-1 ring-black/10 shrink-0"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            <span className="font-medium">{c.name}</span>
            {isDone ? <Check className="w-3 h-3" aria-hidden /> : null}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// FutureWholeMouthRibbon — sister component to WholeMouthRibbon. In
// Future Need mode, whole-mouth procedures (Oral Prophylaxis, Pano,
// Comprehensive Exam, etc.) don't paint on the tooth chart, so without
// this ribbon they were only visible deep in the Future Needs list
// under "GENERAL." Doctor parity request: same indicator strip below
// the chart in Future Need mode as in Today mode, so the doctor sees
// what's flagged for next time at a glance.
//
// Pulls from useFutureNeeds.needs (Open status, no tooth_number).
// All chips are "pending future visit" — amber styling matches the
// pending state of the Today ribbon.
// ---------------------------------------------------------------------
function FutureWholeMouthRibbon({ needs, colorMap, procedures, bare = false }) {
  const chips = useMemo(() => {
    const out = [];
    for (const n of (needs || [])) {
      if (n?.tooth_number) continue;
      out.push({
        key:    n.id,
        name:   n.procedure_name || "Procedure",
        proc:   procedures.find((p) => p.id === n.procedure_id) || null,
      });
    }
    return out;
  }, [needs, procedures]);

  if (chips.length === 0) return null;
  return (
    <div className={`flex items-center gap-2 flex-wrap${bare ? " px-3 py-2" : ""}`} data-testid="doctor-future-whole-mouth-ribbon">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mr-1">
        Whole-mouth
      </span>
      {chips.map((c) => {
        const color = c.proc ? procedureColor(c.proc, colorMap) : "#94a3b8";
        return (
          <span
            key={c.key}
            data-testid={`doctor-future-whole-mouth-chip-${c.key}`}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1 bg-amber-50 ring-amber-200 text-amber-800"
            title={`${c.name} — flagged for a future visit`}
          >
            <span
              className="w-2 h-2 rounded-full ring-1 ring-black/10 shrink-0"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            <span className="font-medium">{c.name}</span>
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// RowTimingControl — per-row override for a Future Need's follow-up
// window. The session picker at the top of the card handles the 90%
// case ("come back in 2 weeks for everything"). This control surfaces
// the exception — an extraction site that needs a same-day check, an
// antibiotic recheck, an urgent re-eval.
//
// VISUAL HIERARCHY: a row that follows the session default is QUIET
// (faint inline text). A row that DEVIATES from the default is LOUD
// (orange pill with a warning glyph) so the doctor's eye lands on it
// instantly when scanning the list. The same prominence carries
// through to the operator's Step 3 banner via the mixed-timing path.
//
// Collapsed: state-dependent button (see above).
// Expanded:  chip row from SESSION_FOLLOW_UP_OPTIONS. Click chip to
//            update + collapse. Click outside dismisses without saves.
// ---------------------------------------------------------------------
function RowTimingControl({ value, sessionDefault, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Outside-click closes the chip row. Mousedown so we beat the chip's
  // own click handler when the user is actually selecting a chip.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const readOnly = !onChange;
  const v = value ?? null;
  const s = sessionDefault ?? null;
  // Three collapsed states the row can be in:
  //   * "exception" — row has a value AND it differs from the session
  //                   default. Most important — render LOUD so doctor
  //                   spots the exception while scanning.
  //   * "tagged"    — row has a value that matches the session default
  //                   (or session is null and row has its own value).
  //                   Quiet confirmation, no visual noise.
  //   * "unset"     — row has no value. Faint "+ Set" affordance only;
  //                   the dominant flow is the session picker.
  let collapsedKind;
  if (v == null) collapsedKind = "unset";
  else if (s != null && v !== s) collapsedKind = "exception";
  else collapsedKind = "tagged";

  async function handlePick(days) {
    setOpen(false);
    if (days === value) return;
    await onChange?.(days);
  }

  return (
    <div ref={containerRef} className="relative" data-testid={testid}>
      {!open ? (
        <button
          type="button"
          onClick={() => !readOnly && setOpen(true)}
          disabled={readOnly}
          data-testid={`${testid}-toggle`}
          data-state={collapsedKind}
          className={[
            "inline-flex items-center gap-1.5 transition-colors",
            collapsedKind === "exception"
              ? "px-2.5 py-1 rounded-md bg-orange-100 border border-orange-300 text-orange-900 text-xs font-semibold hover:bg-orange-200"
              : collapsedKind === "tagged"
                ? "text-[10px] -mt-0.5 px-1.5 py-0.5 rounded text-amber-800 hover:bg-amber-100"
                : "text-[10px] -mt-0.5 px-1.5 py-0.5 rounded text-slate-400 hover:bg-slate-100",
            readOnly ? "cursor-default opacity-70" : "cursor-pointer",
          ].join(" ")}
          title={readOnly ? undefined : "Click to change when this one comes back"}
        >
          {collapsedKind === "exception" ? (
            <>
              <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden />
              <span>Different timing: come back in <strong className="font-bold">{describeFollowUpDays(v)}</strong></span>
            </>
          ) : collapsedKind === "tagged" ? (
            <>Tagged: come back in <strong className="font-semibold">{describeFollowUpDays(v)}</strong></>
          ) : (
            <>+ Set a different return time for this one</>
          )}
        </button>
      ) : (
        <div className="flex flex-wrap gap-1 py-1" data-testid={`${testid}-chips`}>
          {SESSION_FOLLOW_UP_OPTIONS.map((opt) => {
            const active = (value ?? null) === (opt.days ?? null);
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => handlePick(opt.days)}
                data-testid={`${testid}-chip-${opt.days ?? "default"}`}
                className={[
                  "px-2 py-0.5 text-[11px] rounded-full border transition-colors",
                  active
                    ? "bg-teal-600 border-teal-600 text-white font-semibold"
                    : "bg-white border-slate-200 text-slate-700 hover:border-teal-400",
                ].join(" ")}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SaveIndicator({ saving, savedAt, error, onReload }) {
  if (error) {
    return (
      <button
        type="button"
        onClick={onReload}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 underline underline-offset-2"
        data-testid="doctor-draft-conflict-reload"
        title={error}
      >
        <AlertTriangle className="w-3 h-3" /> Reload saved work
      </button>
    );
  }
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
        <Check className="w-3 h-3" /> Saved
      </span>
    );
  }
  return null;
}

function PerformedTodaySection({ items, currency, patient, procedures = [], colorMap = null, onAddClick, onRemove, onSetOutcome, onSetPrice, onSetDiscount, onSetNote }) {
  // Subtotals: gross before discounts, net after per-line discounts.
  // Both count only "done" items — that's what'll actually be billed
  // at checkout. Not-today and Declined sit in the same list so the
  // doctor can flip them later if they change their mind, but they
  // don't roll into the total.
  const doneItems = items.filter((it) => (it.outcome || "done") === "done");
  const grossTotal = doneItems.reduce(
    (s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0), 0);
  const discountTotal = doneItems.reduce(
    (s, it) => s + Number(it.item_discount_centavos || 0), 0);
  const netTotal = Math.max(0, grossTotal - discountTotal);
  const doneCount = doneItems.length;

  // Group items by tooth (matches the Tx Plan builder's mental
  // model). Items without a tooth (e.g. Cleaning, Exam) go into
  // a "General" bucket at the bottom.
  const grouped = useMemo(() => {
    const byTooth = new Map();
    for (const it of items) {
      const key = it.tooth_number ? String(it.tooth_number) : "_general";
      if (!byTooth.has(key)) byTooth.set(key, []);
      byTooth.get(key).push(it);
    }
    const teethKeys = Array.from(byTooth.keys())
      .filter((k) => k !== "_general")
      .sort((a, b) => Number(a) - Number(b));
    const groups = teethKeys.map((k) => ({ tooth: k, items: byTooth.get(k) }));
    if (byTooth.has("_general")) groups.push({ tooth: null, items: byTooth.get("_general") });
    return groups;
  }, [items]);

  const procById = useMemo(() => {
    const m = new Map();
    for (const p of procedures) m.set(p.id, p);
    return m;
  }, [procedures]);

  return (
    <section data-testid="doctor-performed-today">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
          Today's procedures
          {items.length > 0 ? (
            <span className="ml-1.5 text-slate-400 normal-case font-normal">
              · {doneCount} done{items.length !== doneCount ? `, ${items.length - doneCount} held` : ""}
            </span>
          ) : null}
        </h3>
        <button
          onClick={onAddClick}
          data-testid="doctor-add-procedure"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900"
        >
          <Plus className="w-3 h-3" /> Add procedure
        </button>
      </div>
      {items.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-4 text-center">
          <p className="text-xs text-slate-500">
            Pick a procedure from the palette and tap a tooth, or hit <kbd className="px-1 py-0.5 text-[10px] bg-white border border-slate-200 rounded">+ Add procedure</kbd>.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {grouped.map(({ tooth, items: groupItems }) => {
            const groupDone = groupItems.filter((it) => (it.outcome || "done") === "done");
            const groupTotal = Math.max(0, groupDone.reduce(
              (s, it) => s + Number(it.qty || 1) * Number(it.performed_price_centavos || 0)
                          - Number(it.item_discount_centavos || 0), 0));
            return (
              <li
                key={tooth ?? "_general"}
                className="border border-slate-200 rounded-lg bg-white overflow-hidden"
                data-testid={`doctor-today-group-${tooth ?? "general"}`}
              >
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50/70 border-b border-slate-100 text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                  <span>
                    {tooth ? <>🦷 Tooth #{tooth}</> : "General"}
                    <span className="ml-1.5 text-slate-400 normal-case font-normal">· {groupItems.length} {groupItems.length === 1 ? "item" : "items"}</span>
                  </span>
                  <span className="text-slate-700 normal-case tabular-nums">{formatMoney(groupTotal, currency)}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {groupItems.map((it) => {
                    const outcome = it.outcome || "done";
                    const dim = outcome !== "done";
                    const proc = procById.get(it.procedure_id);
                    const swatch = proc ? procedureColor(proc, colorMap) : "#94a3b8";
                    const grossLine = Number(it.qty || 1) * Number(it.performed_price_centavos || 0);
                    const lineId = draftLineId(it);
                    const lineTestId = safeTestId(lineId);
                    return (
                      <li
                        key={lineId}
                        className={`px-3 py-2 flex flex-col gap-1.5 text-sm ${dim ? "bg-slate-50" : ""}`}
                        data-testid={`doctor-item-${lineTestId}`}
                        data-tutorial-id={it.tutorial_id || it.tutorialId || undefined}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-2 truncate min-w-0">
                            <span
                              className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                              style={{ backgroundColor: swatch }}
                              aria-hidden
                            />
                            <span className={`truncate ${dim ? "text-slate-500 line-through" : "text-slate-800"}`}>
                              {it.procedure_name || "Procedure"}
                              {it.surface ? <span className="text-slate-400 no-underline"> · {it.surface}</span> : null}
                            </span>
                          </span>
                          <OutcomeToggle
                            itemId={lineId}
                            outcome={outcome}
                            onSet={onSetOutcome}
                          />
                        </div>
                        <SurfaceCountSubline
                          surfaceCount={splitSurfaces(it.surface).length}
                          unitPriceCentavos={Number(it.procedure_base_centavos ?? it.default_fee_centavos ?? 0)}
                          currency={currency}
                        />
                        <LineEditControls
                          grossCentavos={grossLine}
                          currency={currency}
                          discountAmountCentavos={Number(it.item_discount_centavos || 0) || null}
                          discountPct={it.item_discount_pct ?? null}
                          discountReason={it.item_discount_reason ?? null}
                          notes={it.line_note ?? null}
                          presets={buildLinePresets({
                            patientSeniorPwdTag: patient?.senior_pwd_tag ?? null,
                          })}
                          onPriceChange={onSetPrice ? async (cents) => onSetPrice(lineId, Math.round(cents / Math.max(1, Number(it.qty || 1)))) : null}
                          onDiscountChange={onSetDiscount ? async (vals) => onSetDiscount(lineId, vals) : null}
                          onNoteChange={onSetNote ? async (n) => onSetNote(lineId, n) : null}
                          onRemove={() => onRemove(lineId)}
                          testIdPrefix={`doctor-item-${lineTestId}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
      {items.length > 0 ? (
        <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md text-xs space-y-0.5" data-testid="doctor-today-subtotals" data-tutorial-id="doctor-today-subtotals">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Gross</span>
            <span className="text-slate-700 tabular-nums">{formatMoney(grossTotal, currency)}</span>
          </div>
          {discountTotal > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-amber-700">Discount</span>
              <span className="text-amber-700 tabular-nums">−{formatMoney(discountTotal, currency)}</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between pt-1 border-t border-slate-200">
            <span className="text-slate-600 font-medium">Net · billed at checkout</span>
            <span className="font-semibold text-slate-900 tabular-nums" data-testid="doctor-today-net">{formatMoney(netTotal, currency)}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// 3-state pill: Done (default, teal) / Not today (amber) / Declined
// (rose). Only "done" rolls into the Subtotal and into Checkout's
// billing — the other two persist on the draft so the front desk can
// see what was discussed and surface it on the unfinished-items step.
const OUTCOMES = [
  { id: "done",       label: "Done",      tone: "teal" },
  { id: "not_today",  label: "Not today", tone: "amber" },
  { id: "declined",   label: "Declined",  tone: "rose" },
];

function OutcomeToggle({ itemId, outcome, onSet }) {
  return (
    <div className="inline-flex items-center gap-1 self-start" role="group" aria-label="Outcome">
      {OUTCOMES.map((o) => {
        const active = outcome === o.id;
        const tone =
          o.tone === "teal"  ? (active ? "bg-teal-600 text-white border-teal-600"   : "bg-white text-slate-600 border-slate-200 hover:border-teal-300")
          : o.tone === "amber" ? (active ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300")
          : (active ? "bg-rose-600 text-white border-rose-600"   : "bg-white text-slate-600 border-slate-200 hover:border-rose-300");
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onSet(itemId, o.id)}
            data-testid={`doctor-outcome-${itemId}-${o.id}`}
            aria-pressed={active}
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${tone}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// AddProcedureDialog — minimal procedure picker. List comes from
// list_clinic_procedures (already used by the New Appointment panel).
// Phase 1 is intentionally simple: type → results → click adds. No
// per-tooth selection, no custom-procedure entry, no surface picker.
// Those layer in once the basic flow is validated.
// ---------------------------------------------------------------------
function AddProcedureDialog({ onClose, onPick }) {
  const [procedures, setProcedures] = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.rpc("list_clinic_procedures");
      setProcedures(Array.isArray(data) ? data.filter((p) => !p.archived) : []);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return procedures.slice(0, 60);
    return procedures.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.code || "").toLowerCase().includes(q) ||
      (p.short_code || "").toLowerCase().includes(q),
    ).slice(0, 60);
  }, [procedures, search]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="doctor-add-procedure-dialog"
      >
        <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Add procedure</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search procedure…"
              data-testid="doctor-add-procedure-search"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-teal-500"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400">
              <Loader2 className="w-4 h-4 mx-auto animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">No procedures match.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => onPick(p)}
                    data-testid={`doctor-pick-procedure-${p.id}`}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50"
                  >
                    <p className="text-sm text-slate-800 truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {p.code || p.short_code || "—"}
                      {p.default_fee_centavos
                        ? ` · ${formatMoney(p.default_fee_centavos, "PHP")}`
                        : ""}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Mirror of frontend/src/pages/Checkout.jsx's seedItemFromSchedule.
// Kept intentionally identical so a draft seeded chairside hydrates
// the front desk's Checkout page without translation. If Checkout's
// shape evolves, this needs to evolve in lockstep — see PR review
// guideline in CLAUDE.md.
function seedItemFromSchedule(s) {
  const key = scheduledWorkKey(s);
  return {
    draft_line_id:             createDraftLineId(),
    tn_id:                    s.tn_id,
    procedure_visit_id:       s.procedure_visit_id || null,
    work_key:                 key,
    procedure_id:             s.procedure_id,
    procedure_code:           s.procedure_code,
    procedure_name:           s.procedure_name,
    default_fee_centavos:     s.default_fee_centavos,
    performed_price_centavos: s.default_fee_centavos,
    item_discount_centavos:   0,
    qty:                      1,
    tooth_number:             s.tooth_number || "",
    surface:                  s.surface || null,
    outcome:                  "done",
    treatment_plan_id:        s.treatment_plan_id,
    treatment_plan_item_id:   s.treatment_plan_item_id,
  };
}

function scheduledWorkKey(row) {
  return checkoutWorkKey(row);
}

function sameScheduledWork(a, b) {
  return sameCheckoutWork(a, b);
}

function safeTestId(value) {
  return String(value || "unknown").replace(/[^a-zA-Z0-9_-]/g, "-");
}

// ---------------------------------------------------------------------
function formatMoney(centavos, currency) {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : currency === "INR" ? "₹" : currency ? `${currency} ` : "";
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Per-row subline that makes multi-surface bundles transparent:
// "2 surfaces × ₱1,500/ea". For single-surface rows, renders nothing
// (the price itself is enough).
function SurfaceCountSubline({ surfaceCount, unitPriceCentavos, currency }) {
  const count = Number(surfaceCount || 0);
  if (count <= 1) return null;
  if (!Number.isFinite(unitPriceCentavos) || unitPriceCentavos <= 0) return null;
  return (
    <p className="text-[11px] text-slate-500 -mt-0.5" data-testid="line-surface-subline">
      {count} surfaces × {formatMoney(unitPriceCentavos, currency)}/ea
    </p>
  );
}

// ---------------------------------------------------------------------
// Doctor's session-level "come back when" picker — chip row at the top
// of the Future Needs card. Catalog + label helper live in
// frontend/src/lib/followUpWindow.js so the operator's Step 3 banner
// renders the exact same labels.
// ---------------------------------------------------------------------
